/* Career Tarot V2 engine - faithful runtime for the design markup.
   Live adaptations: theme header/footer replace the design's own, the three
   cards are drawn client-side from the dkc-72 pool (upright and reversed) in
   the canonical career positions, the draw registers a canonical free preview
   at /api/readings/free-reading (token + model insight), and checkout runs on
   the shared rail in its career-three-card-compact presentation. */
(function () {
  'use strict';
  if (!document.querySelector('[data-cr2]')) return;

  var PAGE = '/pages/career-tarot-reading';
  var TYPE = 'Career Tarot';
  var SPREAD = 'career-three-card';
  var VARIANT = 'career-three-card-compact-v1';
  var ANALYTICS_SCHEMA = 'tarot_funnel_v1';
  var FUNNEL_VERSION = 'career-tarot-enterprise-2026-08-24-v2';
  var SESSION_KEY = 'deckaura.career.v2.analytics.session';
  /* This string is part of the backend-signed direct-reading contract. Keep
     richer decision detail in context and the visible-only critical question;
     Career's signed curiosityQuestion field intentionally remains empty. */
  var SCOPE = 'Connect all three cards to one work decision, distinguish controllable from external conditions, and identify one deciding factor plus one verifiable next step.';
  var CONFIDENCE = 'Symbolic reflection only; this is not employment, financial, or legal advice and does not guarantee a job, promotion, salary, income, or business outcome.';
  var POSITIONS = ['Current reality', 'Hidden factor', 'Best next move'];
  var ROLES = ['Current reality', 'Hidden factor', 'Best next move'];
  var API_FREE = 'https://reading.deckaura.com/api/readings/free-reading';
  var KEY = 'deckaura.career.v2.session.v2';
  var PREVIOUS_KEY = KEY + '.previous';
  var HISTORY_KEY = KEY + '.history';
  var activeFreeRequest = null;
  var freeRequestSequence = 0;
  var runtimeLoad = null;
  var runtimeLoadSequence = 0;
  /* Career deliberately has two paid depths. Keep this list aligned with the
     visible offer and the backend manifest; the retired $5.99 tier must never
     re-enter client state through a stale storage or pricing snapshot. */
  var RAIL_TIERS = ['deeper', 'indepth'];
  var TFS = ['30 days', '3 months', '6 months', 'No fixed date'];
  function localeCode() {
    var value = '';
    try { value = String((window.DeckauraLocale && (window.DeckauraLocale.locale || window.DeckauraLocale.language)) || document.documentElement.lang || 'en').toLowerCase(); } catch (_) { value = 'en'; }
    if (/^tr(?:-|$)/.test(value)) return 'tr';
    if (/^es(?:-|$)/.test(value)) return 'es';
    if (/^de(?:-|$)/.test(value)) return 'de';
    if (/^pt(?:-|$)/.test(value)) return 'pt';
    return 'en';
  }
  var LANG = localeCode();
  var RECOVERY_COPY = {
    en: { preparing: 'Opening secure checkout…', failed: 'Checkout could not be opened. Your question and cards are saved. Please try again.', unavailable: 'We could not securely prepare this reading for checkout. Your cards are saved; please try again.', expired: 'Your saved reading needs a fresh secure check. Please try again with the same question and cards.', checking: 'Checking your saved reading securely…' },
    tr: { preparing: 'Güvenli ödeme açılıyor…', failed: 'Ödeme açılamadı. Sorunuz ve kartlarınız kaydedildi. Lütfen yeniden deneyin.', unavailable: 'Bu yorum ödeme için güvenli biçimde hazırlanamadı. Kartlarınız kaydedildi; lütfen yeniden deneyin.', expired: 'Kayıtlı yorumunuzun yeniden güvenli biçimde doğrulanması gerekiyor. Aynı soru ve kartlarla yeniden deneyin.', checking: 'Kayıtlı yorumunuz güvenli biçimde doğrulanıyor…' },
    de: { preparing: 'Die sichere Zahlung wird geöffnet…', failed: 'Die Zahlung konnte nicht geöffnet werden. Frage und Karten sind gespeichert. Bitte versuche es erneut.', unavailable: 'Die Deutung konnte noch nicht sicher für die Zahlung vorbereitet werden. Deine Karten sind gespeichert; bitte versuche es erneut.', expired: 'Deine gespeicherte Deutung muss erneut sicher geprüft werden. Versuche es mit derselben Frage und denselben Karten erneut.', checking: 'Deine gespeicherte Deutung wird sicher geprüft…' },
    es: { preparing: 'Abriendo el pago seguro…', failed: 'No se pudo abrir el pago. Tu pregunta y tus cartas están guardadas. Inténtalo de nuevo.', unavailable: 'No pudimos preparar esta lectura de forma segura para el pago. Tus cartas están guardadas; inténtalo de nuevo.', expired: 'Tu lectura guardada necesita una nueva comprobación segura. Inténtalo de nuevo con la misma pregunta y las mismas cartas.', checking: 'Comprobando tu lectura guardada de forma segura…' },
    pt: { preparing: 'A abrir o pagamento seguro…', failed: 'Não foi possível abrir o pagamento. A pergunta e as cartas estão guardadas. Tente novamente.', unavailable: 'Não foi possível preparar esta leitura com segurança para o pagamento. As cartas estão guardadas; tente novamente.', expired: 'A leitura guardada precisa de uma nova verificação segura. Tente novamente com a mesma pergunta e as mesmas cartas.', checking: 'A verificar a leitura guardada com segurança…' }
  };
  function recoveryText(key) { return (RECOVERY_COPY[LANG] || RECOVERY_COPY.en)[key] || RECOVERY_COPY.en.failed; }
  var SAVED_LANGUAGE_NOTICE = {
    en: 'Your saved question and cards are unchanged. This is a basic card-based reflection in English.',
    tr: 'Kayıtlı sorunuz ve kartlarınız aynı kaldı. Bu, kartlara dayalı temel bir Türkçe yansımadır.',
    de: 'Deine gespeicherte Frage und die Karten bleiben unverändert. Dies ist eine grundlegende Reflexion der Karten auf Deutsch.',
    es: 'Tu pregunta y tus cartas guardadas no cambian. Esta es una reflexión básica de las cartas en español.',
    pt: 'A pergunta e as cartas guardadas mantêm-se. Esta é uma reflexão básica das cartas em português.'
  };
  function insightMatchesLocale() { return !!s.insightLocale && s.insightLocale.split('-')[0] === LANG; }
  var CARD_LANGUAGE = {
    tr: {
      blocked: ' — engellenmiş veya yön değiştirmiş', inArea: ' alanında ',
      frames: [
        'Mevcut konumunuzu {core} şekillendiriyor. Sonucu değerlendirmeden önce bunu gerçek başlangıç noktası kabul edin.',
        'Doğrulanması gereken sessiz etken {core}. Bunu yalnız beklentilerde değil, somut verilerde arayın.',
        'Bir sonraki adımı kontrolünüzde tutmak için {core} ile çalışın. Geri dönüşü zor bir karardan önce bunu küçük bir teste çevirin.'
      ],
      pattern: ['{a}, {at} temasını getirirken {b}, {bt} temasını ekliyor. Bu gerilim yalnız seçtiğiniz durumdan değil, çekilen bu iki karttan doğuyor.','Bu açılım {at} ile {bt} temasını birbirine bağlıyor. Cevabınızı belirlemesi gereken kesin koşul ve onu doğrulayacak kanıt, kişiselleştirilmiş yorum için açık kalıyor.'],
      major: {
        'The Fool':['Yeni yol ve hesaplanmış risk','tüm yanıtlar hazır olmadan yeni bir başlangıca açık olmak','hazırlıksızlık, dürtü veya bedeli hesaplanmamış risk'],
        'The Magician':['Eldeki kaynaklar','becerileri, araçları ve etki alanını bilinçli kullanmak','dağınık çaba, kullanılmayan güç veya içi boş bir sunum'],
        'The High Priestess':['Henüz söylenmemiş bilgi','sezgiyle birlikte saklı ama yararlı bilgiyi ayırt etmek','saklanan gerçekler, göz ardı edilen sezgi veya belirsizliği kanıt sanmak'],
        'The Empress':['Destekle büyüme','insanların, fikirlerin ve değerin gelişebileceği bir ortam','aşırı verme, zayıf sınırlar veya sürdürülemez büyüme'],
        'The Emperor':['Yapı, yetki ve sorumluluk','istikrarlı liderlik, net görev ve uygulanabilir standartlar','katılık, güven olmadan kontrol veya hesap vermeyen yetki'],
        'The Hierophant':['Kurumlar, mentorlar ve kurallar','kanıtlanmış süreçten, uzmanlıktan veya tanınan yetkinlikten yararlanmak','artık uymayan gelenek veya sorgulanması gereken kapı bekçiliği'],
        'The Lovers':['Uyum ve değerlere dayalı seçim','değerler, insanlar ve taahhütler uyuştuğunda çalışan karar','uyumsuzluk, bölünmüş sadakat veya bedelden kaçmak için ertelenen seçim'],
        'The Chariot':['Yönlendirilmiş ivme','tek bir yolu ileri taşıyan odak ve eşgüdüm','çatışan yönler, kontrolsüz güç veya istikrarsızlığı örten hız'],
        'Strength':['Güç kullanmadan etki','sabır, soğukkanlılık ve kendinden emin sınırlar','öz şüphe, tükenen sabır veya gerçek etkinin yerini alan baskı'],
        'The Hermit':['Bağımsız değerlendirme','kanıtları gözden geçirmek ve deneyime kulak vermek','yalıtılma, aşırı analiz veya tavsiyeden kaçınmak'],
        'Wheel of Fortune':['Değişen döngü ve dış zamanlama','tek kişinin dışındaki koşulların yarattığı fırsat','oynaklık, tekrarlanan örüntü veya hazırlanmak yerine şans beklemek'],
        'Justice':['Kanıt, adalet ve yazılı koşullar','kayıt, ölçütler ve gerçekten kararlaştırılanlarla şekillenen karar','çifte standart, eksik belge veya önyargıyla bozulan sonuç'],
        'The Hanged Man':['Karardan önce yeni bakış','yararlı bekleme, yeniden çerçeveleme ve başka açı denemek','öğrenmeden oyalanmak veya sonucu değiştirmeyen fedakârlık'],
        'Death':['Gerekli bitiş ve mesleki geçiş','daha doğru bir yapı veya kimliğe yer açan tamamlanma','bitişe direnmek, eksik kapanış veya bedeli artan erteleme'],
        'Temperance':['Bütünleştirme ve sürdürülebilir uyum','güçleri dikkatle birleştirip düzeni çalışana kadar ayarlamak','uyumsuzluk, aşırılık veya çözdüğünden fazlasını tüketen uzlaşma'],
        'The Devil':['Bağlayıcı teşvik ve bağımlılık','seçimi yöneten ödülü, korkuyu veya yükümlülüğü açıkça görmek','inkâr, bağın gevşemesi veya koşulları yeniden müzakere fırsatı'],
        'The Tower':['Değişmeden kalamayacak yapı','yanlış varsayımı hızla ortadan kaldıran sarsıcı gerçek','kaçınılan sarsıntı, zayıf temel veya küçük uyarılarla gelen değişim'],
        'The Star':['İnandırıcı umut ve yenilenen yön','iyileşme, görünürlük ve yeniden kurulmaya değer uzun vadeli amaç','moral kaybı, zayıf geri bildirim veya somut kanıt isteyen iyimserlik'],
        'The Moon':['Belirsizlik ve eksik gerçekler','bilgi tamamlanana kadar dikkatle yönetilmesi gereken durum','dağılan kafa karışıklığı veya kaygıyı öngörü sanmak'],
        'The Sun':['Görünür ilerleme ve açık destek','netlik, takdir ve şeffaf eylemi destekleyen koşullar','geciken takdir, aşırı güven veya hâlâ tanım isteyen başarı'],
        'Judgement':['Dürüst değerlendirme ve ikinci perde','işin neye dönüştüğüyle ve sonrasıyla açıkça yüzleşmek','öz şüphe, kaçınılan değerlendirme veya hâlâ yanıt bekleyen eski karar'],
        'The World':['Tamamlanma ve bir sonraki seviyeye hazır oluş','döngünün tam değerini vermesi ve daha geniş alan açması','yarım kalan iş, geciken kapanış veya tek eksikle sınırlanan büyüme']
      }
    },
    es: {
      blocked:' — bloqueado o redirigido',inArea:' en el ámbito de ',
      frames:['Tu posición actual está marcada por {core}. Tómalo como el punto de partida real antes de valorar el resultado.','El factor silencioso que debes verificar es {core}. Búscalo en los hechos, no solo en tus expectativas.','Mantén el siguiente paso bajo tu control trabajando con {core}. Conviértelo en una prueba pequeña antes de asumir un compromiso irreversible.'],
      pattern:['{a} aporta {at}, mientras {b} introduce {bt}. Esta tensión nace de esta pareja concreta de cartas, no solo de la situación elegida.','Esta tirada conecta {at} con {bt}. La condición exacta que debería decidir tu respuesta y la prueba que la confirmaría quedan abiertas para la lectura personalizada.'],
      major:{
        'The Fool':['Un camino nuevo y riesgo calculado','apertura a comenzar sin tener todas las respuestas','impulso, mala preparación o riesgo sin valorar'],
        'The Magician':['Recursos ya disponibles','habilidad, herramientas e influencia listas para usarse','esfuerzo disperso, ventaja desaprovechada o apariencia sin sustancia'],
        'The High Priestess':['Información aún no expresada','discernimiento e información útil que sigue privada','hechos ocultos, intuición ignorada o ambigüedad tomada por prueba'],
        'The Empress':['Crecimiento mediante apoyo','un entorno donde personas, ideas y valor pueden desarrollarse','dar de más, límites débiles o crecimiento sin estructura sostenible'],
        'The Emperor':['Estructura, autoridad y responsabilidad','liderazgo estable, funciones claras y normas aplicables','rigidez, control sin confianza o autoridad sin rendición de cuentas'],
        'The Hierophant':['Instituciones, mentoría y reglas','beneficio de procesos probados, guía experta o credenciales','una convención que ya no encaja o barreras que deben cuestionarse'],
        'The Lovers':['Alineación y elección por valores','una decisión que funciona cuando valores y compromisos coinciden','desalineación, lealtad dividida o una elección aplazada'],
        'The Chariot':['Impulso dirigido','voluntad enfocada y esfuerzo coordinado hacia una ruta','direcciones opuestas, fuerza sin control o velocidad inestable'],
        'Strength':['Influencia serena','paciencia, compostura y límites seguros','duda propia, contención agotada o presión en lugar de influencia'],
        'The Hermit':['Criterio independiente','espacio para revisar pruebas y escuchar la experiencia','aislamiento, análisis excesivo o evitar consejo'],
        'Wheel of Fortune':['Ciclo cambiante y tiempo externo','una apertura creada por condiciones que se mueven','volatilidad, patrones repetidos o esperar suerte sin prepararse'],
        'Justice':['Pruebas, equidad y términos escritos','una decisión marcada por el registro y lo acordado','criterios desiguales, documentos ausentes o sesgo'],
        'The Hanged Man':['Nueva perspectiva antes de comprometerse','demora útil, replanteamiento y otra perspectiva','estancamiento sin aprendizaje o sacrificio inútil'],
        'Death':['Final necesario y transición profesional','un cierre que deja espacio a una estructura más auténtica','resistencia al final, cierre incompleto o cambio aplazado'],
        'Temperance':['Integración, ritmo y ajuste sostenible','combinar fortalezas y ajustar hasta que funcione','mal encaje, extremos o compromiso que desgasta'],
        'The Devil':['Incentivos vinculantes y dependencia','ver con claridad la recompensa, miedo u obligación que controla','negación, una atadura que se afloja o posibilidad de renegociar'],
        'The Tower':['Una estructura que debe cambiar','una verdad disruptiva que elimina una suposición falsa','disrupción evitada, base inestable o avisos menores'],
        'The Star':['Esperanza creíble y dirección renovada','recuperación, visibilidad y un objetivo digno de reconstruir','desánimo, poca respuesta u optimismo que necesita pruebas'],
        'The Moon':['Incertidumbre y hechos incompletos','una situación que exige cautela con información parcial','confusión que se despeja o ansiedad tomada por pronóstico'],
        'The Sun':['Progreso visible y apoyo abierto','claridad, reconocimiento y condiciones para actuar con transparencia','reconocimiento tardío, exceso de confianza o éxito sin definir'],
        'Judgement':['Revisión honesta y segunda etapa','afrontar con claridad en qué se ha convertido el trabajo','duda, evaluación evitada o una decisión antigua pendiente'],
        'The World':['Culminación y siguiente nivel','un ciclo que entrega su valor y abre un escenario mayor','asunto inconcluso, cierre tardío o crecimiento limitado']
      }
    },
    de: {
      blocked:' — blockiert oder umgeleitet',inArea:' im Bereich ',
      frames:['Deine aktuelle Position wird durch {core} geprägt. Nimm das als realen Ausgangspunkt, bevor du das Ergebnis bewertest.','Der stille Faktor, den du prüfen solltest, ist {core}. Suche ihn in den Fakten, nicht nur in deinen Erwartungen.','Halte den nächsten Schritt in deiner Hand, indem du mit {core} arbeitest. Mache daraus einen kleinen Test, bevor du dich unumkehrbar festlegst.'],
      pattern:['{a} bringt {at}, während {b} {bt} hinzufügt. Diese Spannung entsteht aus genau diesem Kartenpaar, nicht nur aus der gewählten Situation.','Diese Legung verbindet {at} mit {bt}. Die genaue Bedingung für deine Entscheidung und der Beleg, der sie bestätigen würde, bleiben der persönlichen Deutung vorbehalten.'],
      major:{
        'The Fool':['Neuer Weg und kalkuliertes Risiko','Offenheit für einen Anfang ohne alle Antworten','Impuls, mangelnde Vorbereitung oder unkalkuliertes Risiko'],
        'The Magician':['Bereits verfügbare Mittel','Fähigkeiten, Werkzeuge und Einfluss bewusst einsetzen','zerstreute Kraft, ungenutzter Hebel oder Wirkung ohne Substanz'],
        'The High Priestess':['Noch unausgesprochene Information','Urteilskraft und nützliche, noch private Information','zurückgehaltene Fakten, ignorierte Intuition oder Mehrdeutigkeit als Beleg'],
        'The Empress':['Wachstum durch Förderung','ein Umfeld, in dem Menschen, Ideen und Wert wachsen','Überfürsorge, schwache Grenzen oder Wachstum ohne tragfähige Struktur'],
        'The Emperor':['Struktur, Autorität und Verantwortung','stabile Führung, klare Zuständigkeit und verbindliche Standards','Starre, Kontrolle ohne Vertrauen oder Macht ohne Verantwortung'],
        'The Hierophant':['Institutionen, Mentoren und Regeln','bewährte Prozesse, Fachrat oder anerkannte Qualifikation','eine unpassende Konvention oder zu hinterfragende Zugangshürden'],
        'The Lovers':['Ausrichtung und Werteentscheidung','eine Entscheidung, bei der Werte und Verpflichtungen übereinstimmen','Fehlausrichtung, geteilte Loyalität oder aufgeschobene Wahl'],
        'The Chariot':['Gerichtete Dynamik','fokussierter Wille und koordinierte Bewegung auf einem Weg','konkurrierende Richtungen, Kraft ohne Kontrolle oder instabiles Tempo'],
        'Strength':['Ruhiger Einfluss','Geduld, Haltung und selbstbewusste Grenzen','Selbstzweifel, erschöpfte Zurückhaltung oder Druck statt Einfluss'],
        'The Hermit':['Eigenständiges Urteil','Raum für Faktenprüfung und Erfahrung','Isolation, Überanalyse oder vermiedener Rat'],
        'Wheel of Fortune':['Wechselnder Zyklus und äußeres Timing','eine Öffnung durch veränderte äußere Bedingungen','Volatilität, Wiederholung oder Warten auf Glück'],
        'Justice':['Belege, Fairness und schriftliche Bedingungen','eine Entscheidung nach Aktenlage und Vereinbarung','ungleiche Maßstäbe, fehlende Dokumentation oder Verzerrung'],
        'The Hanged Man':['Neue Perspektive vor der Festlegung','nützliche Verzögerung und Perspektivwechsel','Stillstand ohne Lernen oder wirkungsloses Opfer'],
        'Death':['Notwendiges Ende und beruflicher Übergang','ein Abschluss, der Raum für eine ehrlichere Struktur schafft','Widerstand gegen ein Ende, fehlender Abschluss oder teures Aufschieben'],
        'Temperance':['Integration, Tempo und tragfähige Passung','Stärken sorgfältig verbinden und nachjustieren','schlechte Passung, Extreme oder zermürbender Kompromiss'],
        'The Devil':['Bindende Anreize und Abhängigkeit','Belohnung, Angst oder Pflicht erkennen, die die Wahl steuert','Verdrängung, lockernde Bindung oder Chance zur Neuverhandlung'],
        'The Tower':['Eine Struktur, die sich ändern muss','eine disruptive Wahrheit, die eine falsche Annahme beseitigt','vermiedener Bruch, instabiles Fundament oder kleinere Warnungen'],
        'The Star':['Glaubwürdige Hoffnung und neue Richtung','Erholung, Sichtbarkeit und ein lohnendes Fernziel','Entmutigung, schwaches Feedback oder Hoffnung ohne Beleg'],
        'The Moon':['Unsicherheit und unvollständige Fakten','eine Lage, die bei Teilinformationen Vorsicht verlangt','sich klärende Verwirrung oder Angst als Prognose'],
        'The Sun':['Sichtbarer Fortschritt und offene Unterstützung','Klarheit, Anerkennung und Bedingungen für transparentes Handeln','späte Anerkennung, Übermut oder undefinierter Erfolg'],
        'Judgement':['Ehrliche Bilanz und zweiter Akt','klar prüfen, was aus der Arbeit geworden ist','Selbstzweifel, vermiedene Bewertung oder offene Altentscheidung'],
        'The World':['Abschluss und Bereitschaft für die nächste Stufe','ein Zyklus erfüllt seinen Wert und öffnet eine größere Bühne','Unerledigtes, verzögerter Abschluss oder begrenztes Wachstum']
      }
    },
    pt: {
      blocked:' — bloqueado ou redirecionado',inArea:' na área de ',
      frames:['A sua posição atual é moldada por {core}. Considere isso o ponto de partida real antes de avaliar o resultado.','O fator silencioso a confirmar é {core}. Procure-o nos factos, não apenas nas expectativas.','Mantenha o próximo passo sob o seu controlo trabalhando com {core}. Transforme-o num pequeno teste antes de assumir um compromisso irreversível.'],
      pattern:['{a} traz {at}, enquanto {b} introduz {bt}. Esta tensão nasce deste par específico de cartas, não apenas da situação escolhida.','Esta tiragem liga {at} a {bt}. A condição exata que deve decidir a resposta e a prova que a confirmaria ficam em aberto para a leitura personalizada.'],
      major:{
        'The Fool':['Novo caminho e risco calculado','abertura para começar sem ter todas as respostas','impulso, pouca preparação ou risco não calculado'],
        'The Magician':['Recursos já disponíveis','competência, ferramentas e influência prontas para uso consciente','esforço disperso, vantagem não usada ou aparência sem substância'],
        'The High Priestess':['Informação ainda não dita','discernimento e informação útil que permanece privada','factos retidos, intuição ignorada ou ambiguidade tomada por prova'],
        'The Empress':['Crescimento com apoio','um ambiente onde pessoas, ideias e valor podem crescer','dar em excesso, limites fracos ou crescimento sem estrutura'],
        'The Emperor':['Estrutura, autoridade e responsabilidade','liderança estável, função clara e padrões aplicáveis','rigidez, controlo sem confiança ou autoridade sem prestação de contas'],
        'The Hierophant':['Instituições, mentores e regras','benefício de processo provado, orientação ou credenciais','convenção que já não serve ou barreira a questionar'],
        'The Lovers':['Alinhamento e escolha por valores','decisão que funciona quando valores e compromissos coincidem','desalinhamento, lealdade dividida ou escolha adiada'],
        'The Chariot':['Impulso dirigido','vontade focada e esforço coordenado numa rota','direções concorrentes, força sem controlo ou velocidade instável'],
        'Strength':['Influência serena','paciência, compostura e limites seguros','dúvida, contenção esgotada ou pressão no lugar de influência'],
        'The Hermit':['Julgamento independente','espaço para rever provas e ouvir a experiência','isolamento, análise excessiva ou conselho evitado'],
        'Wheel of Fortune':['Ciclo em mudança e tempo externo','abertura criada por condições externas em movimento','volatilidade, repetição ou esperar sorte sem se preparar'],
        'Justice':['Provas, justiça e termos escritos','decisão moldada pelo registo e pelo acordado','critérios desiguais, documentação em falta ou viés'],
        'The Hanged Man':['Nova perspetiva antes do compromisso','atraso útil, reformulação e outro ponto de vista','estagnação sem aprendizagem ou sacrifício inútil'],
        'Death':['Fim necessário e transição profissional','conclusão que abre espaço para estrutura mais verdadeira','resistência ao fim, fecho incompleto ou mudança adiada'],
        'Temperance':['Integração, ritmo e ajuste sustentável','combinar forças e ajustar até funcionar','mau encaixe, extremos ou compromisso desgastante'],
        'The Devil':['Incentivos vinculativos e dependência','ver a recompensa, o medo ou a obrigação que controla a escolha','negação, vínculo a afrouxar ou hipótese de renegociar'],
        'The Tower':['Estrutura que precisa mudar','verdade disruptiva que remove uma suposição falsa','rutura evitada, base instável ou avisos menores'],
        'The Star':['Esperança credível e direção renovada','recuperação, visibilidade e objetivo digno de reconstrução','desânimo, pouco retorno ou otimismo sem prova'],
        'The Moon':['Incerteza e factos incompletos','situação que exige cautela com informação parcial','confusão a dissipar-se ou ansiedade tomada por previsão'],
        'The Sun':['Progresso visível e apoio aberto','clareza, reconhecimento e condições para ação transparente','reconhecimento tardio, excesso de confiança ou sucesso indefinido'],
        'Judgement':['Revisão honesta e segunda etapa','encarar claramente aquilo em que o trabalho se tornou','dúvida, avaliação evitada ou decisão antiga por responder'],
        'The World':['Conclusão e próximo nível','ciclo que entrega o seu valor e abre um palco maior','assunto por concluir, fecho tardio ou crescimento limitado']
      }
    }
  };
  var MINOR_LANGUAGE = {
    tr:{up:'{rank} temasının yapıcı biçimde işlemesi',rev:'{rank} temasının gecikmesi, zorlanması veya yanlış yöne akması',r:{Ace:'somut bir başlangıç',Two:'canlı bir seçim',Three:'iş birliğiyle büyüme',Four:'istikrar ve koruma',Five:'sürtüşme ve gerçeklik testi',Six:'karşılıklılık ve ilerleme',Seven:'değerlendirme ve sınırlar',Eight:'odaklı hareket ve ustalık',Nine:'kazanılmış bağımsızlık ve dayanıklılık',Ten:'tamamlanma ve uzun vadeli sonuç',Page:'haber ve öğrenme eşiği',Knight:'ilerleme yöntemi',Queen:'olgun iç hâkimiyet',King:'sorumlu dış liderlik'},s:{Wands:'girişim, tempo ve ivme',Cups:'değerler, ilişkiler ve duygusal uyum',Swords:'gerçekler, baskı ve iletişim',Pentacles:'kanıt, kaynaklar ve pratik istikrar'}},
    es:{up:'el patrón «{rank}» en su expresión constructiva',rev:'el patrón «{rank}» bloqueado, bajo presión o mal dirigido',r:{Ace:'una apertura concreta',Two:'una elección activa',Three:'crecimiento mediante colaboración',Four:'estabilidad y contención',Five:'fricción y prueba de realidad',Six:'intercambio y avance',Seven:'evaluación y límites',Eight:'acción enfocada y dominio',Nine:'independencia y resistencia ganadas',Ten:'culminación y efecto a largo plazo',Page:'mensaje y aprendizaje',Knight:'método de avance',Queen:'dominio interno maduro',King:'liderazgo externo responsable'},s:{Wands:'iniciativa, ritmo e impulso',Cups:'valores, relaciones y encaje emocional',Swords:'hechos, presión y comunicación',Pentacles:'pruebas, recursos y estabilidad práctica'}},
    de:{up:'„{rank}“ in konstruktiver Ausprägung',rev:'„{rank}“ in verzögerter, belasteter oder fehlgeleiteter Form',r:{Ace:'eine konkrete Öffnung',Two:'eine aktive Wahl',Three:'Wachstum durch Zusammenarbeit',Four:'Stabilität und Begrenzung',Five:'Reibung und Realitätsprüfung',Six:'Austausch und Vorwärtsbewegung',Seven:'Bewertung und Grenzen',Eight:'fokussierte Bewegung und Können',Nine:'erarbeitete Unabhängigkeit und Ausdauer',Ten:'Vollendung und Langzeitfolge',Page:'Nachricht und Lernschritt',Knight:'Art des Vorangehens',Queen:'reife innere Führung',King:'verantwortliche äußere Führung'},s:{Wands:'Initiative, Tempo und Dynamik',Cups:'Werte, Beziehungen und emotionale Passung',Swords:'Fakten, Druck und Kommunikation',Pentacles:'Belege, Ressourcen und praktische Stabilität'}},
    pt:{up:'um padrão de «{rank}» na sua expressão construtiva',rev:'um padrão de «{rank}» bloqueado, sob pressão ou mal direcionado',r:{Ace:'uma abertura concreta',Two:'uma escolha ativa',Three:'crescimento por colaboração',Four:'estabilidade e contenção',Five:'fricção e teste de realidade',Six:'troca e avanço',Seven:'avaliação e limites',Eight:'movimento focado e domínio',Nine:'independência e resistência conquistadas',Ten:'culminação e efeito de longo prazo',Page:'mensagem e aprendizagem',Knight:'método de avanço',Queen:'domínio interno maduro',King:'liderança externa responsável'},s:{Wands:'iniciativa, ritmo e impulso',Cups:'valores, relações e encaixe emocional',Swords:'factos, pressão e comunicação',Pentacles:'provas, recursos e estabilidade prática'}}
  };
  var UI_TRANSLATIONS = {
    tr:{
      'Personalized 3-card work guidance':'Kişiselleştirilmiş 3 kartlı kariyer rehberi','Free Career':'Ücretsiz Kariyer','Tarot Reading':'Tarot Yorumu','Bring one real work decision to this free 3-card career tarot spread. See the pattern, the hidden factor and the next move reflected in your cards.':'Bu ücretsiz 3 kartlı kariyer açılımına gerçek bir iş kararınızı getirin. Kartlarınızda örüntüyü, gizli etkeni ve bir sonraki adımı görün.','Start My Free Career Reading':'Ücretsiz Kariyer Yorumumu Başlat','View My Saved Career Reading':'Kayıtlı Kariyer Yorumumu Gör','Secure · Instant · No sign-up':'Güvenli · Anında · Kayıt gerekmez','Your three-card reading is free. Compare all three personalized depths after your result.':'Üç kartlı yorumunuz ücretsizdir. Sonuçtan sonra üç kişiselleştirilmiş derinliği karşılaştırın.','Your leverage':'Etki gücünüz','Unseen factor':'Görünmeyen etken','Next move':'Sonraki adım','Full 78-card deck':'78 kartın tamamı','Upright & reversed':'Düz ve ters kartlar','Never sent to analytics':'Analitik sistemlerine gönderilmez','No email required to begin':'Başlamak için e-posta gerekmez','Secure one-time checkout':'Güvenli tek seferlik ödeme','Your question is sent securely only to create this free reading. It is never sent to analytics or advertising platforms. Payment information is handled only by Shopify\'s secure checkout.':'Sorunuz yalnız bu ücretsiz yorumu oluşturmak için güvenli biçimde gönderilir; analitik veya reklam sistemlerine aktarılmaz. Ödeme bilgileri yalnız Shopify\'ın güvenli ödeme sayfasında işlenir.',
      'What are you deciding?':'Hangi kararı veriyorsunuz?','Choose the situation closest to yours. Your career tarot reading will adapt to that decision.':'Durumunuza en yakın seçeneği belirleyin. Kariyer tarot yorumunuz bu karara göre uyarlanır.','Career situation':'Kariyer durumu','Selected':'Seçildi','Return to my saved reading':'Kayıtlı yorumuma dön','Job Application':'İş Başvurusu','Whether this application moves forward':'Başvurunun ilerleyip ilerlemeyeceği','Stay or Leave?':'Kalmak mı Ayrılmak mı?','Whether this role still fits':'Bu rolün hâlâ size uyup uymadığı','Two Offers or Paths':'İki Teklif veya Yol','Which option to commit to':'Hangi seçeneğe bağlanacağınız','Promotion or Raise':'Terfi veya Zam','Whether now is the time to ask':'Talep etmek için doğru zaman olup olmadığı','Career Change':'Kariyer Değişikliği','Whether to move in a new direction':'Yeni bir yöne geçip geçmemek','Workplace Conflict':'İş Yeri Çatışması','The dynamic you are dealing with':'Karşı karşıya olduğunuz dinamik','Business or Side Hustle':'İş veya Ek Gelir Projesi','Whether the idea is worth starting':'Fikrin başlamaya değip değmediği','Something Else':'Başka Bir Konu','Another decision about work':'İşle ilgili başka bir karar',
      'Context':'Bağlam','Question':'Soru','Two short steps. Usually under one minute.':'İki kısa adım. Genellikle bir dakikadan az sürer.','What detail could change this decision?':'Bu kararı hangi ayrıntı değiştirebilir?','Mention the fact that makes this decision difficult. Use at least two words and 10 characters.':'Kararı zorlaştıran somut noktayı yazın. En az iki kelime ve 10 karakter kullanın.','The fact that makes this decision difficult.':'Bu kararı zorlaştıran somut nokta.','Example:':'Örnek:','What do you most need clarity on?':'En çok hangi konuda netliğe ihtiyacınız var?','Ask about the choice in front of you rather than the verdict. Use at least two words and 8 characters, or start from a prompt.':'Kesin sonucu değil, önünüzdeki seçimi sorun. En az iki kelime ve 8 karakter kullanın veya bir örnek soruyla başlayın.','What should I understand before deciding?':'Karar vermeden önce neyi anlamalıyım?','What is actually within my control here?':'Burada gerçekten kontrolümde olan ne?','When does the decision matter?':'Karar hangi zaman aralığında önemli?','30 days':'30 gün','3 months':'3 ay','6 months':'6 ay','No fixed date':'Belirli tarih yok','Back':'Geri','Continue':'Devam et','Choose a situation above':'Yukarıdan bir durum seçin','Add one useful detail':'Yararlı bir ayrıntı ekleyin','Write one focused question':'Tek ve net bir soru yazın','Reveal My Career Cards':'Kariyer Kartlarımı Aç','Ready':'Hazır','Use at least two words':'En az iki kelime kullanın','Add a little more meaningful detail':'Biraz daha anlamlı ayrıntı ekleyin','Your three cards and core pattern are free. Your question is sent securely only to create this reading and is never sent to analytics.':'Üç kartınız ve temel örüntünüz ücretsizdir. Sorunuz yalnız bu yorumu oluşturmak için güvenli biçimde gönderilir ve analitik sistemlerine aktarılmaz.',
      'Your three cards':'Üç kartınız','Your cards for this decision':'Bu karar için kartlarınız','Current reality':'Mevcut gerçeklik','Hidden factor':'Gizli etken','Best next move':'En iyi sonraki adım','Your Career Pattern':'Kariyer Örüntünüz','The central tension':'Temel gerilim','What may be hidden':'Gizli kalabilecek nokta','What your details make concrete':'Ayrıntılarınızın somutlaştırdığı nokta','A closer look from your cards':'Kartlarınıza daha yakından bakış','One move within your control':'Kontrolünüzdeki tek adım','Saved on this device for 24 hours — the same question keeps the same cards':'Bu cihazda 24 saat saklanır — aynı soru aynı kartları korur','Your free pattern is saved for 24 hours. You can continue this question now by choosing a personalized depth.':'Ücretsiz örüntünüz 24 saat saklanır. Bir kişiselleştirme derinliği seçerek bu soruya devam edebilirsiniz.','Ask about a different decision':'Farklı bir karar sor','What your free reading leaves open':'Ücretsiz yorumun açık bıraktığı nokta','What the next depth adds:':'Bir sonraki derinliğin eklediği:','your free reading identifies the central pattern and one grounded move. A personalized reading connects your exact question, context and all three cards to explain the deciding condition, the evidence to verify and your clearest next steps.':'ücretsiz yorum temel örüntüyü ve ayakları yere basan bir adımı gösterir. Kişiselleştirilmiş yorum; kesin sorunuzu, bağlamınızı ve üç kartı birleştirerek belirleyici koşulu, doğrulanacak kanıtı ve en net sonraki adımları açıklar.','Condition':'Koşul','Comparison':'Karşılaştırma','Execution':'Uygulama','Choose this depth →':'Bu derinliği seç →','Selected — review your order':'Seçildi — siparişi gözden geçir','Most chosen':'En çok seçilen','One-time purchase · Written by hand, usually emailed within 90 minutes; busy days up to 24 hours · You will not re-enter your question at checkout':'Tek seferlik satın alma · Elle yazılır, genellikle 90 dakika içinde e-postalanır; yoğun günlerde 24 saate kadar sürebilir · Ödeme sırasında sorunuzu yeniden girmeyeceksiniz.',
      'The Deciding Factor':'Belirleyici Etken','Career Crossroads Map':'Kariyer Yol Ayrımı Haritası','30-Day Career Move Plan':'30 Günlük Kariyer Hamlesi Planı','The Hiring Signal':'İşe Alım Sinyali','Application & Employer Perspective Map':'Başvuru ve İşveren Bakışı Haritası','30-Day Job Search Blueprint':'30 Günlük İş Arama Planı','Stay vs Leave Map':'Kalmak mı Ayrılmak mı Haritası','Exit or Rebuild Plan':'Ayrılma veya Yeniden Kurma Planı','The Key Difference':'Temel Fark','Two-Path Career Comparison':'İki Yol Kariyer Karşılaştırması','Choice + First 30 Days Plan':'Seçim ve İlk 30 Gün Planı','Readiness & Hidden Block':'Hazırlık ve Gizli Engel','Promotion & Leverage Map':'Terfi ve Etki Gücü Haritası','Raise or Promotion Conversation Plan':'Zam veya Terfi Görüşmesi Planı','Career Direction Signal':'Kariyer Yönü Sinyali','Current Path vs New Path':'Mevcut Yol ve Yeni Yol','Career Pivot Blueprint':'Kariyer Geçiş Planı','The Hidden Workplace Dynamic':'Gizli İş Yeri Dinamiği','Power, Boundary & Resolution Map':'Güç, Sınır ve Çözüm Haritası','30-Day Protection & Next-Move Plan':'30 Günlük Koruma ve Sonraki Adım Planı','Opportunity Signal':'Fırsat Sinyali','Viability, Risk & First-Test Map':'Uygulanabilirlik, Risk ve İlk Test Haritası','30-Day Validation Blueprint':'30 Günlük Doğrulama Planı',
      'Today’s free preview allowance has been used. This overview comes from the on-page card meanings and your selected context; it is not presented as a new personalized interpretation. Your saved cards and paid reading options remain available.':'Bugünkü ücretsiz ön izleme hakkı kullanıldı. Bu özet, sayfadaki kart anlamları ve seçtiğiniz bağlamdan hazırlanmıştır; yeni kişisel yorum olarak sunulmaz. Kayıtlı kartlarınız ve ücretli yorum seçenekleri kullanılabilir.','The live preview did not meet our quality checks, so this quick overview was prepared directly from your cards. A paid reading is still prepared from your full question, context and selected depth.':'Canlı ön izleme kalite denetimini geçmedi; bu kısa özet doğrudan kartlarınızdan hazırlandı. Ücretli yorumunuz yine tam sorunuz, bağlamınız ve seçtiğiniz derinlikle hazırlanır.','The live preview service is temporarily unavailable. Your local card pattern remains available, and checkout will keep your question and cards attached.':'Canlı ön izleme hizmeti geçici olarak kullanılamıyor. Yerel kart örüntünüz erişilebilir; ödeme akışı sorunuzu ve kartlarınızı korur.','We could not load the live preview. Your local card pattern remains available; no result has been presented as a model-generated interpretation.':'Canlı ön izleme yüklenemedi. Yerel kart örüntünüz erişilebilir; hiçbir sonuç model üretimi yorum olarak sunulmadı.'
    },
    es:{
      'Personalized 3-card work guidance':'Orientación profesional personalizada en 3 cartas','Free Career':'Tarot profesional','Tarot Reading':'gratuito','Bring one real work decision to this free 3-card career tarot spread. See the pattern, the hidden factor and the next move reflected in your cards.':'Trae una decisión laboral real a esta tirada gratuita de 3 cartas. Observa el patrón, el factor oculto y el siguiente paso reflejados en tus cartas.','Start My Free Career Reading':'Empezar mi lectura profesional gratis','View My Saved Career Reading':'Ver mi lectura profesional guardada','Secure · Instant · No sign-up':'Seguro · Instantáneo · Sin registro','Your three-card reading is free. Compare all three personalized depths after your result.':'Tu lectura de tres cartas es gratuita. Después del resultado, compara las tres profundidades personalizadas.','Your leverage':'Tu ventaja','Unseen factor':'Factor oculto','Next move':'Siguiente paso','Full 78-card deck':'Baraja completa de 78 cartas','Upright & reversed':'Cartas derechas e invertidas','Never sent to analytics':'Nunca se envía a analítica','No email required to begin':'No necesitas correo para empezar','Secure one-time checkout':'Pago único seguro','What are you deciding?':'¿Qué estás decidiendo?','Choose the situation closest to yours. Your career tarot reading will adapt to that decision.':'Elige la situación más parecida a la tuya. La lectura se adaptará a esa decisión.','Career situation':'Situación profesional','Selected':'Seleccionado','Return to my saved reading':'Volver a mi lectura guardada','Job Application':'Candidatura','Whether this application moves forward':'Si la candidatura avanza','Stay or Leave?':'¿Quedarte o irte?','Whether this role still fits':'Si este puesto todavía encaja','Two Offers or Paths':'Dos ofertas o caminos','Which option to commit to':'Con qué opción comprometerte','Promotion or Raise':'Ascenso o aumento','Whether now is the time to ask':'Si es el momento de pedirlo','Career Change':'Cambio profesional','Whether to move in a new direction':'Si avanzar en otra dirección','Workplace Conflict':'Conflicto laboral','The dynamic you are dealing with':'La dinámica que estás viviendo','Business or Side Hustle':'Negocio o proyecto paralelo','Whether the idea is worth starting':'Si vale la pena empezar','Something Else':'Otra situación','Another decision about work':'Otra decisión laboral','Context':'Contexto','Question':'Pregunta','Two short steps. Usually under one minute.':'Dos pasos breves. Normalmente menos de un minuto.','What detail could change this decision?':'¿Qué detalle podría cambiar esta decisión?','Mention the fact that makes this decision difficult. Use at least two words and 10 characters.':'Explica el hecho que dificulta la decisión. Usa al menos dos palabras y 10 caracteres.','The fact that makes this decision difficult.':'El hecho que dificulta esta decisión.','Example:':'Ejemplo:','What do you most need clarity on?':'¿Qué necesitas aclarar más?','Ask about the choice in front of you rather than the verdict. Use at least two words and 8 characters, or start from a prompt.':'Pregunta por la elección, no por un veredicto. Usa al menos dos palabras y 8 caracteres o parte de una sugerencia.','What should I understand before deciding?':'¿Qué debería comprender antes de decidir?','What is actually within my control here?':'¿Qué está realmente bajo mi control?','When does the decision matter?':'¿Cuándo importa esta decisión?','30 days':'30 días','3 months':'3 meses','6 months':'6 meses','No fixed date':'Sin fecha fija','Back':'Atrás','Continue':'Continuar','Choose a situation above':'Elige una situación arriba','Add one useful detail':'Añade un detalle útil','Write one focused question':'Escribe una pregunta concreta','Reveal My Career Cards':'Revelar mis cartas profesionales','Ready':'Listo','Use at least two words':'Usa al menos dos palabras','Add a little more meaningful detail':'Añade un poco más de detalle útil','Your three cards':'Tus tres cartas','Your cards for this decision':'Tus cartas para esta decisión','Current reality':'Realidad actual','Hidden factor':'Factor oculto','Best next move':'Mejor siguiente paso','Your Career Pattern':'Tu patrón profesional','The central tension':'La tensión central','What may be hidden':'Lo que puede estar oculto','What your details make concrete':'Lo que concretan tus datos','A closer look from your cards':'Una mirada más cercana a tus cartas','One move within your control':'Un paso bajo tu control','Ask about a different decision':'Preguntar por otra decisión','Return to my saved reading':'Volver a mi lectura guardada','What your free reading leaves open':'Lo que deja abierto la lectura gratuita','Condition':'Condición','Comparison':'Comparación','Execution':'Ejecución','Choose this depth →':'Elegir esta profundidad →','Selected — review your order':'Seleccionado — revisar pedido','Most chosen':'Más elegido','The Deciding Factor':'El factor decisivo','Career Crossroads Map':'Mapa de encrucijada profesional','30-Day Career Move Plan':'Plan profesional de 30 días','Today’s free preview allowance has been used. This overview comes from the on-page card meanings and your selected context; it is not presented as a new personalized interpretation. Your saved cards and paid reading options remain available.':'Se ha usado el cupo de vista previa gratuita de hoy. Este resumen procede de los significados de las cartas y del contexto elegido; no se presenta como interpretación en vivo del modelo. Tus cartas guardadas y las opciones de pago siguen disponibles.'
    },
    de:{
      'Personalized 3-card work guidance':'Persönliche Berufsorientierung mit 3 Karten','Free Career':'Kostenloses Berufs-','Tarot Reading':'Tarot','Bring one real work decision to this free 3-card career tarot spread. See the pattern, the hidden factor and the next move reflected in your cards.':'Bringe eine echte berufliche Entscheidung in diese kostenlose 3-Karten-Legung. Erkenne Muster, verborgenen Faktor und nächsten Schritt in deinen Karten.','Start My Free Career Reading':'Kostenlose Berufslegung starten','View My Saved Career Reading':'Gespeicherte Berufslegung ansehen','Secure · Instant · No sign-up':'Sicher · Sofort · Ohne Anmeldung','Your three-card reading is free. Compare all three personalized depths after your result.':'Deine Drei-Karten-Legung ist kostenlos. Vergleiche danach alle drei persönlichen Tiefen.','Your leverage':'Dein Hebel','Unseen factor':'Verborgener Faktor','Next move':'Nächster Schritt','Full 78-card deck':'Vollständiges 78-Karten-Deck','Upright & reversed':'Aufrechte und umgekehrte Karten','Never sent to analytics':'Nie an Analysedienste gesendet','No email required to begin':'Keine E-Mail zum Start nötig','Secure one-time checkout':'Sichere Einmalzahlung','What are you deciding?':'Was möchtest du entscheiden?','Choose the situation closest to yours. Your career tarot reading will adapt to that decision.':'Wähle die Situation, die deiner am nächsten kommt. Die Legung passt sich daran an.','Career situation':'Berufliche Situation','Selected':'Ausgewählt','Return to my saved reading':'Zur gespeicherten Legung','Job Application':'Bewerbung','Whether this application moves forward':'Ob die Bewerbung weiterkommt','Stay or Leave?':'Bleiben oder gehen?','Whether this role still fits':'Ob diese Stelle noch passt','Two Offers or Paths':'Zwei Angebote oder Wege','Which option to commit to':'Für welche Option du dich entscheidest','Promotion or Raise':'Beförderung oder Gehalt','Whether now is the time to ask':'Ob jetzt der richtige Zeitpunkt ist','Career Change':'Berufswechsel','Whether to move in a new direction':'Ob du eine neue Richtung einschlägst','Workplace Conflict':'Konflikt am Arbeitsplatz','The dynamic you are dealing with':'Die Dynamik, mit der du umgehst','Business or Side Hustle':'Unternehmen oder Nebenprojekt','Whether the idea is worth starting':'Ob sich der Start lohnt','Something Else':'Etwas anderes','Another decision about work':'Eine andere berufliche Entscheidung','Context':'Kontext','Question':'Frage','Two short steps. Usually under one minute.':'Zwei kurze Schritte. Meist unter einer Minute.','What detail could change this decision?':'Welches Detail könnte die Entscheidung ändern?','Mention the fact that makes this decision difficult. Use at least two words and 10 characters.':'Nenne den Fakt, der die Entscheidung schwierig macht. Mindestens zwei Wörter und 10 Zeichen.','The fact that makes this decision difficult.':'Der Fakt, der diese Entscheidung schwierig macht.','Example:':'Beispiel:','What do you most need clarity on?':'Worüber brauchst du am meisten Klarheit?','Ask about the choice in front of you rather than the verdict. Use at least two words and 8 characters, or start from a prompt.':'Frage nach der anstehenden Wahl statt nach einem Urteil. Nutze mindestens zwei Wörter und 8 Zeichen oder eine Vorlage.','What should I understand before deciding?':'Was sollte ich vor der Entscheidung verstehen?','What is actually within my control here?':'Was liegt hier wirklich in meiner Hand?','When does the decision matter?':'Wann wird die Entscheidung wichtig?','30 days':'30 Tage','3 months':'3 Monate','6 months':'6 Monate','No fixed date':'Kein festes Datum','Back':'Zurück','Continue':'Weiter','Choose a situation above':'Wähle oben eine Situation','Add one useful detail':'Füge ein nützliches Detail hinzu','Write one focused question':'Schreibe eine klare Frage','Reveal My Career Cards':'Meine Berufskarten aufdecken','Ready':'Bereit','Use at least two words':'Nutze mindestens zwei Wörter','Add a little more meaningful detail':'Füge etwas mehr aussagekräftiges Detail hinzu','Your three cards':'Deine drei Karten','Your cards for this decision':'Deine Karten zu dieser Entscheidung','Current reality':'Aktuelle Realität','Hidden factor':'Verborgener Faktor','Best next move':'Bester nächster Schritt','Your Career Pattern':'Dein Berufsmuster','The central tension':'Die zentrale Spannung','What may be hidden':'Was verborgen sein könnte','What your details make concrete':'Was deine Angaben konkret machen','A closer look from your cards':'Ein genauerer Blick auf deine Karten','One move within your control':'Ein Schritt in deiner Hand','Ask about a different decision':'Eine andere Entscheidung fragen','What your free reading leaves open':'Was die kostenlose Legung offenlässt','Condition':'Bedingung','Comparison':'Vergleich','Execution':'Umsetzung','Choose this depth →':'Diese Tiefe wählen →','Selected — review your order':'Ausgewählt — Bestellung prüfen','Most chosen':'Am häufigsten gewählt','The Deciding Factor':'Der entscheidende Faktor','Career Crossroads Map':'Berufliche Wegkreuzung','30-Day Career Move Plan':'30-Tage-Berufsplan','Today’s free preview allowance has been used. This overview comes from the on-page card meanings and your selected context; it is not presented as a new personalized interpretation. Your saved cards and paid reading options remain available.':'Das heutige kostenlose Vorschaukontingent ist verbraucht. Diese Übersicht stammt aus den Kartenbedeutungen und deinem gewählten Kontext; sie wird nicht als neue persönliche Deutung dargestellt. Gespeicherte Karten und Kaufoptionen bleiben verfügbar.'
    },
    pt:{
      'Personalized 3-card work guidance':'Orientação profissional personalizada em 3 cartas','Free Career':'Tarot profissional','Tarot Reading':'gratuito','Bring one real work decision to this free 3-card career tarot spread. See the pattern, the hidden factor and the next move reflected in your cards.':'Traga uma decisão profissional real para esta tiragem gratuita de 3 cartas. Veja o padrão, o fator oculto e o próximo passo refletidos nas cartas.','Start My Free Career Reading':'Iniciar leitura profissional grátis','View My Saved Career Reading':'Ver leitura profissional guardada','Secure · Instant · No sign-up':'Seguro · Imediato · Sem registo','Your three-card reading is free. Compare all three personalized depths after your result.':'A leitura de três cartas é gratuita. Depois do resultado, compare as três profundidades personalizadas.','Your leverage':'A sua vantagem','Unseen factor':'Fator oculto','Next move':'Próximo passo','Full 78-card deck':'Baralho completo de 78 cartas','Upright & reversed':'Cartas direitas e invertidas','Never sent to analytics':'Nunca enviado para análise','No email required to begin':'Não precisa de e-mail para começar','Secure one-time checkout':'Pagamento único seguro','What are you deciding?':'O que está a decidir?','Choose the situation closest to yours. Your career tarot reading will adapt to that decision.':'Escolha a situação mais próxima da sua. A leitura adapta-se a essa decisão.','Career situation':'Situação profissional','Selected':'Selecionado','Return to my saved reading':'Voltar à leitura guardada','Job Application':'Candidatura','Whether this application moves forward':'Se a candidatura avança','Stay or Leave?':'Ficar ou sair?','Whether this role still fits':'Se esta função ainda serve','Two Offers or Paths':'Duas ofertas ou caminhos','Which option to commit to':'A que opção se comprometer','Promotion or Raise':'Promoção ou aumento','Whether now is the time to ask':'Se agora é o momento de pedir','Career Change':'Mudança de carreira','Whether to move in a new direction':'Se deve seguir outra direção','Workplace Conflict':'Conflito no trabalho','The dynamic you are dealing with':'A dinâmica que enfrenta','Business or Side Hustle':'Negócio ou projeto paralelo','Whether the idea is worth starting':'Se vale a pena começar','Something Else':'Outra situação','Another decision about work':'Outra decisão profissional','Context':'Contexto','Question':'Pergunta','Two short steps. Usually under one minute.':'Dois passos breves. Normalmente menos de um minuto.','What detail could change this decision?':'Que detalhe pode mudar esta decisão?','Mention the fact that makes this decision difficult. Use at least two words and 10 characters.':'Indique o facto que torna a decisão difícil. Use pelo menos duas palavras e 10 caracteres.','The fact that makes this decision difficult.':'O facto que torna esta decisão difícil.','Example:':'Exemplo:','What do you most need clarity on?':'Sobre o que precisa de mais clareza?','Ask about the choice in front of you rather than the verdict. Use at least two words and 8 characters, or start from a prompt.':'Pergunte sobre a escolha, não sobre um veredito. Use pelo menos duas palavras e 8 caracteres ou uma sugestão.','What should I understand before deciding?':'O que devo compreender antes de decidir?','What is actually within my control here?':'O que está realmente sob o meu controlo?','When does the decision matter?':'Quando importa esta decisão?','30 days':'30 dias','3 months':'3 meses','6 months':'6 meses','No fixed date':'Sem data fixa','Back':'Voltar','Continue':'Continuar','Choose a situation above':'Escolha uma situação acima','Add one useful detail':'Acrescente um detalhe útil','Write one focused question':'Escreva uma pergunta concreta','Reveal My Career Cards':'Revelar as minhas cartas profissionais','Ready':'Pronto','Use at least two words':'Use pelo menos duas palavras','Add a little more meaningful detail':'Acrescente um pouco mais de detalhe útil','Your three cards':'As suas três cartas','Your cards for this decision':'As cartas para esta decisão','Current reality':'Realidade atual','Hidden factor':'Fator oculto','Best next move':'Melhor próximo passo','Your Career Pattern':'O seu padrão profissional','The central tension':'A tensão central','What may be hidden':'O que pode estar oculto','What your details make concrete':'O que os seus dados tornam concreto','A closer look from your cards':'Uma leitura mais próxima das cartas','One move within your control':'Um passo sob o seu controlo','Ask about a different decision':'Perguntar sobre outra decisão','What your free reading leaves open':'O que a leitura gratuita deixa em aberto','Condition':'Condição','Comparison':'Comparação','Execution':'Execução','Choose this depth →':'Escolher esta profundidade →','Selected — review your order':'Selecionado — rever pedido','Most chosen':'Mais escolhido','The Deciding Factor':'O fator decisivo','Career Crossroads Map':'Mapa de encruzilhada profissional','30-Day Career Move Plan':'Plano profissional de 30 dias','Today’s free preview allowance has been used. This overview comes from the on-page card meanings and your selected context; it is not presented as a new personalized interpretation. Your saved cards and paid reading options remain available.':'A quota de pré-visualização gratuita de hoje foi usada. Este resumo vem dos significados das cartas e do contexto escolhido; não é apresentado como interpretação ao vivo do modelo. As cartas guardadas e as opções pagas continuam disponíveis.'
    }
  };
  var COMMERCE_TRANSLATIONS = {
    tr:{'What the next depth adds:':'Bir sonraki derinliğin eklediği:','The fact or conversation that should change your answer':'Cevabınızı değiştirmesi gereken gerçek veya görüşme','What strengthens each available route':'Her seçeneği güçlendiren unsurlar','How to test the stronger path without unnecessary risk':'Daha güçlü yolu gereksiz risk almadan nasıl test edeceğiniz','Order summary':'Sipariş özeti','Close order summary':'Sipariş özetini kapat','Your selection':'Seçiminiz','Change depth':'Derinliği değiştir','Change reading depth':'Yorum derinliğini değiştir','Based on':'Temel alınanlar','Your question':'Sorunuz','Your cards':'Kartlarınız','Delivery':'Teslimat','By email':'E-posta ile','Timing':'Süre','Total':'Toplam','What this depth adds:':'Bu derinliğin eklediği:','the free result identifies your core pattern. The personalized reading weighs the details you shared, the interaction between all three cards and the selected depth to build a fuller decision framework.':'ücretsiz sonuç temel örüntüyü gösterir. Kişiselleştirilmiş yorum, paylaştığınız ayrıntıları, üç kartın etkileşimini ve seçilen derinliği tartarak daha kapsamlı bir karar çerçevesi kurar.','One-time purchase · no subscription':'Tek seferlik satın alma · abonelik yok','You will not re-enter your question or context at checkout':'Ödeme sırasında sorunuzu veya bağlamınızı yeniden girmeyeceksiniz','Retry secure price check':'Güvenli fiyat kontrolünü yinele','Hide reading options':'Yorum seçeneklerini gizle','Checking…':'Kontrol ediliyor…','Checking price…':'Fiyat kontrol ediliyor…','Checking secure price…':'Güvenli fiyat kontrol ediliyor…','Confirming the secure storefront price…':'Güvenli mağaza fiyatı doğrulanıyor…'},
    es:{'Saved on this device for 24 hours — the same question keeps the same cards':'Guardado 24 horas en este dispositivo — la misma pregunta conserva las mismas cartas','Your free pattern is saved for 24 hours. You can continue this question now by choosing a personalized depth.':'Tu patrón gratuito queda guardado 24 horas. Puedes continuar esta pregunta eligiendo una profundidad personalizada.','What the next depth adds:':'Lo que añade la siguiente profundidad:','your free reading identifies the central pattern and one grounded move. A personalized reading connects your exact question, context and all three cards to explain the deciding condition, the evidence to verify and your clearest next steps.':'la lectura gratuita identifica el patrón central y un paso realista. La lectura personalizada conecta tu pregunta, contexto y las tres cartas para explicar la condición decisiva, la prueba a verificar y los siguientes pasos más claros.','The fact or conversation that should change your answer':'El hecho o conversación que debería cambiar tu respuesta','What strengthens each available route':'Lo que fortalece cada camino','How to test the stronger path without unnecessary risk':'Cómo probar la ruta más sólida sin riesgo innecesario','One-time purchase · Written by hand, usually emailed within 90 minutes; busy days up to 24 hours · You will not re-enter your question at checkout':'Compra única · Escrita a mano, suele enviarse por correo en 90 minutos; en días de mucha demanda hasta 24 horas · No repetirás la pregunta en el pago','Order summary':'Resumen del pedido','Close order summary':'Cerrar resumen','Your selection':'Tu selección','Change depth':'Cambiar profundidad','Change reading depth':'Cambiar profundidad de lectura','Based on':'Basado en','Your question':'Tu pregunta','Your cards':'Tus cartas','Delivery':'Entrega','By email':'Por correo','Timing':'Plazo','Total':'Total','What this depth adds:':'Lo que añade esta profundidad:','One-time purchase · no subscription':'Compra única · sin suscripción','You will not re-enter your question or context at checkout':'No volverás a introducir la pregunta ni el contexto','Retry secure price check':'Reintentar comprobación segura','Hide reading options':'Ocultar opciones','Checking…':'Comprobando…','Checking price…':'Comprobando precio…','Checking secure price…':'Comprobando precio seguro…','Confirming the secure storefront price…':'Confirmando el precio seguro…'},
    de:{'Saved on this device for 24 hours — the same question keeps the same cards':'24 Stunden auf diesem Gerät gespeichert — dieselbe Frage behält dieselben Karten','Your free pattern is saved for 24 hours. You can continue this question now by choosing a personalized depth.':'Dein kostenloses Muster ist 24 Stunden gespeichert. Wähle eine persönliche Tiefe, um diese Frage fortzusetzen.','What the next depth adds:':'Was die nächste Tiefe ergänzt:','your free reading identifies the central pattern and one grounded move. A personalized reading connects your exact question, context and all three cards to explain the deciding condition, the evidence to verify and your clearest next steps.':'die kostenlose Legung zeigt das zentrale Muster und einen realistischen Schritt. Die persönliche Deutung verbindet Frage, Kontext und Karten, um Bedingung, Beleg und nächste Schritte zu erklären.','The fact or conversation that should change your answer':'Der Fakt oder das Gespräch, das deine Antwort ändern sollte','What strengthens each available route':'Was jeden verfügbaren Weg stärkt','How to test the stronger path without unnecessary risk':'Wie du den stärkeren Weg ohne unnötiges Risiko testest','One-time purchase · Written by hand, usually emailed within 90 minutes; busy days up to 24 hours · You will not re-enter your question at checkout':'Einmaliger Kauf · Von Hand geschrieben, meist innerhalb von 90 Minuten per E-Mail; an vollen Tagen bis zu 24 Stunden · Keine erneute Eingabe beim Bezahlen','Order summary':'Bestellübersicht','Close order summary':'Bestellübersicht schließen','Your selection':'Deine Auswahl','Change depth':'Tiefe ändern','Change reading depth':'Deutungstiefe ändern','Based on':'Basiert auf','Your question':'Deine Frage','Your cards':'Deine Karten','Delivery':'Lieferung','By email':'Per E-Mail','Timing':'Zeitrahmen','Total':'Gesamt','What this depth adds:':'Was diese Tiefe ergänzt:','One-time purchase · no subscription':'Einmaliger Kauf · kein Abo','You will not re-enter your question or context at checkout':'Frage und Kontext werden beim Bezahlen nicht erneut eingegeben','Retry secure price check':'Sichere Preisprüfung wiederholen','Hide reading options':'Deutungsoptionen ausblenden','Checking…':'Wird geprüft…','Checking price…':'Preis wird geprüft…','Checking secure price…':'Sicherer Preis wird geprüft…','Confirming the secure storefront price…':'Sicherer Shoppreis wird bestätigt…'},
    pt:{'Saved on this device for 24 hours — the same question keeps the same cards':'Guardado 24 horas neste dispositivo — a mesma pergunta mantém as mesmas cartas','Your free pattern is saved for 24 hours. You can continue this question now by choosing a personalized depth.':'O padrão gratuito fica guardado 24 horas. Pode continuar esta pergunta escolhendo uma profundidade personalizada.','What the next depth adds:':'O que a profundidade seguinte acrescenta:','your free reading identifies the central pattern and one grounded move. A personalized reading connects your exact question, context and all three cards to explain the deciding condition, the evidence to verify and your clearest next steps.':'a leitura gratuita identifica o padrão central e um passo realista. A leitura personalizada liga pergunta, contexto e cartas para explicar a condição decisiva, a prova a confirmar e os próximos passos.','The fact or conversation that should change your answer':'O facto ou conversa que deve mudar a resposta','What strengthens each available route':'O que reforça cada caminho','How to test the stronger path without unnecessary risk':'Como testar a rota mais forte sem risco desnecessário','One-time purchase · Written by hand, usually emailed within 90 minutes; busy days up to 24 hours · You will not re-enter your question at checkout':'Compra única · Escrita à mão, normalmente por e-mail em 90 minutos; em dias de maior procura até 24 horas · Não repetirá a pergunta no pagamento','Order summary':'Resumo do pedido','Close order summary':'Fechar resumo','Your selection':'A sua seleção','Change depth':'Mudar profundidade','Change reading depth':'Mudar profundidade da leitura','Based on':'Com base em','Your question':'A sua pergunta','Your cards':'As suas cartas','Delivery':'Entrega','By email':'Por e-mail','Timing':'Prazo','Total':'Total','What this depth adds:':'O que esta profundidade acrescenta:','One-time purchase · no subscription':'Compra única · sem subscrição','You will not re-enter your question or context at checkout':'Não voltará a inserir a pergunta nem o contexto','Retry secure price check':'Repetir verificação segura','Hide reading options':'Ocultar opções','Checking…':'A verificar…','Checking price…':'A verificar preço…','Checking secure price…':'A verificar preço seguro…','Confirming the secure storefront price…':'A confirmar o preço seguro…'}
  };
  Object.keys(COMMERCE_TRANSLATIONS).forEach(function (language) { Object.assign(UI_TRANSLATIONS[language], COMMERCE_TRANSLATIONS[language]); });
  var ACTION_TRANSLATIONS = {
    tr:{'(Reversed)':'(Ters)','Reversed':'Ters','Upright':'Düz','Continue with this reading':'Bu yorumla devam et','Price unavailable':'Fiyat kullanılamıyor','Preparing secure checkout…':'Güvenli ödeme hazırlanıyor…','Continue to Secure Checkout':'Güvenli ödemeye devam et','Secure price unavailable':'Güvenli fiyat kullanılamıyor','We could not confirm a secure storefront price. Checkout remains disabled.':'Güvenli mağaza fiyatı doğrulanamadı. Ödeme devre dışı bırakıldı.','Price confirmed in':'Fiyat şu para biriminde doğrulandı:','Review order':'Siparişi gözden geçir','Choose depth':'Derinlik seç','Checkout is warming up, please tap again in a second.':'Ödeme hazırlanıyor; lütfen bir saniye sonra yeniden dokunun.','Checkout is warming up, please tap the button again.':'Ödeme hazırlanıyor; lütfen düğmeye yeniden dokunun.','Your three cards are revealed below.':'Üç kartınız aşağıda açıldı.'},
    es:{'(Reversed)':'(Invertida)','Reversed':'Invertida','Upright':'Derecha','Continue with this reading':'Continuar con esta lectura','Price unavailable':'Precio no disponible','Preparing secure checkout…':'Preparando pago seguro…','Continue to Secure Checkout':'Continuar al pago seguro','Secure price unavailable':'Precio seguro no disponible','We could not confirm a secure storefront price. Checkout remains disabled.':'No se pudo confirmar el precio seguro. El pago sigue desactivado.','Price confirmed in':'Precio confirmado en','Review order':'Revisar pedido','Choose depth':'Elegir profundidad','Checkout is warming up, please tap again in a second.':'El pago se está preparando; toca de nuevo en un segundo.','Checkout is warming up, please tap the button again.':'El pago se está preparando; vuelve a tocar el botón.','Your three cards are revealed below.':'Tus tres cartas aparecen a continuación.'},
    de:{'(Reversed)':'(Umgekehrt)','Reversed':'Umgekehrt','Upright':'Aufrecht','Continue with this reading':'Mit dieser Deutung fortfahren','Price unavailable':'Preis nicht verfügbar','Preparing secure checkout…':'Sichere Zahlung wird vorbereitet…','Continue to Secure Checkout':'Zur sicheren Zahlung','Secure price unavailable':'Sicherer Preis nicht verfügbar','We could not confirm a secure storefront price. Checkout remains disabled.':'Der sichere Shoppreis konnte nicht bestätigt werden. Die Zahlung bleibt deaktiviert.','Price confirmed in':'Preis bestätigt in','Review order':'Bestellung prüfen','Choose depth':'Tiefe wählen','Checkout is warming up, please tap again in a second.':'Die Zahlung wird vorbereitet; tippe gleich noch einmal.','Checkout is warming up, please tap the button again.':'Die Zahlung wird vorbereitet; tippe erneut auf die Schaltfläche.','Your three cards are revealed below.':'Deine drei Karten sind unten aufgedeckt.'},
    pt:{'(Reversed)':'(Invertida)','Reversed':'Invertida','Upright':'Direita','Continue with this reading':'Continuar com esta leitura','Price unavailable':'Preço indisponível','Preparing secure checkout…':'A preparar pagamento seguro…','Continue to Secure Checkout':'Continuar para o pagamento seguro','Secure price unavailable':'Preço seguro indisponível','We could not confirm a secure storefront price. Checkout remains disabled.':'Não foi possível confirmar o preço seguro. O pagamento continua desativado.','Price confirmed in':'Preço confirmado em','Review order':'Rever pedido','Choose depth':'Escolher profundidade','Checkout is warming up, please tap again in a second.':'O pagamento está a ser preparado; toque novamente dentro de um segundo.','Checkout is warming up, please tap the button again.':'O pagamento está a ser preparado; toque novamente no botão.','Your three cards are revealed below.':'As suas três cartas são reveladas abaixo.'}
  };
  Object.keys(ACTION_TRANSLATIONS).forEach(function (language) { Object.assign(UI_TRANSLATIONS[language], ACTION_TRANSLATIONS[language]); });
  var LOCALE_EXTRAS = {
    tr:{'No situation chosen yet':'Henüz durum seçilmedi','Change situation':'Durumu değiştir','Choose a situation':'Durum seç','Career decision':'Kariyer kararı','horizon':'zaman aralığı','Reading depth':'Yorum derinliği','Usually within 90 min · rarely up to 24 h':'Genellikle 90 dk içinde · nadiren 24 saate kadar','Düz ve ter kartlar':'Düz ve ters kartlar','Your reading':'Yorumunuz','Your question and cards are saved':'Sorunuz ve kartlarınız kaydedildi','Secure price check':'Güvenli fiyat kontrolü','Take your reading further':'Yorumunuzu derinleştirin'},
    es:{'Your question is sent securely only to create this free reading. It is never sent to analytics or advertising platforms. Payment information is handled only by Shopify\'s secure checkout.':'Tu pregunta se envía de forma segura solo para crear esta lectura gratuita. Nunca se comparte con sistemas de analítica ni publicidad. Los datos de pago se procesan únicamente en el pago seguro de Shopify.','Your three cards and core pattern are free. Your question is sent securely only to create this reading and is never sent to analytics.':'Tus tres cartas y el patrón central son gratuitos. Tu pregunta se envía de forma segura solo para crear esta lectura y nunca se comparte con analítica.','the free result identifies your core pattern. The personalized reading weighs the details you shared, the interaction between all three cards and the selected depth to build a fuller decision framework.':'el resultado gratuito identifica tu patrón central. La lectura personalizada valora los detalles que compartiste, la interacción de las tres cartas y la profundidad elegida para construir un marco de decisión más completo.','No situation chosen yet':'Aún no has elegido una situación','Change situation':'Cambiar situación','Choose a situation':'Elegir una situación','Career decision':'Decisión profesional','horizon':'plazo','Reading depth':'Profundidad de lectura','Usually within 90 min · rarely up to 24 h':'Normalmente en 90 min · excepcionalmente hasta 24 h','Your reading':'Tu lectura','Your question and cards are saved':'Tu pregunta y tus cartas están guardadas','Secure price check':'Comprobación de precio seguro','Take your reading further':'Profundiza en tu lectura'},
    de:{'Free Career':'Kostenloses','Tarot Reading':'Berufstarot','Your question is sent securely only to create this free reading. It is never sent to analytics or advertising platforms. Payment information is handled only by Shopify\'s secure checkout.':'Deine Frage wird sicher und nur für diese kostenlose Deutung übermittelt. Sie wird nie an Analyse- oder Werbesysteme gesendet. Zahlungsdaten werden ausschließlich über Shopifys sichere Kasse verarbeitet.','Your three cards and core pattern are free. Your question is sent securely only to create this reading and is never sent to analytics.':'Deine drei Karten und das Grundmuster sind kostenlos. Deine Frage wird nur für diese Deutung sicher übermittelt und nie an Analysedienste gesendet.','the free result identifies your core pattern. The personalized reading weighs the details you shared, the interaction between all three cards and the selected depth to build a fuller decision framework.':'das kostenlose Ergebnis zeigt dein Grundmuster. Die persönliche Deutung gewichtet deine Angaben, das Zusammenspiel aller drei Karten und die gewählte Tiefe zu einem umfassenderen Entscheidungsrahmen.','No situation chosen yet':'Noch keine Situation gewählt','Change situation':'Situation ändern','Choose a situation':'Situation wählen','Career decision':'Berufliche Entscheidung','horizon':'Zeitraum','Reading depth':'Deutungstiefe','Usually within 90 min · rarely up to 24 h':'Meist innerhalb von 90 Min. · selten bis zu 24 Std.','Your reading':'Deine Deutung','Your question and cards are saved':'Frage und Karten sind gespeichert','Secure price check':'Sichere Preisprüfung','Take your reading further':'Deutung vertiefen'},
    pt:{'Your question is sent securely only to create this free reading. It is never sent to analytics or advertising platforms. Payment information is handled only by Shopify\'s secure checkout.':'A sua pergunta é enviada com segurança apenas para criar esta leitura gratuita. Nunca é partilhada com sistemas de análise ou publicidade. Os dados de pagamento são processados apenas no checkout seguro da Shopify.','Your three cards and core pattern are free. Your question is sent securely only to create this reading and is never sent to analytics.':'As três cartas e o padrão central são gratuitos. A pergunta é enviada com segurança apenas para criar esta leitura e nunca é partilhada com sistemas de análise.','the free result identifies your core pattern. The personalized reading weighs the details you shared, the interaction between all three cards and the selected depth to build a fuller decision framework.':'o resultado gratuito identifica o padrão central. A leitura personalizada pondera os dados que partilhou, a interação entre as três cartas e a profundidade escolhida para criar um quadro de decisão mais completo.','No situation chosen yet':'Ainda não escolheu uma situação','Change situation':'Mudar situação','Choose a situation':'Escolher uma situação','Career decision':'Decisão profissional','horizon':'prazo','Reading depth':'Profundidade da leitura','Usually within 90 min · rarely up to 24 h':'Normalmente em 90 min · excecionalmente até 24 h','Your reading':'A sua leitura','Your question and cards are saved':'A pergunta e as cartas estão guardadas','Secure price check':'Verificação segura de preço','Take your reading further':'Aprofundar a leitura'}
  };
  Object.keys(LOCALE_EXTRAS).forEach(function (language) { Object.assign(UI_TRANSLATIONS[language], LOCALE_EXTRAS[language]); });
  var REVIEW_TRANSLATIONS = {"tr":{"Question":"Soru","Context":"Bağlam","Timeframe":"Zaman aralığı","Review context and timeframe":"Bağlamı ve zaman aralığını gözden geçir"},"de":{"Question":"Frage","Context":"Kontext","Timeframe":"Zeitrahmen","Review context and timeframe":"Kontext und Zeitrahmen prüfen"},"es":{"Question":"Pregunta","Context":"Contexto","Timeframe":"Plazo","Review context and timeframe":"Revisar contexto y plazo"},"pt":{"Question":"Pergunta","Context":"Contexto","Timeframe":"Prazo","Review context and timeframe":"Rever contexto e prazo"}};
  Object.keys(REVIEW_TRANSLATIONS).forEach(function (language) { Object.assign(UI_TRANSLATIONS[language], REVIEW_TRANSLATIONS[language]); });
  function uiText(value) {
    var dictionary = UI_TRANSLATIONS[LANG];
    return dictionary && Object.prototype.hasOwnProperty.call(dictionary, value) ? dictionary[value] : value;
  }
  function setLocalizedText(target, canonical) {
    var elements = typeof target === 'string' ? document.querySelectorAll(target) : (target ? [target] : []);
    Array.prototype.forEach.call(elements, function (el) { el.textContent = uiText(canonical); });
  }
  function setLocalizedTail(el, canonical) {
    if (!el) return;
    var textNodes = Array.prototype.filter.call(el.childNodes, function (node) { return node.nodeType === 3; });
    if (textNodes.length) textNodes[textNodes.length - 1].nodeValue = uiText(canonical);
    else el.appendChild(document.createTextNode(uiText(canonical)));
  }
  function setLocalizedSplitCopy(el, strongCanonical, bodyCanonical) {
    if (!el) return;
    var strong = el.querySelector('strong');
    if (strong) strong.textContent = uiText(strongCanonical);
    var wrote = false;
    Array.prototype.forEach.call(el.childNodes, function (node) {
      if (node.nodeType !== 3) return;
      if (!wrote) { node.nodeValue = ' ' + uiText(bodyCanonical); wrote = true; }
      else node.nodeValue = '';
    });
    if (!wrote) el.appendChild(document.createTextNode(' ' + uiText(bodyCanonical)));
  }
  var FLOW_LANGUAGE = {
    tr:{sitLead:'Kartları bu karardaki gerçekler, belirsizlik ve kontrol alanınızla ilişkilendireceğiz.',example:'Bu kararı zorlaştıran doğrulanabilir gerçeği tek cümleyle anlatın.',chips:['Karar vermeden önce neyi anlamalıyım?','Burada gerçekten kontrolümde olan ne?'],resultHead:'Bu karar için kartlarınız',detailLens:'Doğrulayabildiğiniz gerçekleri umduğunuz sonuçtan ayırın. Her iki seçeneğin de karşılaması gereken tek bir ölçüt ve hâlâ eksik olan tek bir kanıt belirleyin. Kesin öneri ve onu tersine çevirecek koşul kişiselleştirilmiş yorum için açık kalır.',preview:['Bugünkü ücretsiz ön izleme hakkı kullanıldı. Bu özet sayfadaki kart anlamlarından hazırlanmıştır; yeni kişisel yorum değildir.','Canlı ön izleme kalite denetimini geçmedi; bu kısa özet doğrudan kartlarınızdan hazırlandı.','Canlı ön izleme hizmeti geçici olarak kullanılamıyor. Yerel kart örüntünüz erişilebilir.','Canlı ön izleme yüklenemedi. Yerel kart örüntünüz erişilebilir ve model yorumu sunulmadı.'],errors:['Devam etmek için durumunuza en yakın seçeneği belirleyin.','Kararı etkileyen nokta hakkında en az iki anlamlı kelime ve 10 karakter yazın.','Netlik istediğiniz soruyu en az 8 karakterle anlamlı biçimde yazın.']},
    es:{sitLead:'Conectaremos las cartas con los hechos, la incertidumbre y lo que controlas en esta decisión.',example:'Describe en una frase el hecho verificable que dificulta la decisión.',chips:['¿Qué debería comprender antes de decidir?','¿Qué está realmente bajo mi control?'],resultHead:'Tus cartas para esta decisión',detailLens:'Separa los hechos verificables del resultado que esperas. Define un criterio que ambas opciones deban cumplir y una prueba que aún falte. La recomendación final y la condición que podría cambiarla quedan abiertas para la lectura personalizada.',preview:['Se ha usado el cupo de vista previa gratuita de hoy. Este resumen procede de los significados de las cartas; no es una interpretación en vivo.','La vista previa no superó el control de calidad; este resumen se preparó directamente desde tus cartas.','El servicio de vista previa está temporalmente fuera de servicio. Tu patrón local sigue disponible.','No se pudo cargar la vista previa. Tu patrón local sigue disponible y no se presenta ninguna interpretación del modelo.'],errors:['Elige la situación más parecida a la tuya para continuar.','Añade al menos dos palabras útiles y 10 caracteres sobre el hecho que influye en la decisión.','Escribe una pregunta con sentido de al menos 8 caracteres.']},
    de:{sitLead:'Wir verbinden die Karten mit den Fakten, der Unsicherheit und deinem Einfluss in dieser Entscheidung.',example:'Beschreibe in einem Satz den prüfbaren Fakt, der die Entscheidung schwierig macht.',chips:['Was sollte ich vor der Entscheidung verstehen?','Was liegt hier wirklich in meiner Hand?'],resultHead:'Deine Karten zu dieser Entscheidung',detailLens:'Trenne prüfbare Fakten vom erhofften Ergebnis. Lege ein Kriterium fest, das beide Optionen erfüllen müssen, und einen noch fehlenden Beleg. Die endgültige Empfehlung und ihre Umkehrbedingung bleiben der persönlichen Deutung vorbehalten.',preview:['Das heutige kostenlose Vorschaukontingent ist verbraucht. Diese Übersicht stammt aus den Kartenbedeutungen und ist keine neue persönliche Deutung.','Die Live-Vorschau bestand die Qualitätsprüfung nicht; diese Übersicht wurde direkt aus deinen Karten erstellt.','Der Live-Vorschaudienst ist vorübergehend nicht verfügbar. Dein lokales Kartenmuster bleibt sichtbar.','Die Live-Vorschau konnte nicht geladen werden. Dein lokales Kartenmuster bleibt sichtbar; keine Modellinterpretation wird behauptet.'],errors:['Wähle die Situation, die deiner am nächsten kommt.','Schreibe mindestens zwei sinnvolle Wörter und 10 Zeichen zu dem entscheidenden Fakt.','Schreibe eine sinnvolle Frage mit mindestens 8 Zeichen.']},
    pt:{sitLead:'Vamos ligar as cartas aos factos, à incerteza e ao que controla nesta decisão.',example:'Descreva numa frase o facto verificável que torna a decisão difícil.',chips:['O que devo compreender antes de decidir?','O que está realmente sob o meu controlo?'],resultHead:'As cartas para esta decisão',detailLens:'Separe os factos verificáveis do resultado que espera. Defina um critério que as duas opções devem cumprir e uma prova ainda em falta. A recomendação final e a condição que a mudaria ficam em aberto para a leitura personalizada.',preview:['A quota de pré-visualização gratuita de hoje foi usada. Este resumo vem dos significados das cartas; não é uma interpretação ao vivo.','A pré-visualização não passou no controlo de qualidade; este resumo foi preparado diretamente a partir das cartas.','O serviço de pré-visualização está temporariamente indisponível. O padrão local continua visível.','Não foi possível carregar a pré-visualização. O padrão local continua visível e não é apresentada qualquer interpretação do modelo.'],errors:['Escolha a situação mais próxima da sua para continuar.','Acrescente pelo menos duas palavras úteis e 10 caracteres sobre o facto que influencia a decisão.','Escreva uma pergunta clara com pelo menos 8 caracteres.']}
  };
  var NEW_FLOW_LANGUAGE = {
    tr: {
      freeThree:'Ücretsiz · Üç kartlı yorum', askTitle:'Tek ve net bir kariyer sorusu sorun',
      intro:'Gerçekten netlik istediğiniz soruyu yazın. Kartlar mevcut örüntüyü, gizli kalabilecek noktayı ve en iyi sonraki adımınızı yansıtacak.', qLabel:'Kariyer sorunuz', qPlaceholder:'Bir sonraki kariyer hamlemi yapmadan önce neyi anlamalıyım?',
      options:'Bağlam veya zaman aralığı ekleyin', optional:'İsteğe bağlı', contextLabel:'Yararlı bağlam', contextHelp:'Yanıtı gerçekten değiştirebilecek tek gerçeği ekleyin. Boş bırakabilirsiniz.', contextPlaceholder:'Örneğin: Yazılı bir teklifim var, ancak mevcut işverenim tarih vermeden terfi sözü verdi.', timeframe:'Zaman aralığı',
      privacy:'Üç kartınız ve kısa yorumunuz ücretsizdir. Sorunuz yalnızca bu yorumu oluşturmak için kullanılır ve analitik sistemlerine gönderilmez.', freeReading:'Ücretsiz yorumunuz', synthesis:'Bu örüntünün söylediği', critical:'Açık kalan kritik soru', criticalTail:'Aynı soru ve kartlarla devam edin—yeniden hiçbir şey girmeniz gerekmez.',
      nextStep:'Kişiselleştirilmiş sonraki adım', offerTitle:'Bu örüntüyü bir karara dönüştürün', offerIntro:'İhtiyacınız olan derinliği seçin. Her iki yorum da tam sorunuzu, isteğe bağlı bağlamınızı ve aynı üç kartı kullanır.', clear:'Net öneri', plan:'Tam eylem planı', mapDesc:'Belirleyici koşul, doğrulanacak kanıt ve üç uygulanabilir sonraki adım.', planDesc:'Yol karşılaştırması, riskler, zamanlama işaretleri ve odaklı 30 günlük plan.', mapAction:'Karar yorumumu al', planAction:'30 günlük planımı oluştur', purchase:'Tek seferlik dijital yorum · Güvenli ödeme · Elle yazılır, genellikle 90 dakika içinde e-postalanır; yoğun günlerde 24 saate kadar sürebilir', saved:'Sorunuz ve kartlarınız bu cihazda 24 saat saklanır.', different:'Farklı bir kariyer sorusu sorun', group:'Kişiselleştirilmiş kariyer yorumu seçenekleri'
    },
    es: {
      freeThree:'Gratis · Lectura de tres cartas', askTitle:'Haz una pregunta profesional clara',
      intro:'Escribe la pregunta que realmente quieres aclarar. Tus cartas reflejarán el patrón actual, lo que puede estar oculto y tu mejor siguiente paso.', qLabel:'Tu pregunta profesional', qPlaceholder:'¿Qué debería comprender antes de dar mi próximo paso profesional?',
      options:'Añadir contexto o un plazo', optional:'Opcional', contextLabel:'Contexto útil', contextHelp:'Añade solo el hecho que podría cambiar materialmente la respuesta. Puedes dejarlo en blanco.', contextPlaceholder:'Por ejemplo: tengo una oferta por escrito, pero mi empresa actual me ha prometido un ascenso sin fecha.', timeframe:'Plazo',
      privacy:'Tus tres cartas y la lectura breve son gratuitas. Tu pregunta solo se usa para crear esta lectura y nunca se envía a analítica.', freeReading:'Tu lectura gratuita', synthesis:'Lo que sugiere este patrón', critical:'La pregunta crítica que sigue abierta', criticalTail:'Continúa con la misma pregunta y las mismas cartas; no tendrás que escribir nada de nuevo.',
      nextStep:'Siguiente paso personalizado', offerTitle:'Convierte este patrón en una decisión', offerIntro:'Elige la profundidad que necesitas. Ambas lecturas usan tu pregunta exacta, el contexto opcional y estas mismas tres cartas.', clear:'Recomendación clara', plan:'Plan de acción completo', mapDesc:'La condición decisiva, la prueba que debes verificar y tres acciones prácticas.', planDesc:'Comparación de caminos, riesgos, señales de tiempo y un plan centrado de 30 días.', mapAction:'Obtener mi lectura de decisión', planAction:'Crear mi plan de 30 días', purchase:'Lectura digital de pago único · Pago seguro · Escrita a mano, normalmente se envía en 90 minutos; en días de mucha demanda hasta 24 horas', saved:'Tu pregunta y tus cartas se guardan 24 horas en este dispositivo.', different:'Haz otra pregunta profesional', group:'Opciones de lectura profesional personalizada'
    },
    de: {
      freeThree:'Kostenlos · Drei-Karten-Deutung', askTitle:'Stelle eine klare Berufsfrage',
      intro:'Schreibe die Frage, zu der du wirklich Klarheit möchtest. Die Karten spiegeln das aktuelle Muster, den verborgenen Faktor und deinen besten nächsten Schritt.', qLabel:'Deine Berufsfrage', qPlaceholder:'Was sollte ich verstehen, bevor ich meinen nächsten beruflichen Schritt mache?',
      options:'Kontext oder Zeitraum ergänzen', optional:'Optional', contextLabel:'Hilfreicher Kontext', contextHelp:'Ergänze nur den Fakt, der die Antwort wesentlich ändern könnte. Du kannst das Feld leer lassen.', contextPlaceholder:'Zum Beispiel: Ich habe ein schriftliches Angebot, aber mein aktueller Arbeitgeber hat eine Beförderung ohne Termin versprochen.', timeframe:'Zeitraum',
      privacy:'Deine drei Karten und die kurze Deutung sind kostenlos. Deine Frage wird nur für diese Deutung verwendet und nie an Analysedienste gesendet.', freeReading:'Deine kostenlose Deutung', synthesis:'Was dieses Muster nahelegt', critical:'Die entscheidende offene Frage', criticalTail:'Fahre mit derselben Frage und denselben Karten fort—du musst nichts erneut eingeben.',
      nextStep:'Persönlicher nächster Schritt', offerTitle:'Mache aus diesem Muster eine Entscheidung', offerIntro:'Wähle die Tiefe, die du brauchst. Beide Deutungen verwenden deine genaue Frage, optionalen Kontext und dieselben drei Karten.', clear:'Klare Empfehlung', plan:'Vollständiger Aktionsplan', mapDesc:'Die entscheidende Bedingung, der zu prüfende Beleg und drei praktische nächste Schritte.', planDesc:'Wegvergleich, Risiken, Zeitsignale und ein fokussierter 30-Tage-Plan.', mapAction:'Meine Entscheidungsdeutung erhalten', planAction:'Meinen 30-Tage-Plan erstellen', purchase:'Einmalige digitale Deutung · Sichere Zahlung · Von Hand geschrieben, meist innerhalb von 90 Minuten per E-Mail; an vollen Tagen bis zu 24 Stunden', saved:'Deine Frage und Karten bleiben 24 Stunden auf diesem Gerät gespeichert.', different:'Eine andere Berufsfrage stellen', group:'Optionen für eine persönliche Berufsdeutung'
    },
    pt: {
      freeThree:'Grátis · Leitura de três cartas', askTitle:'Faça uma pergunta profissional clara',
      intro:'Escreva a pergunta sobre a qual realmente procura clareza. As cartas refletirão o padrão atual, o que pode estar oculto e o melhor próximo passo.', qLabel:'A sua pergunta profissional', qPlaceholder:'O que devo compreender antes do meu próximo passo profissional?',
      options:'Adicionar contexto ou prazo', optional:'Opcional', contextLabel:'Contexto útil', contextHelp:'Acrescente apenas o facto que pode mudar materialmente a resposta. Pode deixar em branco.', contextPlaceholder:'Por exemplo: tenho uma oferta por escrito, mas o empregador atual prometeu uma promoção sem data.', timeframe:'Prazo',
      privacy:'As três cartas e a leitura breve são gratuitas. A pergunta só é usada para criar esta leitura e nunca é enviada para análise.', freeReading:'A sua leitura gratuita', synthesis:'O que este padrão sugere', critical:'A pergunta crítica ainda em aberto', criticalTail:'Continue com a mesma pergunta e as mesmas cartas—não precisa de voltar a escrever nada.',
      nextStep:'Próximo passo personalizado', offerTitle:'Transforme este padrão numa decisão', offerIntro:'Escolha a profundidade de que precisa. As duas leituras usam a sua pergunta exata, o contexto opcional e estas mesmas três cartas.', clear:'Recomendação clara', plan:'Plano de ação completo', mapDesc:'A condição decisiva, a prova a confirmar e três próximos passos práticos.', planDesc:'Comparação de caminhos, riscos, sinais de tempo e um plano focado de 30 dias.', mapAction:'Obter a minha leitura de decisão', planAction:'Criar o meu plano de 30 dias', purchase:'Leitura digital de compra única · Pagamento seguro · Escrita à mão, normalmente enviada em 90 minutos; em dias de maior procura até 24 horas', saved:'A pergunta e as cartas ficam guardadas 24 horas neste dispositivo.', different:'Fazer outra pergunta profissional', group:'Opções de leitura profissional personalizada'
    }
  };
  function localizeCr2Static() {
    if (LANG === 'en') {
      var englishDepthNote = document.querySelector('#top .cr2-hero-price-note');
      if (englishDepthNote) englishDepthNote.textContent = 'Your three-card reading is free. Optional personalized readings are available after your result.';
      return;
    }
    document.querySelectorAll('[data-cr2]').forEach(function (root) {
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        var parent = node.parentElement;
        if (!parent || /^(?:SCRIPT|STYLE|TEMPLATE|TEXTAREA)$/.test(parent.tagName) || parent.closest('[data-clarity-mask]')) continue;
        var raw = node.nodeValue;
        var trimmed = raw.trim();
        var translated = uiText(trimmed);
        if (trimmed && translated !== trimmed) node.nodeValue = raw.replace(trimmed, translated);
      }
      root.querySelectorAll('[placeholder],[aria-label],[data-cr2-ctabase]').forEach(function (el) {
        ['placeholder','aria-label','data-cr2-ctabase'].forEach(function (name) {
          var value = el.getAttribute(name);
          var translated = value ? uiText(value) : value;
          if (translated && translated !== value) el.setAttribute(name, translated);
        });
      });
    });

    /* The site-wide translation runtime can alter only part of a sentence
       before this feature initializes. These structural assignments make the
       critical funnel copy deterministic instead of depending on the text it
       happened to receive from that earlier runtime. */
    var heroCopy = document.querySelector('#top .cr2-hero-inner > div:first-child');
    if (heroCopy) {
      setLocalizedText(heroCopy.children[0], 'Personalized 3-card work guidance');
      var heroTitle = heroCopy.querySelector('h1');
      if (heroTitle) {
        var titleText = Array.prototype.find.call(heroTitle.childNodes, function (node) { return node.nodeType === 3; });
        if (titleText) titleText.nodeValue = uiText('Free Career') + ' ';
        setLocalizedText(heroTitle.querySelector('span'), 'Tarot Reading');
      }
      setLocalizedText(heroCopy.querySelector('h1 + p'), 'Bring one real work decision to this free 3-card career tarot spread. See the pattern, the hidden factor and the next move reflected in your cards.');
      setLocalizedText(heroCopy.querySelector('.cr2-hero-micro'), 'Secure · Instant · No sign-up');
      var twoDepthCopy = {
        tr: 'Üç kartlık yorumunuz ücretsizdir. Sonucunuzdan sonra isteğe bağlı kişisel yorum seçenekleri sunulur.',
        es: 'La lectura de tres cartas es gratuita. Después del resultado, puedes elegir una lectura personalizada opcional.',
        de: 'Die Drei-Karten-Deutung ist kostenlos. Nach deinem Ergebnis stehen optionale persönliche Deutungen zur Auswahl.',
        pt: 'A leitura de três cartas é gratuita. Depois do resultado, pode escolher uma leitura personalizada opcional.'
      };
      setLocalizedText(heroCopy.querySelector('.cr2-hero-price-note'), twoDepthCopy[LANG] || 'Your three-card reading is free. Optional personalized readings are available after your result.');
    }
    var heroLabels = ['Your leverage','Unseen factor','Next move'];
    document.querySelectorAll('#top .cr2-hero-art > div:last-child > span:not([aria-hidden])').forEach(function (el, i) { if (heroLabels[i]) setLocalizedText(el, heroLabels[i]); });
    var trust = document.querySelector('[data-screen-label="Trust strip"]');
    var trustLabels = ['Full 78-card deck','Upright & reversed','Never sent to analytics','No email required to begin','Secure one-time checkout'];
    if (trust) {
      trust.querySelectorAll('.cr2-trust-items > div').forEach(function (el, i) { if (trustLabels[i]) setLocalizedTail(el, trustLabels[i]); });
      setLocalizedText(trust.querySelector('.cr2-trust-note'), 'Your question is sent securely only to create this free reading. It is never sent to analytics or advertising platforms. Payment information is handled only by Shopify\'s secure checkout.');
    }

    var decide = document.querySelector('#decide');
    if (decide) {
      setLocalizedText(decide.querySelector('[data-cr2-previous]'), 'Return to my saved reading');
    }

    var form = document.querySelector('#form');
    if (form) {
      form.querySelectorAll('[data-cr2-tf]').forEach(function (el) { setLocalizedText(el, el.getAttribute('data-cr2-tf')); });
    }
    var newFlowCopy = NEW_FLOW_LANGUAGE[LANG];
    if (newFlowCopy && form) {
      setLocalizedText(form.querySelector('.cr2-eyebrow'), newFlowCopy.freeThree);
      setLocalizedText(form.querySelector('h2'), newFlowCopy.askTitle);
      setLocalizedText(form.querySelector('#cr2-q-help'), newFlowCopy.intro);
      setLocalizedText(form.querySelector('label[for="cr2-q"]'), newFlowCopy.qLabel);
      var localizedQuestion = form.querySelector('[data-cr2-q]');
      if (localizedQuestion) localizedQuestion.setAttribute('placeholder', newFlowCopy.qPlaceholder);
      var optionSummary = form.querySelector('[data-cr2-options] summary');
      if (optionSummary) {
        setLocalizedText(optionSummary.querySelector('span:first-child'), newFlowCopy.options);
        setLocalizedText(optionSummary.querySelector('.cr2-optional-label'), newFlowCopy.optional);
      }
      setLocalizedText(form.querySelector('label[for="cr2-detail"]'), newFlowCopy.contextLabel);
      setLocalizedText(form.querySelector('#cr2-detail-help'), newFlowCopy.contextHelp);
      var localizedDetail = form.querySelector('[data-cr2-detail]');
      if (localizedDetail) localizedDetail.setAttribute('placeholder', newFlowCopy.contextPlaceholder);
      setLocalizedText(form.querySelector('#cr2-tf-label'), newFlowCopy.timeframe);
      setLocalizedText(form.querySelector('.cr2-privacy-note'), newFlowCopy.privacy);
    }

    var cards = document.querySelector('#cards');
    var positionLabels = ['Current reality','Hidden factor','Best next move'];
    if (cards) {
      cards.querySelectorAll('[data-cr2-card]').forEach(function (el, i) {
        var role = el.querySelector('.cr2-card-role');
        if (positionLabels[i] && role) setLocalizedText(role, positionLabels[i]);
      });
    }
    var pattern = document.querySelector('[data-cr2-pattern]');
    if (pattern) {
      setLocalizedText(pattern.querySelector('[data-cr2-act="newdecision"]'), 'Ask about a different decision');
    }
    setLocalizedText(document.querySelector('[data-cr2-act="newdecision"]'), 'Ask about a different decision');
    var offer = document.querySelector('[data-cr2-offer]');
    if (offer) {
      var depthGroup = offer.querySelector('[role="group"],[role="radiogroup"]');
      if (depthGroup) depthGroup.setAttribute('aria-label', uiText('Reading depth'));
    }
    var result = document.querySelector('[data-cr2-result]');
    if (newFlowCopy && result) {
      setLocalizedText(result.querySelector('.cr2-synthesis-card .cr2-eyebrow'), newFlowCopy.freeReading);
      setLocalizedText(result.querySelector('.cr2-synthesis-card h3'), newFlowCopy.synthesis);
      setLocalizedText(result.querySelector('.cr2-open-question .cr2-eyebrow'), newFlowCopy.critical);
      setLocalizedText(result.querySelector('.cr2-open-question > span'), newFlowCopy.criticalTail);
      setLocalizedText(result.querySelector('.cr2-offer-heading .cr2-eyebrow'), newFlowCopy.nextStep);
      setLocalizedText(result.querySelector('.cr2-offer-heading h3'), newFlowCopy.offerTitle);
      setLocalizedText(result.querySelector('.cr2-offer-heading p'), newFlowCopy.offerIntro);
      var localizedMap = result.querySelector('[data-cr2-ladder="map"]');
      var localizedPlan = result.querySelector('[data-cr2-ladder="plan"]');
      if (localizedMap) {
        setLocalizedText(localizedMap.querySelector('.cr2-package-kicker'), newFlowCopy.clear);
        setLocalizedText(localizedMap.querySelector('.cr2-package-description'), newFlowCopy.mapDesc);
        var mapAction = localizedMap.querySelector('[data-cr2-laction]');
        if (mapAction) mapAction.setAttribute('data-cr2-cta-base', newFlowCopy.mapAction);
      }
      if (localizedPlan) {
        setLocalizedText(localizedPlan.querySelector('.cr2-package-kicker'), newFlowCopy.plan);
        setLocalizedText(localizedPlan.querySelector('.cr2-package-description'), newFlowCopy.planDesc);
        var planAction = localizedPlan.querySelector('[data-cr2-laction]');
        if (planAction) planAction.setAttribute('data-cr2-cta-base', newFlowCopy.planAction);
      }
      setLocalizedText(result.querySelector('.cr2-purchase-note'), newFlowCopy.purchase);
      setLocalizedText(result.querySelector('.cr2-result-actions > span'), newFlowCopy.saved);
      setLocalizedText(result.querySelector('[data-cr2-act="newdecision"]'), newFlowCopy.different);
      var localizedGroup = result.querySelector('.cr2-package-grid');
      if (localizedGroup) localizedGroup.setAttribute('aria-label', newFlowCopy.group);
    }

    var drawer = document.querySelector('[data-cr2-drawer]');
    if (drawer) {
      setLocalizedText(drawer.querySelector('#cr2-order-title'), 'Order summary');
      var closeDrawer = drawer.querySelector('[data-cr2-act="closedrawer"]');
      if (closeDrawer) closeDrawer.setAttribute('aria-label', uiText('Close order summary'));
      var drawerLabels = ['Your selection','Change depth','Based on','Your question','Your cards','Delivery','Timing','Total'];
      drawer.querySelectorAll('dt').forEach(function (el, i) { if (drawerLabels[i]) setLocalizedText(el, drawerLabels[i]); });
      var changeGroup = drawer.querySelector('[role="radiogroup"]');
      if (changeGroup) changeGroup.setAttribute('aria-label', uiText('Change reading depth'));
      var delivery = Array.prototype.find.call(drawer.querySelectorAll('dd'), function (el) { return el.textContent.trim() === 'By email' || /correo|E-Mail|e-mail|posta/i.test(el.textContent); });
      if (delivery) setLocalizedText(delivery, 'By email');
      var timing = Array.prototype.find.call(drawer.querySelectorAll('dd'), function (el) { return /20.?30/.test(el.textContent) && /90/.test(el.textContent); });
      if (timing) setLocalizedText(timing, 'Usually within 90 min · rarely up to 24 h');
      setLocalizedSplitCopy(drawer.querySelector('p'), 'What this depth adds:', 'the free result identifies your core pattern. The personalized reading weighs the details you shared, the interaction between all three cards and the selected depth to build a fuller decision framework.');
      var drawerNotes = drawer.querySelectorAll('ul li');
      if (drawerNotes[0]) setLocalizedText(drawerNotes[0], 'One-time purchase · no subscription');
      if (drawerNotes[1]) setLocalizedText(drawerNotes[1], 'You will not re-enter your question or context at checkout');
    }
  }
  function dedupeReviewAccessibility() {
    var seen = {};
    document.querySelectorAll('figure blockquote').forEach(function (quote) {
      var fingerprint = String(quote.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      var figure = quote.closest('figure');
      if (!fingerprint || !figure) return;
      if (seen[fingerprint]) figure.setAttribute('aria-hidden', 'true');
      else seen[fingerprint] = true;
    });
  }

  var SITS = [
    { id: 'job', title: 'Job Application', sub: 'Whether this application moves forward', phrase: 'job application',
      lead: 'Let us look at what strengthens or weakens your position.',
      example: 'I completed the final interview and they mentioned one concern.',
      chips: ['What should I understand before accepting this role?', 'What is the strongest part of my application right now?'],
      packs: ['The Hiring Signal', 'Application & Employer Perspective Map', '30-Day Job Search Blueprint'],
      tension: 'Your readiness is competing with how you are being read from the outside.',
      hidden: 'The outcome may rest less on your ability than on one concern that has not been answered directly.',
      move: 'Name the single concern you would address if you were asked about it tomorrow.' },
    { id: 'stay', title: 'Stay or Leave?', sub: 'Whether this role still fits', phrase: 'stay-or-leave decision',
      lead: 'Let us compare staying with leaving using the same criteria.',
      example: 'My role is stable, but growth has stalled for the last year.',
      chips: ['What would need to change for staying to make sense?', 'What am I actually trying to leave behind?'],
      packs: ['The Deciding Factor', 'Stay vs Leave Map', 'Exit or Rebuild Plan'],
      tension: 'Security is competing with visible progress.',
      hidden: 'The decision may depend less on ability and more on an unresolved conversation or condition.',
      move: 'Define the evidence you would need before making the next commitment.' },
    { id: 'offers', title: 'Two Offers or Paths', sub: 'Which option to commit to', phrase: 'choice between two paths',
      lead: 'Let us compare the two paths using the same criteria.',
      example: 'One offer pays more, the other has the work I actually want.',
      chips: ['Which path better supports long-term growth?', 'What am I most likely to regret in six months?'],
      packs: ['The Key Difference', 'Two-Path Career Comparison', 'Choice + First 30 Days Plan'],
      tension: 'Immediate gain is competing with the direction you want to build.',
      hidden: 'The two options may be closer than they look, with one difference doing most of the deciding.',
      move: 'Write down the single criterion you would accept as the tie-breaker.' },
    { id: 'promotion', title: 'Promotion or Raise', sub: 'Whether now is the time to ask', phrase: 'promotion or raise',
      lead: 'Let us look at what strengthens your case before you ask.',
      example: 'My manager supports me, but the budget is uncertain.',
      chips: ['What should I prepare before asking for a raise?', 'What is holding my case back right now?'],
      packs: ['Readiness & Hidden Block', 'Promotion & Leverage Map', 'Raise or Promotion Conversation Plan'],
      tension: 'The work you have done is competing with how visible it has been.',
      hidden: 'Timing and one decision-maker constraint may weigh more than performance.',
      move: 'Collect the two results you would lead with in the conversation.' },
    { id: 'change', title: 'Career Change', sub: 'Whether to move in a new direction', phrase: 'career change',
      lead: 'Let us compare your current path with the one you are considering.',
      example: 'I am drawn to a new field but I would start again at a lower level.',
      chips: ['Is this direction a real fit or an escape?', 'What could I test before committing?'],
      packs: ['Career Direction Signal', 'Current Path vs New Path', 'Career Pivot Blueprint'],
      tension: 'The pull of a new direction is competing with what you have already built.',
      hidden: 'What attracts you to the new path may be available in a smaller form first.',
      move: 'Choose one low-cost test that would tell you something within a month.' },
    { id: 'conflict', title: 'Workplace Conflict', sub: 'The dynamic you are dealing with', phrase: 'workplace situation',
      lead: 'Let us look at the dynamic shaping the situation.',
      example: 'A colleague keeps overriding my decisions in front of others.',
      chips: ['What boundary would change this situation?', 'Is this temporary or a signal to leave?'],
      packs: ['The Hidden Workplace Dynamic', 'Power, Boundary & Resolution Map', '30-Day Protection & Next-Move Plan'],
      tension: 'Keeping the peace is competing with protecting your standing.',
      hidden: 'The dynamic may be shaped by a pressure that has little to do with you.',
      move: 'Decide the one boundary you are willing to state plainly and hold.' },
    { id: 'business', title: 'Business or Side Hustle', sub: 'Whether the idea is worth starting', phrase: 'business idea',
      lead: 'Let us look at the strength of the idea and its first real test.',
      example: 'I have paying interest but no time to deliver it yet.',
      chips: ['What should I test before investing more?', 'Is now the right time to start?'],
      packs: ['Opportunity Signal', 'Viability, Risk & First-Test Map', '30-Day Validation Blueprint'],
      tension: 'Conviction in the idea is competing with the resources it needs.',
      hidden: 'The risk may sit in capacity and timing rather than in the idea itself.',
      move: 'Define the smallest version you could put in front of one real customer.' },
    { id: 'other', title: 'Something Else', sub: 'Another decision about work', phrase: 'career decision',
      lead: 'Let us look at the decision in front of you.',
      example: 'Describe the fact that makes this decision difficult.',
      chips: ['What should I understand before deciding?', 'What is actually within my control here?'],
      packs: ['The Deciding Factor', 'Career Crossroads Map', '30-Day Career Move Plan'],
      tension: 'Certainty is competing with the need to move.',
      hidden: 'One condition you have not tested may be carrying most of the weight.',
      move: 'Name the fact that would change your answer, then go and find it.' }
  ];

  var PACKS = [
    { id: 'map', tier: 'deeper', sitIndex: 1, base: 'Career Crossroads Map', kicker: 'Clarity',
      prev: 'What strengthens each available route' },
    { id: 'plan', tier: 'indepth', sitIndex: 2, base: '30-Day Career Move Plan', kicker: 'Action plan',
      prev: 'How to test the stronger path without unnecessary risk' }
  ];

  /* Every Major has an upright and reversed career core. The position frame
     below turns that core into three genuinely different interpretations,
     so all 22 Majors have 132 meaningful career outcomes rather than a
     generic fallback. Minor cards combine their rank and suit semantics. */
  var MAJOR_COPY = {
    'The Fool': ['A new path and calculated risk', 'openness to beginning before every answer is known', 'impulse, poor preparation or risk that has not been priced'],
    'The Magician': ['Resources already within reach', 'skill, tools and influence ready to be used deliberately', 'scattered effort, underused leverage or presentation without substance'],
    'The High Priestess': ['Information not yet spoken', 'discernment and useful information that is still private', 'withheld facts, ignored intuition or ambiguity being mistaken for evidence'],
    'The Empress': ['Growth through support and cultivation', 'an environment where people, ideas or value can develop', 'over-giving, weak boundaries or growth without a sustainable structure'],
    'The Emperor': ['Structure, authority and clear ownership', 'stable leadership, defined responsibility and enforceable standards', 'rigidity, control without trust or authority that is not accountable'],
    'The Hierophant': ['Institutions, mentors and established rules', 'benefit from proven process, expert guidance or recognized credentials', 'a convention that no longer fits or gatekeeping that needs to be questioned'],
    'The Lovers': ['Alignment and a values-based choice', 'a decision that works when values, people and commitments agree', 'misalignment, divided loyalty or a choice being deferred to avoid a trade-off'],
    'The Chariot': ['Directed momentum', 'focused will and coordinated effort moving one route forward', 'competing directions, force without control or speed that hides instability'],
    'Strength': ['Steady influence over force', 'patience, composure and confident boundaries', 'self-doubt, exhausted restraint or pressure replacing real influence'],
    'The Hermit': ['Independent judgement and expert reflection', 'space to review the evidence and listen to hard-won experience', 'isolation, over-analysis or advice being avoided'],
    'Wheel of Fortune': ['A changing cycle and external timing', 'an opening created by movement in conditions beyond one person', 'volatility, repeated patterns or waiting for luck instead of preparing'],
    'Justice': ['Evidence, fairness and written terms', 'a decision shaped by the record, the criteria and what was actually agreed', 'unequal standards, missing documentation or an outcome distorted by bias'],
    'The Hanged Man': ['A new angle before commitment', 'useful delay, reframing and a willingness to test another perspective', 'stalling without learning or sacrifice that no longer changes the outcome'],
    'Death': ['A necessary ending and professional transition', 'completion that clears space for a more truthful structure or identity', 'resistance to an ending, incomplete closure or change postponed at a cost'],
    'Temperance': ['Integration, pacing and sustainable fit', 'combining strengths carefully and adjusting until the arrangement works', 'poor fit, extremes or a compromise that drains more than it solves'],
    'The Devil': ['Binding incentives and hidden dependence', 'clear sight of the reward, fear or obligation that is controlling the choice', 'denial, a loosening hold or the first real chance to renegotiate the terms'],
    'The Tower': ['A structure that cannot stay unchanged', 'a disruptive truth that removes a false assumption quickly', 'avoided disruption, unstable foundations or change arriving in smaller warnings'],
    'The Star': ['Credible hope and renewed direction', 'recovery, visibility and a long-range aim worth rebuilding toward', 'discouragement, weak feedback or optimism that needs a concrete proof point'],
    'The Moon': ['Uncertainty, projection and incomplete facts', 'a situation that must be navigated carefully while information is partial', 'confusion beginning to clear or anxiety being mistaken for a forecast'],
    'The Sun': ['Visible progress and open support', 'clarity, recognition and conditions that favour transparent action', 'delayed recognition, overconfidence or success that still needs definition'],
    'Judgement': ['An honest review and deliberate second act', 'a clear reckoning with what the work has become and what must follow', 'self-doubt, an avoided evaluation or an old decision still asking to be answered'],
    'The World': ['Completion and readiness for the next level', 'a cycle delivering its full value and opening a wider stage', 'unfinished business, delayed completion or growth constrained by one missing piece']
  };
  var MINOR_RANK = {
    Ace: ['a concrete opening', 'new potential that can be tested now', 'potential blocked by timing, confidence or missing resources'],
    Two: ['a live choice', 'two factors that need to be held in honest comparison', 'indecision, imbalance or a choice being postponed'],
    Three: ['early expansion', 'progress that grows through collaboration and visible effort', 'misalignment, weak cooperation or growth without coordination'],
    Four: ['stability and containment', 'a foundation worth using and protecting', 'stagnation, over-control or stability becoming a limit'],
    Five: ['friction and a useful reality check', 'pressure that reveals what must change', 'conflict being internalized or a setback that has not been processed'],
    Six: ['exchange and forward movement', 'support, recognition or transition creating usable momentum', 'unequal exchange, delayed movement or recognition with conditions attached'],
    Seven: ['assessment and defended ground', 'discernment, patience and a position that must be evaluated', 'weak boundaries, doubt or effort continuing without evidence'],
    Eight: ['focused movement and practice', 'repeatable action building speed or mastery', 'delay, scattered work or motion that is not producing learning'],
    Nine: ['earned independence and resilience', 'experience creating a strong near-term position', 'fatigue, guardedness or success carrying an unsustainable cost'],
    Ten: ['culmination and long-term consequence', 'a cycle showing the full weight or return of prior choices', 'overload, unstable legacy or completion being resisted'],
    Page: ['a message and learning edge', 'curiosity, study or news opening a practical next step', 'inexperience, mixed messages or preparation that has not become action'],
    Knight: ['a method of pursuit', 'commitment expressed through a recognizable pace and style', 'misdirected effort, unreliable pace or action outrunning judgement'],
    Queen: ['mature internal command', 'discernment and influence that do not need performance to be real', 'overextension, self-protection or capability being kept too private'],
    King: ['accountable external leadership', 'authority, standards and decision-making that can hold consequences', 'domination, inflexibility or leadership detached from the facts']
  };
  var POSITION_FRAME = [
    function (core) { return 'Your present position is being shaped by ' + core + '. Treat that as the real starting point before judging the outcome.'; },
    function (core) { return 'The quiet factor to verify is ' + core + '. Ask where it appears in the facts, not only in your expectations.'; },
    function (core) { return 'Keep the next step inside your control by working with ' + core + '. Turn it into one small test before making an irreversible commitment.'; }
  ];
  var CARD_NAME_LANGUAGE = {
    tr:{
      major:{'The Fool':'Deli','The Magician':'Büyücü','The High Priestess':'Başrahibe','The Empress':'İmparatoriçe','The Emperor':'İmparator','The Hierophant':'Aziz','The Lovers':'Âşıklar','The Chariot':'Savaş Arabası','Strength':'Güç','The Hermit':'Ermiş','Wheel of Fortune':'Kader Çarkı','Justice':'Adalet','The Hanged Man':'Asılan Adam','Death':'Ölüm','Temperance':'Denge','The Devil':'Şeytan','The Tower':'Kule','The Star':'Yıldız','The Moon':'Ay','The Sun':'Güneş','Judgement':'Mahkeme','The World':'Dünya'},
      rank:{Ace:'Ası',Two:'İkilisi',Three:'Üçlüsü',Four:'Dörtlüsü',Five:'Beşlisi',Six:'Altılısı',Seven:'Yedilisi',Eight:'Sekizlisi',Nine:'Dokuzlusu',Ten:'Onlusu',Page:'Prensi',Knight:'Şövalyesi',Queen:'Kraliçesi',King:'Kralı'},
      suit:{Wands:'Değnek',Cups:'Kupa',Swords:'Kılıç',Pentacles:'Tılsım'}
    },
    es:{
      major:{'The Fool':'El Loco','The Magician':'El Mago','The High Priestess':'La Sacerdotisa','The Empress':'La Emperatriz','The Emperor':'El Emperador','The Hierophant':'El Hierofante','The Lovers':'Los Enamorados','The Chariot':'El Carro','Strength':'La Fuerza','The Hermit':'El Ermitaño','Wheel of Fortune':'La Rueda de la Fortuna','Justice':'La Justicia','The Hanged Man':'El Colgado','Death':'La Muerte','Temperance':'La Templanza','The Devil':'El Diablo','The Tower':'La Torre','The Star':'La Estrella','The Moon':'La Luna','The Sun':'El Sol','Judgement':'El Juicio','The World':'El Mundo'},
      rank:{Ace:'As',Two:'Dos',Three:'Tres',Four:'Cuatro',Five:'Cinco',Six:'Seis',Seven:'Siete',Eight:'Ocho',Nine:'Nueve',Ten:'Diez',Page:'Sota',Knight:'Caballero',Queen:'Reina',King:'Rey'},
      suit:{Wands:'Bastos',Cups:'Copas',Swords:'Espadas',Pentacles:'Oros'}
    },
    de:{
      major:{'The Fool':'Der Narr','The Magician':'Der Magier','The High Priestess':'Die Hohepriesterin','The Empress':'Die Herrscherin','The Emperor':'Der Herrscher','The Hierophant':'Der Hierophant','The Lovers':'Die Liebenden','The Chariot':'Der Wagen','Strength':'Die Kraft','The Hermit':'Der Eremit','Wheel of Fortune':'Rad des Schicksals','Justice':'Die Gerechtigkeit','The Hanged Man':'Der Gehängte','Death':'Der Tod','Temperance':'Die Mäßigkeit','The Devil':'Der Teufel','The Tower':'Der Turm','The Star':'Der Stern','The Moon':'Der Mond','The Sun':'Die Sonne','Judgement':'Das Gericht','The World':'Die Welt'},
      rank:{Ace:'Ass',Two:'Zwei',Three:'Drei',Four:'Vier',Five:'Fünf',Six:'Sechs',Seven:'Sieben',Eight:'Acht',Nine:'Neun',Ten:'Zehn',Page:'Bube',Knight:'Ritter',Queen:'Königin',King:'König'},
      suit:{Wands:'Stäbe',Cups:'Kelche',Swords:'Schwerter',Pentacles:'Münzen'}
    },
    pt:{
      major:{'The Fool':'O Louco','The Magician':'O Mago','The High Priestess':'A Sacerdotisa','The Empress':'A Imperatriz','The Emperor':'O Imperador','The Hierophant':'O Hierofante','The Lovers':'Os Enamorados','The Chariot':'O Carro','Strength':'A Força','The Hermit':'O Eremita','Wheel of Fortune':'Roda da Fortuna','Justice':'A Justiça','The Hanged Man':'O Enforcado','Death':'A Morte','Temperance':'A Temperança','The Devil':'O Diabo','The Tower':'A Torre','The Star':'A Estrela','The Moon':'A Lua','The Sun':'O Sol','Judgement':'O Julgamento','The World':'O Mundo'},
      rank:{Ace:'Ás',Two:'Dois',Three:'Três',Four:'Quatro',Five:'Cinco',Six:'Seis',Seven:'Sete',Eight:'Oito',Nine:'Nove',Ten:'Dez',Page:'Pajem',Knight:'Cavaleiro',Queen:'Rainha',King:'Rei'},
      suit:{Wands:'Paus',Cups:'Copas',Swords:'Espadas',Pentacles:'Ouros'}
    }
  };
  function localizedCardName(name) {
    if (LANG === 'en') return name;
    var language = CARD_NAME_LANGUAGE[LANG];
    if (language && language.major[name]) return language.major[name];
    var parts = String(name || '').split(' of ');
    if (language && parts.length === 2 && language.rank[parts[0]] && language.suit[parts[1]]) {
      if (LANG === 'tr') return language.suit[parts[1]] + ' ' + language.rank[parts[0]];
      if (LANG === 'de') return language.rank[parts[0]] + ' der ' + language.suit[parts[1]];
      return language.rank[parts[0]] + ' de ' + language.suit[parts[1]];
    }
    var dictionary = window.DeckauraToolTranslations || {};
    return String(dictionary[name] || name);
  }
  function careerFrame(position, core) {
    var language = CARD_LANGUAGE[LANG];
    if (!language) return POSITION_FRAME[position](core);
    return language.frames[position].replace('{core}', core);
  }
  var SUIT_COPY = {
    Wands: {
      theme: 'Initiative, pace and workable momentum',
      lines: [
        'The current picture is being shaped by initiative, pace and whether the work genuinely gives you energy.',
        'The hidden factor may be momentum without a defined target, or an opening no one has acted on yet.',
        'Choose one small action that tests demand or fit before you make the larger move.'
      ]
    },
    Cups: {
      theme: 'Values, relationships and emotional fit',
      lines: [
        'The current picture depends on whether the work, team and direction still match what matters to you.',
        'The hidden factor may be an emotional loyalty or discomfort that has not yet been named plainly.',
        'Have the conversation that tests belonging and values before treating the feeling as a final verdict.'
      ]
    },
    Swords: {
      theme: 'Facts, pressure and communication',
      lines: [
        'The current picture is being shaped by the facts available, the pressure around them and how clearly they are being discussed.',
        'The hidden factor may be an unresolved assumption, decision or conversation doing more work than it appears.',
        'Clarify the key fact in writing and decide from what is verified rather than what is implied.'
      ]
    },
    Pentacles: {
      theme: 'Evidence, resources and practical stability',
      lines: [
        'The current picture rests on tangible evidence: pay, skill, time, stability and what has actually been delivered.',
        'The hidden factor may be a resource constraint or practical trade-off that optimism alone cannot remove.',
        'Set one concrete measure—budget, date, proof of demand or skill milestone—before committing.'
      ]
    }
  };

  /* Card-specific orientation semantics from the existing localized editorial
     dataset; card identity, draw, positions and signed snapshots stay separate. */
  var MINOR_CARD_COPY = {
  "Ace of Wands": {
    "en": [
      "initiative, spark, potential",
      "stalled start, doubt, misplaced energy"
    ],
    "tr": [
      "girişim, ilham, potansiyel",
      "geciken başlangıç, şüphe, yanlış yönlenen enerji"
    ],
    "de": [
      "Initiative, Inspiration, Potenzial",
      "gebremster Beginn, Zweifel, fehlgeleitete Energie"
    ],
    "es": [
      "iniciativa, inspiración, potencial",
      "inicio frenado, duda, energía mal dirigida"
    ],
    "pt": [
      "iniciativa, inspiração, potencial",
      "início travado, dúvida, energia mal direcionada"
    ]
  },
  "Two of Wands": {
    "en": [
      "planning, choice, wider view",
      "indecision, playing safe, fear of leaving"
    ],
    "tr": [
      "planlama, seçim, geniş bakış",
      "kararsızlık, güvenliye sığınma, ayrılma korkusu"
    ],
    "de": [
      "Planung, Wahl, Weitblick",
      "Unentschlossenheit, Absicherung, Angst vor Aufbruch"
    ],
    "es": [
      "planificación, elección, perspectiva amplia",
      "indecisión, exceso de cautela, miedo a partir"
    ],
    "pt": [
      "planeamento, escolha, visão ampla",
      "indecisão, cautela excessiva, medo de partir"
    ]
  },
  "Three of Wands": {
    "en": [
      "expansion, foresight, progress",
      "delay, narrow view, waiting on others"
    ],
    "tr": [
      "genişleme, öngörü, ilerleme",
      "gecikme, dar bakış, başkasını bekleme"
    ],
    "de": [
      "Erweiterung, Voraussicht, Fortschritt",
      "Verzögerung, enge Sicht, Warten auf andere"
    ],
    "es": [
      "expansión, previsión, progreso",
      "demora, visión limitada, esperar a otros"
    ],
    "pt": [
      "expansão, previsão, progresso",
      "atraso, visão limitada, espera por outros"
    ]
  },
  "Four of Wands": {
    "en": [
      "stability, belonging, milestone",
      "unsettled ground, tension at home, postponed joy"
    ],
    "tr": [
      "istikrar, aidiyet, dönüm noktası",
      "belirsiz zemin, evde gerilim, ertelenen sevinç"
    ],
    "de": [
      "Stabilität, Zugehörigkeit, Meilenstein",
      "Unsicherheit, häusliche Spannung, vertagte Freude"
    ],
    "es": [
      "estabilidad, pertenencia, hito",
      "inestabilidad, tensión doméstica, alegría aplazada"
    ],
    "pt": [
      "estabilidade, pertença, marco",
      "instabilidade, tensão em casa, alegria adiada"
    ]
  },
  "Five of Wands": {
    "en": [
      "friction, competition, noise",
      "avoided conflict, suppressed tension, resolution"
    ],
    "tr": [
      "sürtüşme, rekabet, gürültü",
      "çatışmadan kaçınma, bastırma, çözüm"
    ],
    "de": [
      "Reibung, Wettbewerb, Unruhe",
      "vermiedener Konflikt, unterdrückte Spannung, Lösung"
    ],
    "es": [
      "fricción, competencia, ruido",
      "conflicto evitado, tensión reprimida, resolución"
    ],
    "pt": [
      "atrito, competição, ruído",
      "conflito evitado, tensão reprimida, resolução"
    ]
  },
  "Six of Wands": {
    "en": [
      "recognition, confidence, progress",
      "lost momentum, unmet credit, doubt"
    ],
    "tr": [
      "takdir, güven, ilerleme",
      "ivme kaybı, görülmeme, şüphe"
    ],
    "de": [
      "Anerkennung, Vertrauen, Fortschritt",
      "verlorener Schwung, fehlende Würdigung, Zweifel"
    ],
    "es": [
      "reconocimiento, confianza, progreso",
      "impulso perdido, falta de reconocimiento, duda"
    ],
    "pt": [
      "reconhecimento, confiança, progresso",
      "perda de impulso, falta de reconhecimento, dúvida"
    ]
  },
  "Seven of Wands": {
    "en": [
      "defence, persistence, boundary",
      "overwhelm, giving ground, exhaustion"
    ],
    "tr": [
      "savunma, kararlılık, sınır",
      "bunalmışlık, geri çekilme, yorgunluk"
    ],
    "de": [
      "Verteidigung, Beharrlichkeit, Grenze",
      "Überforderung, Rückzug, Erschöpfung"
    ],
    "es": [
      "defensa, persistencia, límites",
      "agobio, retirada, agotamiento"
    ],
    "pt": [
      "defesa, persistência, limite",
      "sobrecarga, recuo, esgotamento"
    ]
  },
  "Eight of Wands": {
    "en": [
      "acceleration, news, momentum",
      "delay, scattered focus, held messages"
    ],
    "tr": [
      "hızlanma, haber, ivme",
      "gecikme, dağınıklık, ertelenen iletişim"
    ],
    "de": [
      "Beschleunigung, Nachricht, Schwung",
      "Verzögerung, Zerstreuung, vertagte Kommunikation"
    ],
    "es": [
      "aceleración, noticias, impulso",
      "demora, dispersión, comunicación aplazada"
    ],
    "pt": [
      "aceleração, notícia, impulso",
      "atraso, dispersão, comunicação adiada"
    ]
  },
  "Nine of Wands": {
    "en": [
      "resilience, guarding, near-completion",
      "depletion, defensiveness, giving up early"
    ],
    "tr": [
      "dayanıklılık, korunma, sona yaklaşma",
      "tükenme, savunmacılık, erken vazgeçme"
    ],
    "de": [
      "Widerstandskraft, Schutz, naher Abschluss",
      "Erschöpfung, Abwehrhaltung, frühes Aufgeben"
    ],
    "es": [
      "resistencia, protección, cercanía al final",
      "agotamiento, actitud defensiva, abandono temprano"
    ],
    "pt": [
      "resiliência, proteção, proximidade do fim",
      "esgotamento, postura defensiva, desistência precoce"
    ]
  },
  "Ten of Wands": {
    "en": [
      "overload, duty, strain",
      "releasing weight, delegation, collapse"
    ],
    "tr": [
      "aşırı yük, görev, zorlanma",
      "yükü bırakma, paylaşma, tükenme"
    ],
    "de": [
      "Überlastung, Pflicht, Anspannung",
      "Entlastung, Delegation, Erschöpfung"
    ],
    "es": [
      "sobrecarga, deber, tensión",
      "soltar peso, delegar, agotarse"
    ],
    "pt": [
      "sobrecarga, dever, tensão",
      "alívio da carga, delegação, esgotamento"
    ]
  },
  "Page of Wands": {
    "en": [
      "curiosity, experiment, eagerness",
      "restlessness, unfinished starts, impatience"
    ],
    "tr": [
      "merak, deneme, heves",
      "huzursuzluk, yarım başlangıç, sabırsızlık"
    ],
    "de": [
      "Neugier, Versuch, Begeisterung",
      "Unruhe, unfertiger Beginn, Ungeduld"
    ],
    "es": [
      "curiosidad, prueba, entusiasmo",
      "inquietud, inicios inconclusos, impaciencia"
    ],
    "pt": [
      "curiosidade, experiência, entusiasmo",
      "inquietação, começos incompletos, impaciência"
    ]
  },
  "Knight of Wands": {
    "en": [
      "boldness, speed, adventure",
      "impulsiveness, burnout, inconsistency"
    ],
    "tr": [
      "cesaret, hız, keşif",
      "dürtüsellik, tükenmişlik, tutarsızlık"
    ],
    "de": [
      "Mut, Tempo, Entdeckung",
      "Impulsivität, Erschöpfung, Unbeständigkeit"
    ],
    "es": [
      "audacia, velocidad, aventura",
      "impulsividad, desgaste, inconsistencia"
    ],
    "pt": [
      "ousadia, velocidade, aventura",
      "impulsividade, desgaste, inconstância"
    ]
  },
  "Queen of Wands": {
    "en": [
      "confidence, presence, magnetism",
      "self-doubt, jealousy, dimmed voice"
    ],
    "tr": [
      "güven, varlık, çekicilik",
      "özgüvensizlik, kıskançlık, sesini kısma"
    ],
    "de": [
      "Vertrauen, Präsenz, Ausstrahlung",
      "Selbstzweifel, Eifersucht, gedämpfte Stimme"
    ],
    "es": [
      "confianza, presencia, magnetismo",
      "inseguridad, celos, voz apagada"
    ],
    "pt": [
      "confiança, presença, magnetismo",
      "insegurança, ciúme, voz apagada"
    ]
  },
  "King of Wands": {
    "en": [
      "leadership, conviction, scale",
      "domination, impulsive orders, ego"
    ],
    "tr": [
      "liderlik, inanç, geniş bakış",
      "baskınlık, acele emir, ego"
    ],
    "de": [
      "Führung, Überzeugung, Weitblick",
      "Dominanz, voreilige Befehle, Ego"
    ],
    "es": [
      "liderazgo, convicción, amplitud",
      "dominio, órdenes impulsivas, ego"
    ],
    "pt": [
      "liderança, convicção, amplitude",
      "dominação, ordens impulsivas, ego"
    ]
  },
  "Ace of Cups": {
    "en": [
      "openness, feeling, offer",
      "guarded heart, withheld feeling, blocked flow"
    ],
    "tr": [
      "açıklık, duygu, teklif",
      "korunma, duyguyu tutma, tıkanma"
    ],
    "de": [
      "Offenheit, Gefühl, Angebot",
      "geschütztes Herz, zurückgehaltenes Gefühl, Blockade"
    ],
    "es": [
      "apertura, emoción, ofrecimiento",
      "corazón protegido, emoción retenida, bloqueo"
    ],
    "pt": [
      "abertura, sentimento, oferta",
      "coração protegido, emoção contida, bloqueio"
    ]
  },
  "Two of Cups": {
    "en": [
      "mutuality, connection, agreement",
      "imbalance, disconnect, one-sided effort"
    ],
    "tr": [
      "karşılıklılık, bağ, anlaşma",
      "dengesizlik, kopukluk, tek taraflı çaba"
    ],
    "de": [
      "Gegenseitigkeit, Verbindung, Einigung",
      "Ungleichgewicht, Distanz, einseitige Mühe"
    ],
    "es": [
      "reciprocidad, conexión, acuerdo",
      "desequilibrio, desconexión, esfuerzo unilateral"
    ],
    "pt": [
      "reciprocidade, ligação, acordo",
      "desequilíbrio, desligação, esforço unilateral"
    ]
  },
  "Three of Cups": {
    "en": [
      "community, celebration, allies",
      "isolation, gossip, crowded focus"
    ],
    "tr": [
      "topluluk, kutlama, destek",
      "yalnızlık, dedikodu, dağınık odak"
    ],
    "de": [
      "Gemeinschaft, Feier, Verbündete",
      "Isolation, Gerüchte, zerstreuter Fokus"
    ],
    "es": [
      "comunidad, celebración, aliados",
      "aislamiento, rumores, atención dispersa"
    ],
    "pt": [
      "comunidade, celebração, aliados",
      "isolamento, rumores, foco disperso"
    ]
  },
  "Four of Cups": {
    "en": [
      "withdrawal, apathy, reflection",
      "re-engagement, noticing, restlessness"
    ],
    "tr": [
      "geri çekilme, ilgisizlik, düşünme",
      "yeniden katılma, fark etme, huzursuzluk"
    ],
    "de": [
      "Rückzug, Gleichgültigkeit, Reflexion",
      "erneute Beteiligung, Wahrnehmung, Unruhe"
    ],
    "es": [
      "retirada, apatía, reflexión",
      "reconexión, atención, inquietud"
    ],
    "pt": [
      "recolhimento, apatia, reflexão",
      "reaproximação, atenção, inquietação"
    ]
  },
  "Five of Cups": {
    "en": [
      "grief, regret, focus on loss",
      "acceptance, moving on, forgiveness"
    ],
    "tr": [
      "yas, pişmanlık, kayba odaklanma",
      "kabullenme, ilerleme, bağışlama"
    ],
    "de": [
      "Trauer, Bedauern, Verlustfokus",
      "Annahme, Weitergehen, Vergebung"
    ],
    "es": [
      "duelo, arrepentimiento, atención a la pérdida",
      "aceptación, avanzar, perdón"
    ],
    "pt": [
      "luto, arrependimento, foco na perda",
      "aceitação, seguir adiante, perdão"
    ]
  },
  "Six of Cups": {
    "en": [
      "memory, nostalgia, reconnection",
      "stuck in the past, idealising, letting go"
    ],
    "tr": [
      "anı, özlem, yeniden bağ",
      "geçmişte kalma, idealleştirme, bırakma"
    ],
    "de": [
      "Erinnerung, Sehnsucht, Wiederverbindung",
      "Vergangenheitsbindung, Idealisierung, Loslassen"
    ],
    "es": [
      "memoria, nostalgia, reconexión",
      "anclarse al pasado, idealización, soltar"
    ],
    "pt": [
      "memória, nostalgia, religação",
      "prisão ao passado, idealização, desapego"
    ]
  },
  "Seven of Cups": {
    "en": [
      "choice, fantasy, dispersal",
      "clarity, narrowing down, disillusion"
    ],
    "tr": [
      "seçenek, hayal, dağılma",
      "netlik, eleme, yanılsamadan çıkma"
    ],
    "de": [
      "Wahl, Fantasie, Zerstreuung",
      "Klarheit, Eingrenzung, Ernüchterung"
    ],
    "es": [
      "elección, fantasía, dispersión",
      "claridad, selección, desengaño"
    ],
    "pt": [
      "escolha, fantasia, dispersão",
      "clareza, seleção, desilusão"
    ]
  },
  "Eight of Cups": {
    "en": [
      "departure, search, honesty",
      "drifting, fear of leaving, return"
    ],
    "tr": [
      "ayrılış, arayış, dürüstlük",
      "sürüklenme, ayrılma korkusu, geri dönüş"
    ],
    "de": [
      "Aufbruch, Suche, Ehrlichkeit",
      "Treibenlassen, Angst vor Abschied, Rückkehr"
    ],
    "es": [
      "partida, búsqueda, honestidad",
      "deriva, miedo a partir, regreso"
    ],
    "pt": [
      "partida, busca, honestidade",
      "deriva, medo de partir, retorno"
    ]
  },
  "Nine of Cups": {
    "en": [
      "contentment, wish met, ease",
      "surface pleasure, unmet wish, excess"
    ],
    "tr": [
      "memnuniyet, dilek, rahatlık",
      "yüzeysel haz, eksiklik, aşırılık"
    ],
    "de": [
      "Zufriedenheit, erfüllter Wunsch, Leichtigkeit",
      "oberflächlicher Genuss, offener Wunsch, Übermaß"
    ],
    "es": [
      "satisfacción, deseo cumplido, comodidad",
      "placer superficial, deseo pendiente, exceso"
    ],
    "pt": [
      "satisfação, desejo atendido, tranquilidade",
      "prazer superficial, desejo pendente, excesso"
    ]
  },
  "Ten of Cups": {
    "en": [
      "harmony, belonging, fulfilment",
      "misaligned ideal, strained bonds, distance"
    ],
    "tr": [
      "uyum, aidiyet, doyum",
      "uyumsuz beklenti, gergin bağ, mesafe"
    ],
    "de": [
      "Harmonie, Zugehörigkeit, Erfüllung",
      "unpassendes Ideal, belastete Bindung, Distanz"
    ],
    "es": [
      "armonía, pertenencia, plenitud",
      "ideal desajustado, vínculos tensos, distancia"
    ],
    "pt": [
      "harmonia, pertença, realização",
      "ideal desalinhado, laços tensos, distância"
    ]
  },
  "Page of Cups": {
    "en": [
      "sensitivity, invitation, sincerity",
      "naivety, moodiness, guarded message"
    ],
    "tr": [
      "duyarlılık, davet, samimiyet",
      "saflık, değişken ruh hali, çekingen mesaj"
    ],
    "de": [
      "Feinfühligkeit, Einladung, Aufrichtigkeit",
      "Naivität, Stimmungsschwankung, zurückhaltende Nachricht"
    ],
    "es": [
      "sensibilidad, invitación, sinceridad",
      "ingenuidad, altibajos, mensaje contenido"
    ],
    "pt": [
      "sensibilidade, convite, sinceridade",
      "ingenuidade, oscilação de humor, mensagem contida"
    ]
  },
  "Knight of Cups": {
    "en": [
      "romance, pursuit, offer",
      "inconsistency, idealism, empty gesture"
    ],
    "tr": [
      "romantizm, yaklaşım, teklif",
      "tutarsızlık, idealizm, boş jest"
    ],
    "de": [
      "Romantik, Annäherung, Angebot",
      "Unbeständigkeit, Idealismus, leere Geste"
    ],
    "es": [
      "romance, acercamiento, ofrecimiento",
      "inconsistencia, idealismo, gesto vacío"
    ],
    "pt": [
      "romance, aproximação, oferta",
      "inconstância, idealismo, gesto vazio"
    ]
  },
  "Queen of Cups": {
    "en": [
      "empathy, attunement, care",
      "over-giving, absorbed emotion, withdrawal"
    ],
    "tr": [
      "empati, duyarlılık, özen",
      "aşırı verme, yüklenme, geri çekilme"
    ],
    "de": [
      "Empathie, Gespür, Fürsorge",
      "übermäßiges Geben, übernommene Gefühle, Rückzug"
    ],
    "es": [
      "empatía, sintonía, cuidado",
      "dar en exceso, absorber emociones, retirada"
    ],
    "pt": [
      "empatia, sintonia, cuidado",
      "doação excessiva, absorção emocional, recolhimento"
    ]
  },
  "King of Cups": {
    "en": [
      "composure, maturity, steadiness",
      "suppression, volatility, cool distance"
    ],
    "tr": [
      "soğukkanlılık, olgunluk, denge",
      "bastırma, değişkenlik, soğuk mesafe"
    ],
    "de": [
      "Gelassenheit, Reife, Beständigkeit",
      "Unterdrückung, Schwankung, kühle Distanz"
    ],
    "es": [
      "serenidad, madurez, equilibrio",
      "represión, volatilidad, frialdad"
    ],
    "pt": [
      "serenidade, maturidade, equilíbrio",
      "repressão, instabilidade, frieza"
    ]
  },
  "Ace of Swords": {
    "en": [
      "clarity, insight, honesty",
      "confusion, harsh words, misused truth"
    ],
    "tr": [
      "netlik, içgörü, dürüstlük",
      "karışıklık, sert söz, gerçeği çarpıtma"
    ],
    "de": [
      "Klarheit, Einsicht, Ehrlichkeit",
      "Verwirrung, harte Worte, missbrauchte Wahrheit"
    ],
    "es": [
      "claridad, comprensión, honestidad",
      "confusión, palabras duras, verdad mal utilizada"
    ],
    "pt": [
      "clareza, compreensão, honestidade",
      "confusão, palavras duras, verdade mal usada"
    ]
  },
  "Two of Swords": {
    "en": [
      "stalemate, avoidance, weighing",
      "forced choice, information arriving, deadlock"
    ],
    "tr": [
      "çıkmaz, kaçınma, tartma",
      "zorunlu seçim, yeni bilgi, tıkanıklık"
    ],
    "de": [
      "Stillstand, Vermeidung, Abwägen",
      "erzwungene Wahl, neue Information, Blockade"
    ],
    "es": [
      "punto muerto, evasión, ponderación",
      "elección forzada, nueva información, bloqueo"
    ],
    "pt": [
      "impasse, fuga, ponderação",
      "escolha forçada, nova informação, bloqueio"
    ]
  },
  "Three of Swords": {
    "en": [
      "hurt, honesty, grief",
      "recovery, released hurt, unspoken pain"
    ],
    "tr": [
      "incinme, dürüstlük, yas",
      "toparlanma, acıyı bırakma, söylenmeyen acı"
    ],
    "de": [
      "Verletzung, Ehrlichkeit, Trauer",
      "Erholung, gelöster Schmerz, ungesagtes Leid"
    ],
    "es": [
      "dolor, honestidad, duelo",
      "recuperación, soltar dolor, pena no expresada"
    ],
    "pt": [
      "dor, honestidade, luto",
      "recuperação, liberação da dor, sofrimento não dito"
    ]
  },
  "Four of Swords": {
    "en": [
      "pause, recovery, stillness",
      "restlessness, avoided rest, re-entry"
    ],
    "tr": [
      "duraklama, toparlanma, sakinlik",
      "huzursuzluk, dinlenmeyi erteleme, dönüş"
    ],
    "de": [
      "Pause, Erholung, Stille",
      "Unruhe, vermiedene Ruhe, Wiedereinstieg"
    ],
    "es": [
      "pausa, recuperación, quietud",
      "inquietud, descanso evitado, regreso"
    ],
    "pt": [
      "pausa, recuperação, quietude",
      "inquietação, descanso evitado, retorno"
    ]
  },
  "Five of Swords": {
    "en": [
      "conflict, cost of winning, discord",
      "reconciliation, walking away, lingering resentment"
    ],
    "tr": [
      "çatışma, kazanmanın bedeli, uyumsuzluk",
      "uzlaşma, uzaklaşma, süren kırgınlık"
    ],
    "de": [
      "Konflikt, Kosten des Sieges, Zwietracht",
      "Versöhnung, Weggehen, bleibender Groll"
    ],
    "es": [
      "conflicto, coste de vencer, discordia",
      "reconciliación, alejarse, resentimiento persistente"
    ],
    "pt": [
      "conflito, custo da vitória, discórdia",
      "reconciliação, afastamento, ressentimento persistente"
    ]
  },
  "Six of Swords": {
    "en": [
      "moving on, distance, easing",
      "stuck in transit, resistance, unfinished exit"
    ],
    "tr": [
      "ilerleme, mesafe, hafifleme",
      "arada kalma, direnç, tamamlanmayan ayrılış"
    ],
    "de": [
      "Weitergehen, Abstand, Erleichterung",
      "festgefahrener Übergang, Widerstand, offener Abschied"
    ],
    "es": [
      "avanzar, distancia, alivio",
      "tránsito detenido, resistencia, salida incompleta"
    ],
    "pt": [
      "seguir adiante, distância, alívio",
      "transição travada, resistência, saída incompleta"
    ]
  },
  "Seven of Swords": {
    "en": [
      "strategy, concealment, workaround",
      "exposure, honesty, self-deception"
    ],
    "tr": [
      "strateji, gizleme, alternatif yol",
      "açığa çıkma, dürüstlük, kendini yanıltma"
    ],
    "de": [
      "Strategie, Verbergen, Umweg",
      "Offenlegung, Ehrlichkeit, Selbsttäuschung"
    ],
    "es": [
      "estrategia, ocultación, alternativa",
      "exposición, honestidad, autoengaño"
    ],
    "pt": [
      "estratégia, ocultação, alternativa",
      "exposição, honestidade, autoengano"
    ]
  },
  "Eight of Swords": {
    "en": [
      "feeling trapped, hesitation, restriction",
      "freeing, new perspective, self-imposed limits"
    ],
    "tr": [
      "sıkışmışlık, tereddüt, kısıt",
      "özgürleşme, yeni bakış, içsel sınırlar"
    ],
    "de": [
      "Engegefühl, Zögern, Einschränkung",
      "Befreiung, neue Perspektive, eigene Grenzen"
    ],
    "es": [
      "sentirse atrapado, vacilación, restricción",
      "liberación, nueva perspectiva, límites propios"
    ],
    "pt": [
      "sensação de prisão, hesitação, restrição",
      "libertação, nova perspectiva, limites próprios"
    ]
  },
  "Nine of Swords": {
    "en": [
      "anxiety, worry, sleeplessness",
      "relief, perspective, hidden fear"
    ],
    "tr": [
      "kaygı, endişe, uykusuzluk",
      "rahatlama, bakış açısı, gizli korku"
    ],
    "de": [
      "Angst, Sorge, Schlaflosigkeit",
      "Erleichterung, Perspektive, verborgene Angst"
    ],
    "es": [
      "ansiedad, preocupación, insomnio",
      "alivio, perspectiva, miedo oculto"
    ],
    "pt": [
      "ansiedade, preocupação, insónia",
      "alívio, perspectiva, medo oculto"
    ]
  },
  "Ten of Swords": {
    "en": [
      "ending, bottom point, release",
      "recovery, resistance to ending, slow relief"
    ],
    "tr": [
      "bitiş, dip nokta, bırakma",
      "toparlanma, bitişe direnç, yavaş rahatlama"
    ],
    "de": [
      "Ende, Tiefpunkt, Loslassen",
      "Erholung, Widerstand gegen Ende, langsame Entlastung"
    ],
    "es": [
      "final, punto bajo, liberación",
      "recuperación, resistencia al final, alivio gradual"
    ],
    "pt": [
      "fim, ponto baixo, desapego",
      "recuperação, resistência ao fim, alívio gradual"
    ]
  },
  "Page of Swords": {
    "en": [
      "curiosity, questioning, alertness",
      "haste, gossip, scattered thinking"
    ],
    "tr": [
      "merak, sorgulama, dikkat",
      "acele, dedikodu, dağınık düşünce"
    ],
    "de": [
      "Neugier, Fragen, Wachsamkeit",
      "Hast, Gerüchte, zerstreutes Denken"
    ],
    "es": [
      "curiosidad, preguntas, atención",
      "prisa, rumores, pensamiento disperso"
    ],
    "pt": [
      "curiosidade, perguntas, atenção",
      "pressa, rumores, pensamento disperso"
    ]
  },
  "Knight of Swords": {
    "en": [
      "urgency, directness, drive",
      "recklessness, blunt words, burnout"
    ],
    "tr": [
      "aciliyet, doğrudanlık, hareket",
      "düşüncesizlik, kırıcı söz, tükenme"
    ],
    "de": [
      "Dringlichkeit, Direktheit, Antrieb",
      "Leichtsinn, verletzende Worte, Erschöpfung"
    ],
    "es": [
      "urgencia, franqueza, impulso",
      "imprudencia, palabras hirientes, desgaste"
    ],
    "pt": [
      "urgência, franqueza, impulso",
      "imprudência, palavras duras, desgaste"
    ]
  },
  "Queen of Swords": {
    "en": [
      "discernment, honesty, boundaries",
      "coldness, criticism, guarded distance"
    ],
    "tr": [
      "ayırt etme, dürüstlük, sınırlar",
      "soğukluk, eleştiri, mesafe"
    ],
    "de": [
      "Unterscheidung, Ehrlichkeit, Grenzen",
      "Kälte, Kritik, Distanz"
    ],
    "es": [
      "discernimiento, honestidad, límites",
      "frialdad, crítica, distancia"
    ],
    "pt": [
      "discernimento, honestidade, limites",
      "frieza, crítica, distância"
    ]
  },
  "King of Swords": {
    "en": [
      "clarity, authority, principle",
      "rigidity, cold logic, misused power"
    ],
    "tr": [
      "netlik, otorite, ilke",
      "katılık, soğuk mantık, gücü kötüye kullanma"
    ],
    "de": [
      "Klarheit, Autorität, Prinzip",
      "Starrheit, kalte Logik, Machtmissbrauch"
    ],
    "es": [
      "claridad, autoridad, principios",
      "rigidez, lógica fría, abuso de poder"
    ],
    "pt": [
      "clareza, autoridade, princípio",
      "rigidez, lógica fria, abuso de poder"
    ]
  },
  "Ace of Pentacles": {
    "en": [
      "opportunity, resource, foundation",
      "missed opening, insecurity, delay"
    ],
    "tr": [
      "fırsat, kaynak, temel",
      "kaçan fırsat, güvensizlik, gecikme"
    ],
    "de": [
      "Gelegenheit, Ressource, Grundlage",
      "verpasste Chance, Unsicherheit, Verzögerung"
    ],
    "es": [
      "oportunidad, recursos, base",
      "oportunidad perdida, inseguridad, demora"
    ],
    "pt": [
      "oportunidade, recurso, base",
      "oportunidade perdida, insegurança, atraso"
    ]
  },
  "Two of Pentacles": {
    "en": [
      "balance, adjustment, juggling",
      "overcommitment, dropped balance, strain"
    ],
    "tr": [
      "denge, uyum, idare etme",
      "aşırı söz verme, dengesizlik, zorlanma"
    ],
    "de": [
      "Balance, Anpassung, Koordination",
      "Überverpflichtung, Ungleichgewicht, Anspannung"
    ],
    "es": [
      "equilibrio, adaptación, coordinación",
      "exceso de compromisos, desequilibrio, tensión"
    ],
    "pt": [
      "equilíbrio, adaptação, coordenação",
      "compromissos excessivos, desequilíbrio, tensão"
    ]
  },
  "Three of Pentacles": {
    "en": [
      "collaboration, craft, recognition",
      "misalignment, unclear roles, solo strain"
    ],
    "tr": [
      "işbirliği, beceri, takdir",
      "uyumsuzluk, belirsiz roller, yalnız yüklenme"
    ],
    "de": [
      "Zusammenarbeit, Können, Anerkennung",
      "Fehlabstimmung, unklare Rollen, einsame Last"
    ],
    "es": [
      "colaboración, oficio, reconocimiento",
      "desajuste, roles confusos, carga individual"
    ],
    "pt": [
      "colaboração, habilidade, reconhecimento",
      "desalinhamento, papéis confusos, carga individual"
    ]
  },
  "Four of Pentacles": {
    "en": [
      "security, caution, retention",
      "loosening grip, scarcity fear, release"
    ],
    "tr": [
      "güvenlik, temkin, koruma",
      "gevşetme, kıtlık korkusu, bırakma"
    ],
    "de": [
      "Sicherheit, Vorsicht, Bewahren",
      "Lockerung, Mangelangst, Loslassen"
    ],
    "es": [
      "seguridad, cautela, conservación",
      "aflojar el control, miedo a la escasez, soltar"
    ],
    "pt": [
      "segurança, cautela, conservação",
      "soltar o controlo, medo da escassez, desapego"
    ]
  },
  "Five of Pentacles": {
    "en": [
      "lack, exclusion, hard season",
      "recovery, help offered, pride"
    ],
    "tr": [
      "eksiklik, dışlanma, zor dönem",
      "toparlanma, sunulan yardım, gurur"
    ],
    "de": [
      "Mangel, Ausgrenzung, schwierige Zeit",
      "Erholung, angebotene Hilfe, Stolz"
    ],
    "es": [
      "carencia, exclusión, dificultad",
      "recuperación, ayuda ofrecida, orgullo"
    ],
    "pt": [
      "falta, exclusão, fase difícil",
      "recuperação, ajuda oferecida, orgulho"
    ]
  },
  "Six of Pentacles": {
    "en": [
      "giving, receiving, fair terms",
      "unequal exchange, strings attached, dependency"
    ],
    "tr": [
      "verme, alma, adil koşullar",
      "eşitsizlik, koşullu yardım, bağımlılık"
    ],
    "de": [
      "Geben, Empfangen, faire Bedingungen",
      "ungleicher Tausch, bedingte Hilfe, Abhängigkeit"
    ],
    "es": [
      "dar, recibir, condiciones justas",
      "intercambio desigual, ayuda condicionada, dependencia"
    ],
    "pt": [
      "dar, receber, condições justas",
      "troca desigual, ajuda condicionada, dependência"
    ]
  },
  "Seven of Pentacles": {
    "en": [
      "patience, assessment, long game",
      "impatience, wasted effort, poor return"
    ],
    "tr": [
      "sabır, değerlendirme, uzun vade",
      "sabırsızlık, boşa çaba, zayıf karşılık"
    ],
    "de": [
      "Geduld, Bewertung, Langfristigkeit",
      "Ungeduld, vergebliche Mühe, geringer Ertrag"
    ],
    "es": [
      "paciencia, evaluación, largo plazo",
      "impaciencia, esfuerzo desperdiciado, poco retorno"
    ],
    "pt": [
      "paciência, avaliação, longo prazo",
      "impaciência, esforço desperdiçado, pouco retorno"
    ]
  },
  "Eight of Pentacles": {
    "en": [
      "diligence, skill, repetition",
      "distraction, uninspired grind, cut corners"
    ],
    "tr": [
      "özen, beceri, tekrar",
      "dikkat dağılması, isteksizlik, kestirme yol"
    ],
    "de": [
      "Sorgfalt, Fähigkeit, Wiederholung",
      "Ablenkung, lustlose Routine, Abkürzung"
    ],
    "es": [
      "dedicación, habilidad, repetición",
      "distracción, rutina sin motivación, atajos"
    ],
    "pt": [
      "dedicação, habilidade, repetição",
      "distração, rotina sem motivação, atalhos"
    ]
  },
  "Nine of Pentacles": {
    "en": [
      "independence, comfort, earned ease",
      "over-reliance, hollow comfort, restlessness"
    ],
    "tr": [
      "bağımsızlık, konfor, hak edilmiş rahatlık",
      "aşırı dayanma, boş konfor, huzursuzluk"
    ],
    "de": [
      "Unabhängigkeit, Komfort, verdiente Ruhe",
      "übermäßiges Anlehnen, leerer Komfort, Unruhe"
    ],
    "es": [
      "independencia, comodidad, bienestar ganado",
      "dependencia excesiva, comodidad vacía, inquietud"
    ],
    "pt": [
      "independência, conforto, tranquilidade conquistada",
      "dependência excessiva, conforto vazio, inquietação"
    ]
  },
  "Ten of Pentacles": {
    "en": [
      "legacy, stability, family systems",
      "instability, inherited pattern, short view"
    ],
    "tr": [
      "miras, istikrar, aile düzeni",
      "istikrarsızlık, aktarılan alışkanlık, kısa vade"
    ],
    "de": [
      "Vermächtnis, Stabilität, Familiensystem",
      "Instabilität, geerbtes Muster, kurze Sicht"
    ],
    "es": [
      "legado, estabilidad, sistemas familiares",
      "inestabilidad, patrones heredados, visión corta"
    ],
    "pt": [
      "legado, estabilidade, sistemas familiares",
      "instabilidade, padrões herdados, visão curta"
    ]
  },
  "Page of Pentacles": {
    "en": [
      "learning, diligence, first step",
      "distraction, procrastination, unrealistic plan"
    ],
    "tr": [
      "öğrenme, özen, ilk adım",
      "dağınıklık, erteleme, gerçekçi olmayan plan"
    ],
    "de": [
      "Lernen, Sorgfalt, erster Schritt",
      "Ablenkung, Aufschub, unrealistischer Plan"
    ],
    "es": [
      "aprendizaje, dedicación, primer paso",
      "distracción, aplazamiento, plan irreal"
    ],
    "pt": [
      "aprendizagem, dedicação, primeiro passo",
      "distração, adiamento, plano irreal"
    ]
  },
  "Knight of Pentacles": {
    "en": [
      "consistency, method, reliability",
      "stagnation, over-caution, dull repetition"
    ],
    "tr": [
      "tutarlılık, yöntem, güvenilirlik",
      "durgunluk, aşırı temkin, tekdüzelik"
    ],
    "de": [
      "Beständigkeit, Methode, Verlässlichkeit",
      "Stillstand, übermäßige Vorsicht, Eintönigkeit"
    ],
    "es": [
      "constancia, método, fiabilidad",
      "estancamiento, cautela excesiva, monotonía"
    ],
    "pt": [
      "constância, método, fiabilidade",
      "estagnação, cautela excessiva, monotonia"
    ]
  },
  "Queen of Pentacles": {
    "en": [
      "resourcefulness, warmth, competence",
      "over-extension, self-neglect, smothering"
    ],
    "tr": [
      "beceriklilik, sıcaklık, yetkinlik",
      "aşırı yüklenme, kendini ihmal, aşırı koruma"
    ],
    "de": [
      "Einfallsreichtum, Wärme, Kompetenz",
      "Überlastung, Selbstvernachlässigung, Überbehütung"
    ],
    "es": [
      "ingenio, calidez, competencia",
      "sobrecarga, descuido propio, sobreprotección"
    ],
    "pt": [
      "criatividade prática, acolhimento, competência",
      "sobrecarga, descuido próprio, proteção excessiva"
    ]
  },
  "King of Pentacles": {
    "en": [
      "stability, mastery, provision",
      "control through money, rigidity, stagnation"
    ],
    "tr": [
      "istikrar, ustalık, destek",
      "parayla kontrol, katılık, durgunluk"
    ],
    "de": [
      "Stabilität, Können, Versorgung",
      "Kontrolle durch Geld, Starrheit, Stillstand"
    ],
    "es": [
      "estabilidad, dominio, provisión",
      "control económico, rigidez, estancamiento"
    ],
    "pt": [
      "estabilidade, domínio, provisão",
      "controlo pelo dinheiro, rigidez, estagnação"
    ]
  }
};

  function cardCareerCopy(card, position) {
    var major = MAJOR_COPY[card.n];
    if (major) {
      var localizedMajor = CARD_LANGUAGE[LANG] && CARD_LANGUAGE[LANG].major[card.n];
      var majorCopy = localizedMajor || major;
      return {
        theme: majorCopy[0] + (card.r ? (CARD_LANGUAGE[LANG] ? CARD_LANGUAGE[LANG].blocked : ' — blocked or redirected') : ''),
        line: careerFrame(position, card.r ? majorCopy[2] : majorCopy[1])
      };
    }
    var minor = MINOR_CARD_COPY[card.n];
    if (minor) {
      var localized = minor[LANG] || minor.en;
      var core = localized[card.r ? 1 : 0];
      return { theme: core, line: careerFrame(position, core) };
    }
    return { theme: 'Career evidence still to verify', line: careerFrame(position, 'a fact that has not yet been tested') };
  }

  function derivePattern(cards) {
    var first = cardCareerCopy(cards[0], 0);
    var second = cardCareerCopy(cards[1], 1);
    var third = cardCareerCopy(cards[2], 2);
    var language = CARD_LANGUAGE[LANG];
    function patternTheme(value) {
      value = String(value || '');
      if (LANG === 'de') return value.charAt(0).toLocaleLowerCase('de-DE') + value.slice(1);
      return value.toLocaleLowerCase(LANG === 'tr' ? 'tr-TR' : (LANG === 'pt' ? 'pt-BR' : (LANG === 'es' ? 'es-ES' : 'en-US')));
    }
    var firstName = localizedCardName(cards[0].n) + (cards[0].r ? (LANG === 'tr' ? ' ters' : LANG === 'es' ? ' invertida' : LANG === 'de' ? ' umgekehrt' : LANG === 'pt' ? ' invertida' : ' reversed') : '');
    var secondName = localizedCardName(cards[1].n) + (cards[1].r ? (LANG === 'tr' ? ' ters' : LANG === 'es' ? ' invertida' : LANG === 'de' ? ' umgekehrt' : LANG === 'pt' ? ' invertida' : ' reversed') : '');
    if (language) {
      return {
        tension: language.pattern[0].replace('{a}', firstName).replace('{at}', patternTheme(first.theme)).replace('{b}', secondName).replace('{bt}', patternTheme(second.theme)),
        hidden: second.line,
        move: third.line,
        bridge: language.pattern[1].replace('{at}', patternTheme(first.theme)).replace('{bt}', patternTheme(second.theme))
      };
    }
    return { tension: firstName + ' brings ' + first.theme.toLowerCase() + ', while ' + secondName + ' introduces ' + second.theme.toLowerCase() + '. The tension belongs to this exact pair, not only to the situation you selected.', hidden: second.line, move: third.line, bridge: 'This draw connects ' + first.theme.toLowerCase() + ' with ' + second.theme.toLowerCase() + '. The exact condition that should decide your answer, and the evidence that would confirm it, remain open for the personalized reading.' };
  }

  var POOL = [
    'The Fool', 'The Magician', 'The High Priestess', 'The Empress', 'The Emperor', 'The Hierophant', 'The Lovers', 'The Chariot', 'Strength', 'The Hermit', 'Wheel of Fortune', 'Justice', 'The Hanged Man', 'Death', 'Temperance', 'The Devil', 'The Tower', 'The Star', 'The Moon', 'The Sun', 'Judgement', 'The World',
    'Ace of Wands', 'Two of Wands', 'Three of Wands', 'Four of Wands', 'Five of Wands', 'Six of Wands', 'Seven of Wands', 'Eight of Wands', 'Nine of Wands', 'Ten of Wands', 'Page of Wands', 'Knight of Wands', 'Queen of Wands', 'King of Wands',
    'Ace of Cups', 'Two of Cups', 'Three of Cups', 'Four of Cups', 'Five of Cups', 'Six of Cups', 'Seven of Cups', 'Eight of Cups', 'Nine of Cups', 'Ten of Cups', 'Page of Cups', 'Knight of Cups', 'Queen of Cups', 'King of Cups',
    'Ace of Swords', 'Two of Swords', 'Three of Swords', 'Four of Swords', 'Five of Swords', 'Six of Swords', 'Seven of Swords', 'Eight of Swords', 'Nine of Swords', 'Ten of Swords', 'Page of Swords', 'Knight of Swords', 'Queen of Swords', 'King of Swords',
    'Ace of Pentacles', 'Two of Pentacles', 'Three of Pentacles', 'Four of Pentacles', 'Five of Pentacles', 'Six of Pentacles', 'Seven of Pentacles', 'Eight of Pentacles', 'Nine of Pentacles', 'Ten of Pentacles', 'Page of Pentacles', 'Knight of Pentacles', 'Queen of Pentacles', 'King of Pentacles'
  ];

  /* ------------ state ------------ */
  var s = {
    sit: 'other', step: 3, detail: '', q: '', tf: 'No fixed date', err: '',
    phase: 'form', anim: false, cards: null, pkg: null, packageChosen: false, drawer: false,
    readingId: '', previewToken: '', transportFallback: false, transportFailure: '',
    createdAt: 0, authorityKey: '', authorityRefreshRequired: false,
    checkoutMessage: '', checkoutMessageError: false,
    insight: '', insightLocale: '', previewMode: '', previewSource: '', fetching: false, buying: false,
    attemptId: '', sentEvents: {}, pricing: null, pricingSignature: '', pricingRetry: false,
    checkoutQueued: false, checkoutVia: '',
    stickyEligible: false, packageVisible: false, footerVisible: false, keyboardOpen: false
  };
  var animTimer = 0;
  var returnAnchor = '';
  var cardArt = null;
  var sessionId = sessionIdentifier();
  var drawerWasOpen = false;
  var drawerTrigger = null;
  var resultViewObserver = null;
  var offerViewObserver = null;
  var stickyObserver = null;
  var packageObserver = null;
  var footerObserver = null;
  var engagementTimer = 0;
  var engagedVisibleMs = 0;
  var engagedLastTick = 0;
  var railReadingId = '';
  var railSyncSignature = '';

  function newId(prefix) {
    try { if (crypto && typeof crypto.randomUUID === 'function') return prefix + '_' + crypto.randomUUID(); } catch (_) {}
    return prefix + '_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
  }
  function safeCode(value, fallback) {
    var code = String(value || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 80);
    return code || fallback || 'unknown';
  }
  function sessionIdentifier() {
    try {
      var existing = sessionStorage.getItem(SESSION_KEY);
      if (existing && /^[a-zA-Z0-9_-]{16,96}$/.test(existing)) return existing;
      var next = newId('cs');
      sessionStorage.setItem(SESSION_KEY, next);
      return next;
    } catch (_) { return newId('cs'); }
  }
  function ensureAttempt() {
    if (!s.attemptId) s.attemptId = newId('ca');
    return s.attemptId;
  }
  function analyticsSuppressed() {
    try {
      var query = new URLSearchParams(window.location.search || '');
      return query.has('funnel_audit') || query.get('internal') === 'true' || query.get('traffic_type') === 'internal' || query.has('deckaura_internal') || query.has('codex_audit') || query.has('alltools_audit');
    } catch (_) { return false; }
  }
  function publishFunnelEvent(name, data, options) {
    if (analyticsSuppressed()) return false;
    options = options || {};
    if (options.ensureAttempt !== false) ensureAttempt();
    var onceKey = options.onceKey || (options.once ? name : '');
    if (onceKey && s.sentEvents[onceKey]) return false;
    if (onceKey) s.sentEvents[onceKey] = true;
    var payload = Object.assign({
      analytics_schema: ANALYTICS_SCHEMA,
      funnel_version: FUNNEL_VERSION,
      tool: 'career_tarot',
      page_variant: VARIANT,
      session_id: sessionId,
      attempt_id: s.attemptId || '',
      reading_id: s.readingId || '',
      event_uuid: newId('ce')
    }, data || {});
    delete payload.question;
    delete payload.context;
    delete payload.detail;
    delete payload.email;
    delete payload.name;
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (_) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (_) {}
    try { document.dispatchEvent(new CustomEvent('deckaura:funnel-event', { detail: { name: name, data: payload } })); } catch (_) {}
    if (onceKey && typeof persistState === 'function') persistState('');
    return true;
  }
  function firstInteraction(source) {
    ensureAttempt();
    publishFunnelEvent('first_interaction', { source: source || 'unknown' }, { once: true });
  }
  function visitorId() {
    var key = 'deckaura_visitor_v1';
    try {
      var existing = localStorage.getItem(key);
      if (existing && /^[a-zA-Z0-9_-]{16,96}$/.test(existing)) return existing;
      var bytes = new Uint8Array(18); crypto.getRandomValues(bytes);
      var next = 'v_' + Array.prototype.map.call(bytes, function (x) { return x.toString(16).padStart(2, '0'); }).join('');
      localStorage.setItem(key, next);
      return next;
    } catch (_) { return 'v_' + Date.now().toString(16) + Math.random().toString(36).slice(2, 10); }
  }
  function sitOf() { for (var i = 0; i < SITS.length; i++) if (SITS[i].id === s.sit) return SITS[i]; return null; }
  function packOf() { for (var i = 0; i < PACKS.length; i++) if (PACKS[i].id === s.pkg) return PACKS[i]; return null; }
  function packName(p) {
    var sit = sitOf();
    var situationalName = sit && Number.isInteger(p && p.sitIndex) ? sit.packs[p.sitIndex] : '';
    return uiText(LANG === 'en' && situationalName ? situationalName : p.base);
  }
  function pricingTier(pack) {
    if (!pack || !s.pricing || !Array.isArray(s.pricing.tiers)) return null;
    for (var i = 0; i < s.pricing.tiers.length; i++) if (s.pricing.tiers[i].tier === pack.tier) return s.pricing.tiers[i];
    return null;
  }
  function priceReady(pack) {
    var tier = pricingTier(pack);
    return !!(s.pricing && s.pricing.status === 'ready' && tier && tier.available && tier.variant_id && tier.formatted_price);
  }
  function priceLabel(pack) {
    var tier = pricingTier(pack);
    return priceReady(pack) ? String(tier.formatted_price) : '';
  }
  function normalizedPricingSnapshot(snapshot) {
    var sourceStatus = snapshot && /^(?:loading|ready|fallback_ready|error)$/.test(String(snapshot.status || '')) ? String(snapshot.status) : 'error';
    var status = sourceStatus === 'fallback_ready' ? 'ready' : sourceStatus;
    var tiers = snapshot && Array.isArray(snapshot.tiers) ? snapshot.tiers.map(function (tier) {
      return {
        tier: RAIL_TIERS.indexOf(String(tier && tier.tier || '')) >= 0 ? String(tier.tier) : '',
        variant_id: String(tier && tier.variant_id || ''),
        price_minor: Number.isInteger(Number(tier && tier.price_minor)) ? Number(tier.price_minor) : null,
        formatted_price: String(tier && tier.formatted_price || '').slice(0, 40),
        available: !!(tier && tier.available)
      };
    }).filter(function (tier) { return !!tier.tier; }) : [];
    return {
      status: status,
      source_status: sourceStatus,
      currency: /^[A-Z]{3}$/.test(String(snapshot && snapshot.currency || '')) ? String(snapshot.currency) : '',
      latency_ms: Math.max(0, Number(snapshot && snapshot.latency_ms) || 0),
      error_code: String(snapshot && snapshot.error_code || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_').slice(0, 80),
      tiers: tiers
    };
  }
  function handlePricingSnapshot(snapshot) {
    var next = normalizedPricingSnapshot(snapshot);
    var signature = JSON.stringify({ status: next.status, source_status: next.source_status, currency: next.currency, error_code: next.error_code, tiers: next.tiers });
    if (signature === s.pricingSignature) return;
    s.pricingSignature = signature;
    s.pricing = next;
    if (next.status === 'loading') {
      publishFunnelEvent('price_check_started', { currency: next.currency || '', retry: s.pricingRetry ? 1 : 0 });
    } else if (next.status === 'ready') {
      publishFunnelEvent('price_check_succeeded', { status: next.source_status, currency: next.currency || '', latency_ms: Math.round(next.latency_ms) });
      s.pricingRetry = false;
      if (s.checkoutQueued) setTimeout(continueQueuedCheckout, 0);
    } else {
      publishFunnelEvent('price_check_failed', { error_code: next.error_code || 'price_unavailable', latency_ms: Math.round(next.latency_ms) });
      if (s.checkoutQueued) resetCheckout('failed', true);
    }
    render();
  }
  function openQuestion() {
    var sit = sitOf();
    var prompts = {
      job: 'Which hiring signal is strongest, what concern could still change the outcome, and what should I verify before committing?',
      stay: 'What would have to become concrete for staying to make sense, what weakens that case, and what should I verify before deciding?',
      offers: 'Which path has stronger real-world evidence, what condition could reverse that lean, and what should I verify before committing?',
      promotion: 'How strong is the case for asking now, what hidden constraint could change the timing, and what should I prepare first?',
      change: 'Is this new direction a durable fit or an escape, what evidence could change that view, and what should I test first?',
      conflict: 'What is driving this workplace dynamic, what boundary or escalation point matters, and what should I document or verify next?',
      business: 'What evidence supports this idea, what risk could invalidate it, and what is the smallest useful test to run next?',
      other: 'What is the deciding condition in this career question, what could change the direction, and what should I verify next?'
    };
    var fallback = prompts[sit ? sit.id : 'other'];
    if (!validCards(s.cards) || !s.q.trim()) return fallback;
    var question = s.q.trim().replace(/\s+/g, ' ').slice(0, 140);
    var hiddenCard = localizedCardName(s.cards[1].n) + (s.cards[1].r ? ' ' + uiText('(Reversed)') : '');
    var moveCard = localizedCardName(s.cards[2].n) + (s.cards[2].r ? ' ' + uiText('(Reversed)') : '');
    if (LANG === 'tr') return '“' + question + '” sorusunda, ' + hiddenCard + ' kartının gösterdiği gizli etkenin yönü gerçekten değiştirdiğini ve ' + moveCard + ' kartındaki sonraki adıma geçmenin doğru olduğunu hangi gözlemlenebilir kanıt doğrular?';
    if (LANG === 'es') return 'En «' + question + '», ¿qué prueba observable confirmaría que el factor oculto de ' + hiddenCard + ' realmente cambia la dirección y justifica el siguiente paso de ' + moveCard + '?';
    if (LANG === 'de') return 'Welche beobachtbare Tatsache würde bei „' + question + '“ bestätigen, dass der verborgene Faktor von ' + hiddenCard + ' die Richtung wirklich ändert und den nächsten Schritt von ' + moveCard + ' rechtfertigt?';
    if (LANG === 'pt') return 'Em «' + question + '», que prova observável confirmaria que o fator oculto de ' + hiddenCard + ' realmente muda a direção e justifica o próximo passo de ' + moveCard + '?';
    return 'For “' + question + ',” what observable evidence would confirm that the hidden factor in ' + hiddenCard + ' truly changes the direction and supports the next step in ' + moveCard + '?';
  }
  function inferSituation() {
    var text = String(s.q + ' ' + s.detail).toLowerCase();
    if (/(two offers?|compare (?:two )?(?:roles?|paths?)|which (?:offer|role|path)|iki teklif|zwei angebote|dos ofertas|duas ofertas)/.test(text)) return 'offers';
    if (/(promotion|raise|salary increase|terfi|zam|beförderung|gehaltserhöhung|ascenso|aumento|promoção)/.test(text)) return 'promotion';
    if (/(stay or leave|quit|resign|leave (?:my|the) job|kalmak|ayrılmak|istifa|kündigen|bleiben|renunciar|deixar o emprego)/.test(text)) return 'stay';
    if (/(career change|change careers?|new career|switch fields?|kariyer değiş|alan değiş|berufswechsel|cambiar de carrera|mudar de carreira)/.test(text)) return 'change';
    if (/(conflict|colleague|coworker|boss|manager|iş ?yeri çatış|iş arkadaş|patron|kollege|vorgesetzt|conflicto|colega|chefe)/.test(text)) return 'conflict';
    if (/(business|side hustle|freelance|startup|girişim|kendi iş|ek gelir|nebenerwerb|negocio|empreendimento)/.test(text)) return 'business';
    if (/(application|interview|recruiter|hiring|new job|iş başvuru|mülakat|işe alın|bewerbung|vorstellungsgespräch|solicitud|entrevista|candidatura)/.test(text)) return 'job';
    return 'other';
  }
  function detailLens() {
    var sit = sitOf() || SITS[7];
    var text = norm(s.detail + ' ' + s.q);
    var promised = /(promise|promised|verbal|not given|no date|without a date|not written|unwritten)/.test(text);
    var pay = /(pay|salary|compensation|money|higher-paying|higher paying|raise|bonus)/.test(text);
    var autonomy = /(autonomy|control|authority|flexib|remote|scope)/.test(text);
    var offer = /(offer|proposal|contract|terms|package)/.test(text);
    var manager = /(manager|boss|supervisor|leadership|director|decision maker|decision-maker)/.test(text);
    var deadline = /(deadline|date|timeline|notice period|start date|decision date|by friday|by monday|this week|next week)/.test(text);
    var interview = /(interview|application|recruiter|hiring|candidate|assessment)/.test(text);
    if (sit.id === 'offers') {
      var factors = [];
      if (pay) factors.push('guaranteed compensation');
      if (autonomy) factors.push('decision authority and day-to-day autonomy');
      factors.push('written scope, manager, growth criteria and reversibility');
      return (promised || offer ? 'Do not compare signed terms with an unwritten possibility as if both were equally certain. Treat every verbal promise as upside until its title, pay, owner and date are in writing. ' : 'Compare both paths on the same verified facts, not on hope or prestige alone. ') + 'Put ' + factors.join(', ') + (deadline ? ', including the decision deadline' : '') + ' in one table. The free layer can name that fair comparison; the final lean and the condition that could reverse it remain in the personalized reading.';
    }
    if (sit.id === 'stay') return (promised ? 'A promise is not yet a changed job. Separate what has an owner, date and written commitment from what has only been discussed. ' : '') + (manager ? 'Name who can actually approve the change, not only who supports it. ' : '') + 'Compare staying and leaving on growth, control, compensation, workload and reversibility using the same evidence. The exact threshold for choosing a path remains open.';
    if (sit.id === 'job') return 'Separate what the employer has confirmed from what you inferred. ' + (interview ? 'Use the interview feedback, named concern and next promised contact as evidence. ' : '') + (offer || pay ? 'Compare the written role scope, compensation and reporting line, not only the headline offer. ' : '') + (deadline ? 'Confirm the decision date and what happens if it slips. ' : '') + 'The deciding signal and how much weight to give it remain open.';
    if (sit.id === 'promotion') return 'Your evidence is the work already delivered, who can verify it, the published criteria and available budget. ' + (manager ? 'Separate your manager’s support from their authority to approve title or pay. ' : '') + (deadline ? 'Ask for the next decision date and owner. ' : '') + 'The strongest ask and timing remain open.';
    if (sit.id === 'conflict') return 'Look for repeated behaviour, specific examples, the response to one clear boundary and the formal route available if it continues. Those observable facts matter more than guessing another person’s motives; the safest sequence remains open.';
    if (sit.id === 'change') return 'Compare the new path with the current one through a small test: real demand, transferable skills, learning cost, income runway and how you feel after doing the work rather than imagining it. The stronger route remains open.';
    if (sit.id === 'business') return 'Count paid interest, delivery capacity, margin and repeat demand—not compliments—as evidence. A small offer tested with one real customer is more useful than a large launch. The risk threshold and 30-day sequence remain open.';
    return 'Separate the facts you can verify from the outcome you hope for. Name one criterion that both options must meet and one piece of evidence still missing. The final recommendation and its reversing condition remain open.';
  }
  function cardSlug(name) { return String(name || '').toLowerCase().replace(/^the\s+/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function cardImg(name, width) {
    width = Number(width) > 0 ? Math.round(Number(width)) : 560;
    if (window.DeckauraTarotArt && typeof window.DeckauraTarotArt.url === 'function') {
      var sharedUrl = window.DeckauraTarotArt.url(name, width);
      if (sharedUrl) return sharedUrl;
    }
    var seed = document.querySelector('#top img[src*="dkc-"]') || document.querySelector('[data-cr2-result] img[src*="dkc-"]');
    if (!seed) return '';
    try {
      var url = new URL(seed.getAttribute('src') || seed.src, window.location.href);
      url.pathname = url.pathname.replace(/dkc-[^/]+\.webp$/i, 'dkc-' + cardSlug(name) + '.webp');
      url.search = '';
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) { return ''; }
  }
  function norm(v) { return String(v || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim(); }
  function questionKey(value) {
    var key = String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    try { return key.normalize('NFKC'); } catch (_) { return key; }
  }
  function authorityContext() {
    var dl = window.DeckauraLocale || {};
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    return {
      locale: String(dl.locale || document.documentElement.lang || 'en').toLowerCase(),
      country: String(dl.country || 'US').toUpperCase(),
      currency: String(dl.currency || currency || 'USD').toUpperCase(),
      market: String(dl.market || '').toLowerCase()
    };
  }
  function authorityKey() { return JSON.stringify(authorityContext()); }
  function readingIdentity() { return JSON.stringify([s.readingId, s.q, s.detail, s.tf, s.sit, s.cards, authorityKey()]); }
  function invalidateReadingRequest() {
    freeRequestSequence += 1;
    var request = activeFreeRequest;
    activeFreeRequest = null;
    s.fetching = false;
    if (request && request.controller) { try { request.controller.abort(); } catch (_) {} }
  }
  function readingHistory() {
    var rows = [];
    try { rows = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch (_) {}
    if (!Array.isArray(rows)) return [];
    return rows.filter(function (row) { return row && row.v === 2 && row.phase === 'result' && validCards(row.cards) && row.savedAt && Date.now() - Number(row.savedAt) < 864e5 && typeof row.q === 'string' && row.q.length <= 200; });
  }
  function rememberReading(payload) {
    if (!payload || payload.phase !== 'result' || !validCards(payload.cards)) return;
    var rows = readingHistory().filter(function (row) { return questionKey(row.q) !== questionKey(payload.q); });
    rows.push(payload);
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(rows)); } catch (_) {}
  }
  function matchingReading() {
    var rows = readingHistory(), previous = previousState();
    if (previous) rows.push(previous);
    for (var i = rows.length - 1; i >= 0; i--) if (questionKey(rows[i].q) === questionKey(s.q)) return rows[i];
    return null;
  }

  function validCards(value) {
    return Array.isArray(value) && value.length === 3 && value.every(function (c) { return c && POOL.indexOf(c.n) >= 0 && typeof c.r === 'boolean'; });
  }
  function storedState() {
    var raw = null;
    try { raw = JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (_) { raw = null; }
    if (!raw) { try { raw = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (_) { raw = null; } }
    if (!raw || raw.v !== 2 || !raw.savedAt || Date.now() - Number(raw.savedAt) >= 864e5) return null;
    return raw;
  }
  function statePayload() {
    return {
      v: 2, savedAt: s.phase === 'result' && s.createdAt ? s.createdAt : Date.now(), sit: s.sit, step: s.step, detail: s.detail, q: s.q, tf: s.tf,
      phase: s.phase, cards: validCards(s.cards) ? s.cards : null, pkg: s.pkg, packageChosen: s.packageChosen,
      readingId: s.readingId, previewToken: s.previewToken,
      authorityKey: s.authorityKey, authorityRefreshRequired: s.authorityRefreshRequired,
      transportFallback: s.transportFallback, transportFailure: s.transportFailure,
      insight: s.insight, insightLocale: s.insightLocale, previewMode: s.previewMode, previewSource: s.previewSource, attemptId: s.attemptId, sentEvents: s.sentEvents,
      seed: norm(s.sit + ' ' + s.q), returnAnchor: returnAnchor
    };
  }
  function persistState(anchor) {
    if (typeof anchor === 'string') returnAnchor = anchor;
    var payload = statePayload();
    try { sessionStorage.setItem(KEY, JSON.stringify(payload)); } catch (_) {}
    if (payload.phase === 'result' && payload.cards) { try { localStorage.setItem(KEY, JSON.stringify(payload)); } catch (_) {} rememberReading(payload); }
  }
  function previousState() {
    var saved = null;
    try { saved = JSON.parse(localStorage.getItem(PREVIOUS_KEY) || 'null'); } catch (_) { saved = null; }
    if (!saved || saved.v !== 2 || saved.phase !== 'result' || !validCards(saved.cards) || !saved.savedAt || Date.now() - Number(saved.savedAt) >= 864e5) return null;
    return saved;
  }
  function restoreState() {
    var saved = storedState();
    if (!saved) return false;
    s.sit = saved.sit && SITS.some(function (x) { return x.id === saved.sit; }) ? saved.sit : 'other';
    /* Saved pre-result sessions from the retired three-gate flow resume at the
       single question composer instead of reopening a mandatory old step. */
    s.step = 3;
    s.detail = String(saved.detail || '').slice(0, 320);
    s.q = String(saved.q || '').slice(0, 200);
    s.attemptId = /^[a-zA-Z0-9_-]{16,96}$/.test(String(saved.attemptId || '')) ? String(saved.attemptId) : '';
    s.sentEvents = saved.sentEvents && typeof saved.sentEvents === 'object' ? saved.sentEvents : {};
    s.tf = TFS.indexOf(saved.tf) >= 0 ? saved.tf : 'No fixed date';
    if (saved.phase === 'result' && validCards(saved.cards) && meaningfulInput(s.q, 8, 1).valid) {
      s.phase = 'result'; s.cards = saved.cards; s.readingId = String(saved.readingId || '');
      s.createdAt = Number(saved.savedAt);
      var savedToken = String(saved.previewToken || '');
      s.previewToken = /^[a-f0-9]{32}$/i.test(savedToken) ? savedToken : '';
      s.authorityKey = String(saved.authorityKey || '');
      s.authorityRefreshRequired = !!s.previewToken && (saved.authorityRefreshRequired === true || s.authorityKey !== authorityKey());
      s.insight = String(saved.insight || '');
      s.insightLocale = String(saved.insightLocale || '').toLowerCase();
      if (!s.insightLocale && s.authorityKey) { try { s.insightLocale = JSON.parse(s.authorityKey).locale || ''; } catch (_) {} }
      s.previewMode = /^(?:model|quota_fallback|quality_fallback|transport_fallback|unavailable)$/.test(String(saved.previewMode || '')) ? String(saved.previewMode) : '';
      s.previewSource = safeCode(saved.previewSource, '').slice(0, 80);
      s.transportFailure = /^(?:timeout|http_408|http_429|http_5xx)$/.test(String(saved.transportFailure || '')) ? String(saved.transportFailure) : '';
      s.transportFallback = !s.previewToken && saved.transportFallback === true && !!s.transportFailure;
      s.packageChosen = saved.packageChosen === true && PACKS.some(function (p) { return p.id === saved.pkg; });
      s.pkg = s.packageChosen ? saved.pkg : null;
      ensureAttempt();
    }
    returnAnchor = String(saved.returnAnchor || '');
    return s.phase === 'result';
  }

  /* One free result is preserved for 24 hours; returning visitors see the same
     exact question, cards and package instead of drawing until they like one. */
  function pickCards() {
    var seed = norm(s.sit + ' ' + s.q);
    var previous = matchingReading();
    if (previous) {
      s.createdAt = Number(previous.savedAt);
      var sameContext = previous.detail === s.detail && previous.tf === s.tf && previous.sit === s.sit;
      s.readingId = sameContext ? String(previous.readingId || '') : newId('cr2');
      if (sameContext && /^[a-f0-9]{32}$/i.test(String(previous.previewToken || ''))) {
        s.previewToken = previous.previewToken;
        s.authorityKey = String(previous.authorityKey || '');
        s.authorityRefreshRequired = true;
        s.insight = String(previous.insight || '');
        s.insightLocale = String(previous.insightLocale || '').toLowerCase();
        if (!s.insightLocale && s.authorityKey) { try { s.insightLocale = JSON.parse(s.authorityKey).locale || ''; } catch (_) {} }
        s.previewMode = String(previous.previewMode || '');
        s.previewSource = String(previous.previewSource || '');
      }
      return previous.cards.map(function (card) { return { n: card.n, r: card.r }; });
    }
    var now = Date.now();
    var h = 7;
    for (var i = 0; i < seed.length; i++) h = (h * 33 + seed.charCodeAt(i)) % 1000003;
    var extra = 0;
    try { var b = new Uint8Array(2); crypto.getRandomValues(b); extra = b[0] * 256 + b[1]; } catch (_) { extra = Math.floor(Math.random() * 65536); }
    var x = (h + extra) % 1000003;
    var cards = [];
    var used = {};
    while (cards.length < 3) {
      x = (x * 31 + 17) % 1000003;
      var idx = x % POOL.length;
      if (used[idx]) continue;
      used[idx] = 1;
      cards.push({ n: POOL[idx], r: (x % 100) < 38 });
    }
    s.readingId = 'cr2-' + now.toString(36) + Math.random().toString(36).slice(2, 7);
    s.createdAt = now;
    return cards;
  }
  function preloadCardImages(cards) {
    if (!Array.isArray(cards)) return;
    var width = window.matchMedia && window.matchMedia('(max-width: 600px)').matches ? 260 : 520;
    cards.forEach(function (card) {
      var url = cardImg(card.n, width);
      if (!url) return;
      var image = new Image();
      image.decoding = 'async';
      image.src = url;
    });
  }
  function signalString() {
    if (!s.cards) return '';
    /* API sinyal etiketleri backend'in kanonik CAREER_DIRECT_POSITIONS sozlesmesine sabitlenir;
       ekran etiketleri POSITIONS ile ayri kalir (2026-09-03 career contract fix). */
    var apiPositions = ['Current Position', 'Deciding Factor', 'Best Next Step'];
    return s.cards.map(function (c, i) { return apiPositions[i] + ': ' + c.n + ' ' + (c.r ? 'Reversed' : 'Upright'); }).join('; ');
  }
  function contextLine() {
    var sit = sitOf();
    var line = 'Situation: ' + (sit ? sit.phrase : 'career decision') + '.';
    var det = s.detail.trim();
    if (det) line += ' Deciding detail: "' + det.slice(0, 320) + '".';
    line += ' Timeframe: ' + s.tf + '.';
    return line.slice(0, 500);
  }

  /* ------------ render ------------ */
  function bindAll(name, value) {
    value = uiText(value);
    document.querySelectorAll('[data-cr2-bind="' + name + '"]').forEach(function (el) {
      if (el.textContent !== value) el.textContent = value;
    });
  }
  function localizedBasis(sit, timeframe) {
    var title = uiText(sit ? sit.title : 'Career decision');
    var period = uiText(timeframe);
    if (LANG === 'es' || LANG === 'pt') return title + ' · ' + uiText('horizon') + ' de ' + period;
    if (LANG === 'de') return title + ' · ' + uiText('horizon') + ': ' + period;
    return title + ' · ' + period + ' ' + uiText('horizon');
  }
  function show(sel, on, disp) {
    document.querySelectorAll(sel).forEach(function (el) { el.style.display = on ? (disp || '') : 'none'; });
  }
  function setModalInert(on) {
    document.querySelectorAll('[data-cr2-inerted]').forEach(function (el) {
      el.removeAttribute('inert');
      el.removeAttribute('data-cr2-inerted');
    });
    if (!on) return;
    var drawer = document.querySelector('[data-cr2-drawer]');
    var overlay = document.querySelector('[data-cr2-overlay]');
    if (!drawer) return;
    function protectBranch(parent) {
      Array.prototype.forEach.call(parent.children || [], function (child) {
        if (child === drawer || child === overlay) return;
        if (child.contains(drawer) || (overlay && child.contains(overlay))) protectBranch(child);
        else {
          child.setAttribute('inert', '');
          child.setAttribute('data-cr2-inerted', 'true');
        }
      });
    }
    protectBranch(document.body);
  }
  function focusableInDrawer() {
    var drawer = document.querySelector('[data-cr2-drawer]');
    if (!drawer) return [];
    return Array.prototype.filter.call(drawer.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'), function (el) {
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }
  function openDrawer(trigger, via) {
    var pack = packOf();
    if (!pack) return;
    drawerTrigger = trigger || document.activeElement;
    s.drawer = true;
    render();
    persistState('packages');
    publishFunnelEvent('offer_opened', { package_id: pack.id, package_tier: pack.tier, placement: via || 'unknown' });
  }

  function closePackageComparison() {
    var details = document.querySelector('[data-cr2-package-comparison]');
    if (details) details.open = false;
  }

  function closeDrawer(reason) {
    if (!s.drawer) return;
    var pack = packOf();
    s.drawer = false;
    render();
    persistState('packages');
    publishFunnelEvent('offer_closed', { package_id: pack ? pack.id : '', close_reason: reason || 'unknown' });
    var trigger = drawerTrigger;
    drawerTrigger = null;
    if (trigger && typeof trigger.focus === 'function') {
      try { trigger.focus({ preventScroll: true }); } catch (_) { try { trigger.focus(); } catch (_) {} }
    }
  }
  function meaningfulInput(value, minimum, minimumTokens) {
    var text = String(value || '').trim().replace(/\s+/g, ' ');
    var tokens = text.split(/\s+/).filter(Boolean);
    var compact = text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
    var unique = {};
    for (var i = 0; i < compact.length; i++) unique[compact.charAt(i)] = true;
    var remaining = Math.max(0, minimum - text.length);
    return { valid: text.length >= minimum && tokens.length >= (minimumTokens || 2) && Object.keys(unique).length >= 3, remaining: remaining, tokenCount: tokens.length };
  }
  function requirementText(value, minimum, minimumTokens) {
    var check = meaningfulInput(value, minimum, minimumTokens);
    if (check.remaining > 0) {
      if (LANG === 'tr') return check.remaining + ' karakter daha gerekli';
      if (LANG === 'es') return 'Faltan ' + check.remaining + ' caracteres';
      if (LANG === 'de') return 'Noch ' + check.remaining + ' Zeichen nötig';
      if (LANG === 'pt') return 'Faltam ' + check.remaining + ' caracteres';
      return check.remaining + ' more character' + (check.remaining === 1 ? '' : 's') + ' needed';
    }
    if (check.tokenCount < (minimumTokens || 2)) return 'Use at least two words';
    if (!check.valid) return 'Add a little more meaningful detail';
    return 'Ready';
  }
  function optionalContextRequirementText(value) {
    if (!String(value || '').trim()) {
      if (LANG === 'tr') return 'İsteğe bağlı';
      if (LANG === 'es') return 'Opcional';
      if (LANG === 'de') return 'Optional';
      if (LANG === 'pt') return 'Opcional';
      return 'Optional';
    }
    return requirementText(value, 10);
  }
  function stepReady() {
    return meaningfulInput(s.q, 8, 1).valid;
  }
  function updateNextState() {
    var nextBtn = document.querySelector('[data-cr2-act="next"]');
    if (!nextBtn) return;
    var ready = stepReady();
    nextBtn.disabled = !ready;
    nextBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
    var label = ready ? 'Reveal My Career Cards' : 'Write one focused question';
    bindAll('nextLabel', label);
  }
  function render() {
    var sit = sitOf();
    var pack = packOf();

    show('[data-cr2-step="2"]', false);
    show('[data-cr2-step="3"]', true);
    var backBtn = document.querySelector('[data-cr2-act="back"]');
    if (backBtn) backBtn.style.display = 'none';
    updateNextState();
    document.querySelectorAll('[data-cr2-stepdot]').forEach(function (el) {
      var n = Number(el.getAttribute('data-cr2-stepdot'));
      var on = s.step === n, done = s.step > n;
      el.style.color = on ? '#F7F2E8' : (done ? '#AAAAB6' : '#6E6B7A');
      var ring = el.querySelector('span');
      if (ring) {
        ring.style.borderColor = on ? '#DEC28D' : 'rgba(247,242,232,0.2)';
        ring.style.background = on ? '#DEC28D' : 'transparent';
        ring.style.color = on ? '#171B2B' : (done ? '#AAAAB6' : '#6E6B7A');
      }
    });

    var errBox = document.querySelector('[data-cr2-err]');
    if (errBox) { errBox.style.display = s.err ? 'flex' : 'none'; var et = errBox.querySelector('[data-cr2-errtext]'); if (et) et.textContent = uiText(s.err); }
    var detailField = document.querySelector('[data-cr2-detail]');
    var questionField = document.querySelector('[data-cr2-q]');
    if (detailField) detailField.setAttribute('aria-invalid', 'false');
    if (questionField) questionField.setAttribute('aria-invalid', s.err ? 'true' : 'false');

    document.querySelectorAll('[data-cr2-sit]').forEach(function (el) {
      var on = el.getAttribute('data-cr2-sit') === s.sit;
      el.setAttribute('aria-checked', on ? 'true' : 'false');
      el.setAttribute('tabindex', on || (!s.sit && el.getAttribute('data-cr2-sit') === SITS[0].id) ? '0' : '-1');
      el.style.background = on ? 'rgba(222,194,141,0.09)' : '#0F1220';
      el.style.borderColor = on ? '#DEC28D' : 'rgba(255,255,255,0.10)';
      var sel = el.querySelector('[data-cr2-selmark]');
      if (sel) sel.style.display = on ? 'flex' : 'none';
    });
    bindAll('sitTitle', sit ? sit.title : 'No situation chosen yet');
    bindAll('sitLead', LANG !== 'en' && FLOW_LANGUAGE[LANG] ? FLOW_LANGUAGE[LANG].sitLead : (sit ? sit.lead : 'Choose the situation closest to yours above and your reading will adapt to that decision.'));
    bindAll('changeSitLabel', sit ? 'Change situation' : 'Choose a situation');
    bindAll('detailExample', LANG !== 'en' && FLOW_LANGUAGE[LANG] ? FLOW_LANGUAGE[LANG].example : (sit ? sit.example : SITS[7].example));
    var detTa = document.querySelector('[data-cr2-detail]');
    if (detTa) {
      detTa.readOnly = s.phase !== 'form' || s.buying;
      if (detTa.value !== s.detail) detTa.value = s.detail;
      if (sit) detTa.setAttribute('placeholder', LANG !== 'en' && FLOW_LANGUAGE[LANG] ? FLOW_LANGUAGE[LANG].example : uiText(sit.example));
    }
    var qTa = document.querySelector('[data-cr2-q]');
    if (qTa) { qTa.readOnly = s.phase !== 'form' || s.buying; if (qTa.value !== s.q) qTa.value = s.q; }
    bindAll('detailCount', s.detail.length + ' / 320');
    bindAll('qCount', s.q.length + ' / 200');
    document.querySelectorAll('[data-cr2-review-label]').forEach(function (node) { node.textContent = uiText(node.getAttribute('data-cr2-review-label')); });
    document.querySelectorAll('[data-cr2-reading-question]').forEach(function (node) { node.textContent = s.q; });
    document.querySelectorAll('[data-cr2-reading-context]').forEach(function (node) { node.textContent = s.detail; });
    document.querySelectorAll('[data-cr2-reading-timeframe]').forEach(function (node) { node.textContent = uiText(s.tf); });
    document.querySelectorAll('[data-cr2-reading-context-row]').forEach(function (node) { node.hidden = !s.detail.trim(); });
    bindAll('detailRequirement', optionalContextRequirementText(s.detail));
    bindAll('qRequirement', requirementText(s.q, 8, 1));
    document.querySelectorAll('[data-cr2-bind="detailRequirement"]').forEach(function (el) { el.setAttribute('data-ready', !s.detail.trim() || meaningfulInput(s.detail, 10).valid ? 'true' : 'false'); });
    document.querySelectorAll('[data-cr2-bind="qRequirement"]').forEach(function (el) { el.setAttribute('data-ready', meaningfulInput(s.q, 8, 1).valid ? 'true' : 'false'); });
    document.querySelectorAll('[data-cr2-chip]').forEach(function (el, i) {
      var chips = LANG !== 'en' && FLOW_LANGUAGE[LANG] ? FLOW_LANGUAGE[LANG].chips : (sit || SITS[7]).chips;
      el.textContent = uiText(chips[i] || '');
      el.style.display = chips[i] ? '' : 'none';
    });
    document.querySelectorAll('[data-cr2-tf]').forEach(function (el) {
      var on = el.getAttribute('data-cr2-tf') === s.tf;
      el.setAttribute('aria-checked', on ? 'true' : 'false');
      el.setAttribute('tabindex', on ? '0' : '-1');
      el.style.background = on ? 'rgba(143,192,174,0.14)' : 'transparent';
      el.style.borderColor = on ? 'rgba(143,192,174,0.6)' : 'rgba(247,242,232,0.16)';
      el.style.color = on ? '#F7F2E8' : '#E6E1EC';
      el.style.fontWeight = on ? '600' : '400';
    });

    var isResult = s.phase === 'result' && !!s.cards;
    show('#decide', !isResult);
    show('#form', !isResult);
    show('[data-cr2-result]', isResult);
    show('[data-cr2-previous]', !isResult && !!previousState(), 'inline-flex');
    bindAll('startLabel', isResult ? 'View My Saved Career Reading' : 'Start My Free Career Reading');
    if (isResult) {
      bindAll('resultHead', LANG !== 'en' && FLOW_LANGUAGE[LANG] ? FLOW_LANGUAGE[LANG].resultHead : (sit ? 'Your cards for this ' + sit.phrase : 'Your cards for this decision'));
      document.querySelectorAll('[data-cr2-card]').forEach(function (el) {
        var i = Number(el.getAttribute('data-cr2-card'));
        var c = s.cards[i];
        if (!c) return;
        var cur = cardCareerCopy(c, i);
        var img = el.querySelector('img');
        if (img) {
          var src = cardImg(c.n, 260);
          var srcset = src + ' 260w, ' + cardImg(c.n, 520) + ' 520w';
          if (img.getAttribute('srcset') !== srcset) img.setAttribute('srcset', srcset);
          img.setAttribute('sizes', '(max-width: 600px) 92px, 260px');
          if (img.getAttribute('src') !== src) img.setAttribute('src', src);
          img.setAttribute('loading', 'eager');
          img.setAttribute('fetchpriority', 'high');
          img.setAttribute('alt', localizedCardName(c.n) + ' tarot · ' + uiText(c.r ? 'Reversed' : 'Upright'));
          img.style.transform = c.r ? 'rotate(180deg)' : 'none';
        }
        var nm = el.querySelector('[data-cr2-cname]');
        if (nm) nm.textContent = localizedCardName(c.n) + (c.r ? ' ' + uiText('(Reversed)') : '');
        var th = el.querySelector('[data-cr2-ctheme]');
        if (th) th.textContent = cur.theme;
        var ln = el.querySelector('[data-cr2-cline]');
        if (ln) ln.textContent = cur.line;
      });
      var pattern = derivePattern(s.cards);
      bindAll('snapTension', pattern.tension);
      bindAll('snapHidden', pattern.hidden);
      bindAll('detailLens', LANG !== 'en' && FLOW_LANGUAGE[LANG] ? FLOW_LANGUAGE[LANG].detailLens : detailLens());
      bindAll('snapMove', pattern.move);
      bindAll('bridgeLead', pattern.bridge);
      /* Visible-only conversion bridge. The signed Career snapshot continues to
         use the visitor's original question because its backend contract keeps
         curiosityQuestion empty. */
      bindAll('criticalQuestion', openQuestion());
      var ins = document.querySelector('[data-cr2-insight]');
      if (ins) {
        ins.style.display = s.insight && insightMatchesLocale() ? '' : 'none';
        var ip = ins.querySelector('[data-cr2-insighttext]');
        if (ip) ip.textContent = s.insight;
      }
      var previewStatus = document.querySelector('[data-cr2-preview-status]');
      if (previewStatus) {
        var previewNotice = s.previewMode === 'quota_fallback'
          ? 'Today’s free preview allowance has been used. This overview comes from the on-page card meanings and your selected context; it is not presented as a new personalized interpretation. Your saved cards and paid reading options remain available.'
          : (s.previewMode === 'quality_fallback'
          ? 'The live preview did not meet our quality checks, so this quick overview was prepared directly from your cards. A paid reading is still prepared from your full question, context and selected depth.'
          : (s.previewMode === 'transport_fallback'
            ? 'The live preview service is temporarily unavailable. Your local card pattern remains available, and checkout will keep your question and cards attached.'
            : (s.previewMode === 'unavailable' ? 'We could not load the live preview. Your local card pattern remains available; no result has been presented as a model-generated interpretation.' : '')));
        var previewIndex = s.previewMode === 'quota_fallback' ? 0 : (s.previewMode === 'quality_fallback' ? 1 : (s.previewMode === 'transport_fallback' ? 2 : 3));
        previewStatus.textContent = previewNotice && LANG !== 'en' && FLOW_LANGUAGE[LANG] ? FLOW_LANGUAGE[LANG].preview[previewIndex] : uiText(previewNotice);
        if (s.insight && !insightMatchesLocale()) { previewNotice = SAVED_LANGUAGE_NOTICE[LANG] || SAVED_LANGUAGE_NOTICE.en; previewStatus.textContent = previewNotice; }
        previewStatus.style.display = previewNotice ? 'block' : 'none';
      }
      bindAll('packHead', sit ? 'Choose how deeply you want to explore your ' + sit.phrase : 'Choose how deeply you want to explore this decision');
    }

    document.querySelectorAll('[data-cr2-ladder]').forEach(function (el) {
      var id = el.getAttribute('data-cr2-ladder');
      var p = null;
      for (var i = 0; i < PACKS.length; i++) if (PACKS[i].id === id) p = PACKS[i];
      if (!p) return;
      var on = s.pkg === id;
      el.removeAttribute('aria-checked');
      el.setAttribute('tabindex', '0');
      el.disabled = s.buying === true;
      el.setAttribute('aria-disabled', el.disabled ? 'true' : 'false');
      if (on && s.buying) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
      el.classList.toggle('is-selected', on);
      el.classList.toggle('is-busy', on && s.buying === true);
      var nm = el.querySelector('[data-cr2-lname]');
      if (nm) nm.textContent = packName(p);
      var act = el.querySelector('[data-cr2-laction]');
      if (act) {
        var ctaBase = act.getAttribute('data-cr2-cta-base') || String(act.textContent || '').trim() || uiText('Choose this depth →');
        if (!act.hasAttribute('data-cr2-cta-base')) act.setAttribute('data-cr2-cta-base', ctaBase);
        act.textContent = on && s.buying ? uiText('Preparing secure checkout…') : ctaBase + (priceReady(p) ? ' · ' + priceLabel(p) : '');
      }
    });
    document.querySelectorAll('[data-cr2-pkg]').forEach(function (el) {
      var id = el.getAttribute('data-cr2-pkg');
      var p = null;
      for (var i = 0; i < PACKS.length; i++) if (PACKS[i].id === id) p = PACKS[i];
      if (!p) return;
      var on = s.pkg === id;
      el.classList.toggle('cr2-pk-sel', on);
      el.classList.toggle('cr2-pk-busy', on && s.buying === true);
      var nm = el.querySelector('[data-cr2-pname]');
      if (nm) nm.textContent = packName(p);
      var cta = el.querySelector('[data-cr2-pcta]');
      if (cta) {
        cta.setAttribute('aria-pressed', on ? 'true' : 'false');
        cta.textContent = on ? (priceReady(p) ? uiText('Continue with this reading') + ' · ' + priceLabel(p) : uiText('Continue with this reading')) : cta.getAttribute('data-cr2-ctabase');
      }
    });
    document.querySelectorAll('[data-cr2-drawer-pkg]').forEach(function (el) {
      var id = el.getAttribute('data-cr2-drawer-pkg');
      var p = PACKS.filter(function (candidate) { return candidate.id === id; })[0];
      if (!p) return;
      var on = s.pkg === id;
      el.setAttribute('aria-checked', on ? 'true' : 'false');
      el.setAttribute('tabindex', on ? '0' : '-1');
      var name = el.querySelector('[data-cr2-drawer-pname]');
      if (name) name.textContent = packName(p);
    });

    document.querySelectorAll('[data-cr2-price]').forEach(function (el) {
      var id = el.getAttribute('data-cr2-price');
      var p = null;
      for (var i = 0; i < PACKS.length; i++) if (PACKS[i].id === id) p = PACKS[i];
      if (!p) return;
      var ready = priceReady(p);
      var failed = s.pricing && s.pricing.status === 'error';
      el.classList.toggle('cr2-price-skeleton', !ready && !failed);
      el.classList.toggle('cr2-price-error', !!failed);
      el.textContent = ready ? priceLabel(p) : uiText(failed ? 'Price unavailable' : (el.tagName === 'DIV' ? 'Checking secure price…' : 'Checking…'));
    });

    var drawer = document.querySelector('[data-cr2-drawer]');
    var overlay = document.querySelector('[data-cr2-overlay]');
    var drawerOn = s.drawer && !!pack;
    if (drawer) drawer.style.display = drawerOn ? 'block' : 'none';
    if (overlay) overlay.style.display = drawerOn ? 'block' : 'none';
    if (drawerOn) {
      bindAll('selName', packName(pack));
      bindAll('selPrice', priceReady(pack) ? priceLabel(pack) : '—');
      bindAll('selBasis', localizedBasis(sit, s.tf));
      bindAll('selQuestion', s.q.trim() ? '“' + s.q.trim() + '”' : '—');
      bindAll('selCards', s.cards ? s.cards.map(function (c) { return localizedCardName(c.n) + (c.r ? ' ' + uiText('(Reversed)') : ''); }).join(' · ') : '');
    }
    var checkoutButton = document.querySelector('[data-cr2-act="checkout"]');
    var priceStatus = document.querySelector('[data-cr2-status]');
    var retryPrice = document.querySelector('[data-cr2-act="retryprice"]');
    var checkoutReady = !!(pack && priceReady(pack) && checkoutAuthorizationReady() && !s.buying);
    if (checkoutButton) {
      checkoutButton.disabled = !checkoutReady;
      checkoutButton.setAttribute('aria-disabled', checkoutReady ? 'false' : 'true');
      checkoutButton.setAttribute('aria-busy', s.buying || !s.pricing || s.pricing.status === 'loading' ? 'true' : 'false');
      checkoutButton.textContent = s.buying ? uiText('Preparing secure checkout…') : (checkoutReady ? uiText('Continue to Secure Checkout') + ' · ' + priceLabel(pack) : uiText(s.pricing && s.pricing.status === 'error' ? 'Secure price unavailable' : 'Checking secure price…'));
    }
    if (priceStatus) {
      if (s.buying) priceStatus.textContent = s.authorityRefreshRequired ? recoveryText('checking') : recoveryText('preparing');
      else if (s.checkoutMessage) priceStatus.textContent = s.checkoutMessage;
      else if (!s.pricing || s.pricing.status === 'loading') priceStatus.textContent = uiText('Confirming the secure storefront price…');
      else if (s.pricing.status === 'error') priceStatus.textContent = uiText('We could not confirm a secure storefront price. Checkout remains disabled.');
      else priceStatus.textContent = '';
      priceStatus.style.color = s.checkoutMessageError && !s.buying ? '#EBC9D3' : '#E6E1EC';
      priceStatus.style.display = drawerOn || (isResult && (s.checkoutMessage || s.buying || !s.pricing || s.pricing.status === 'loading' || s.pricing.status === 'error')) ? 'block' : 'none';
    }
    if (retryPrice) retryPrice.style.display = isResult && s.pricing && s.pricing.status === 'error' ? 'block' : 'none';

    document.body.classList.toggle('cr2-drawer-open', drawerOn);
    setModalInert(drawerOn);
    if (drawerOn && !drawerWasOpen && drawer) {
      var close = drawer.querySelector('[data-cr2-act="closedrawer"]');
      try { (close || drawer).focus({ preventScroll: true }); } catch (_) { try { (close || drawer).focus(); } catch (_) {} }
    }
    drawerWasOpen = drawerOn;

    var sticky = document.querySelector('[data-cr2-sticky]');
    if (sticky) {
      var mobileViewport = !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
      var stickyOn = mobileViewport && isResult && s.stickyEligible && !s.packageVisible && !s.footerVisible && !s.keyboardOpen && !drawerOn && !stickyIsDismissed();
      sticky.style.display = stickyOn ? 'flex' : 'none';
      document.body.classList.toggle('cr2-sticky-on', stickyOn);
      document.documentElement.classList.toggle('cr2-sticky-on', stickyOn);
      bindAll('stickyTop', pack ? uiText(sit ? sit.title : 'Your reading') + ' · ' + uiText(s.tf) : 'Your question and cards are saved');
      bindAll('stickyMain', pack ? packName(pack) + (priceReady(pack) ? ' · ' + priceLabel(pack) : ' · ' + uiText('Secure price check')) : 'Take your reading further');
      var sb = sticky.querySelector('[data-cr2-act="sticky"]');
      if (sb) {
        sb.disabled = pack ? !priceReady(pack) : false;
        sb.setAttribute('aria-disabled', sb.disabled ? 'true' : 'false');
        sb.textContent = pack ? (priceReady(pack) ? uiText('Review order') : uiText('Checking price…')) : uiText('Choose depth');
      }
    }
    syncRail();
  }

  /* ------------ canonical free preview ------------ */
  function sanitizeFreeInsight(value) {
    var clean = String(value || '').replace(/<[^>]*>/g, ' ').replace(/&(amp|lt|gt|quot|#39);/g, function (m, k) { return { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'" }[k]; }).replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    var givesAwayPaidDecision = /\b(you should|you must|accept|decline|resign|leave the job|stay in the role|ask for the raise|choose option|the deciding condition is|your best action is|do this next)\b/i.test(clean);
    if (givesAwayPaidDecision) {
      if (LANG !== 'en' && FLOW_LANGUAGE[LANG]) return FLOW_LANGUAGE[LANG].detailLens;
      return 'The cards reinforce the tension already visible in this spread, but they do not settle the decision on their own. The specific condition, evidence and recommendation remain open for the personalized reading.';
    }
    var sentences = clean.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [clean];
    return sentences.slice(0, 2).join(' ').slice(0, 520).trim();
  }
  function fetchReading() {
    if (s.fetching || s.previewToken || s.transportFallback || !s.cards || !s.q.trim()) return;
    s.fetching = true;
    var dl = window.DeckauraLocale || {};
    var activeCurrency = '';
    try { activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || ''; } catch (_) {}
    var sig = signalString();
    var body = {
      tool: PAGE, type: TYPE, spread: SPREAD,
      presentationVariant: VARIANT,
      scope: SCOPE, confidence: CONFIDENCE,
      question: s.q.trim().slice(0, 400),
      context: contextLine(),
      signals: sig, cards: sig,
      /* Direct-reading contracts reserve these optional legacy fields. The
         category-aware continuation is expressed in the visible package copy. */
      curiosityQuestion: '', focus: '',
      visitorId: visitorId(),
      readingId: s.readingId,
      lang: String(dl.language || 'en').toLowerCase(),
      locale: String(dl.locale || (document.documentElement && document.documentElement.lang) || 'en'),
      country: String(dl.country || 'US').toUpperCase(),
      currency: String(dl.currency || activeCurrency || 'USD').toUpperCase(),
      market: String(dl.market || '').toLowerCase(),
      snapshotVersion: 'reading-snapshot-v2',
      analyticsSchema: ANALYTICS_SCHEMA,
      funnelVersion: FUNNEL_VERSION,
      attemptId: ensureAttempt()
    };
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    var request = { sequence: ++freeRequestSequence, identity: readingIdentity(), authorityKey: authorityKey(), controller: ac };
    activeFreeRequest = request;
    function currentRequest() { return activeFreeRequest === request && request.sequence === freeRequestSequence && s.phase === 'result' && request.identity === readingIdentity(); }
    var timer = ac ? setTimeout(function () { ac.abort(); }, 16000) : 0;
    fetch(API_FREE, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body), signal: ac ? ac.signal : undefined
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, data: j }; }); })
      .then(function (res) {
        if (!currentRequest()) return;
        if (res.ok && res.data) {
          var responseToken = String(res.data.token || '');
          s.previewToken = /^[a-f0-9]{32}$/i.test(responseToken) ? responseToken : '';
          if (!s.previewToken) {
            s.previewMode = 'unavailable';
            s.previewSource = 'preview_token_invalid';
            publishFunnelEvent('free_result_resolved', { result_mode: 'validation_error', error_code: 'preview_token_invalid' }, { onceKey: 'free_result_resolved' });
            render();
            persistState('');
            return;
          }
          s.transportFallback = false;
          s.transportFailure = '';
          s.authorityKey = request.authorityKey;
          // A durable replay may belong to an earlier question in this visitor's
          // history. Resume it explicitly at checkout instead of assuming it is
          // still the backend's current authority.
          s.authorityRefreshRequired = res.data.replayed === true;
          s.previewSource = safeCode(res.data.servedSource || res.data.compactInsightSource, 'unknown');
          var quotaFallback = /quota/.test(s.previewSource);
          var qualityFallback = !quotaFallback && (/^deterministic_/.test(s.previewSource) || String(res.data.auditStatus || res.data.compactInsightAuditStatus || '').toLowerCase() === 'passed_fallback');
          s.previewMode = quotaFallback ? 'quota_fallback' : (qualityFallback ? 'quality_fallback' : 'model');
          var raw = String(res.data.compactInsight || res.data.teaser || '');
          s.insight = sanitizeFreeInsight(raw);
          s.insightLocale = String(res.data.resolvedLanguage || res.data.locale || res.data.lang || JSON.parse(request.authorityKey).locale || LANG).toLowerCase();
          publishFunnelEvent('free_result_resolved', { result_mode: quotaFallback ? 'quota_fallback' : (qualityFallback ? 'quality_fallback' : 'success'), source: s.previewSource.slice(0, 40), audit_reason: qualityFallback ? safeCode(res.data.auditReason || res.data.compactInsightAuditReason, 'quality_fallback') : '' }, { onceKey: 'free_result_resolved' });
          render();
          persistState('');
        } else if (res.data && res.data.offerAllowed === true && (res.status === 408 || res.status === 429 || res.status >= 500)) {
          s.transportFailure = res.status === 408 ? 'http_408' : (res.status === 429 ? 'http_429' : 'http_5xx');
          s.transportFallback = true;
          s.previewMode = 'transport_fallback';
          s.previewSource = s.transportFailure;
          publishFunnelEvent('free_result_resolved', { result_mode: 'fallback', error_code: s.transportFailure }, { onceKey: 'free_result_resolved' });
          render();
          syncRail();
          persistState('');
        } else {
          s.previewMode = 'unavailable';
          s.previewSource = safeCode(res.data && res.data.error, 'reading_unavailable');
          render();
          persistState('');
          publishFunnelEvent('free_result_resolved', { result_mode: res.status >= 500 ? 'server_error' : 'validation_error', error_code: safeCode(res.data && res.data.error, 'reading_unavailable') }, { onceKey: 'free_result_resolved' });
        }
      })
      .catch(function (error) {
        if (!currentRequest()) return;
        if (error && error.name === 'AbortError') {
          s.transportFailure = 'timeout';
          s.transportFallback = true;
          s.previewMode = 'transport_fallback';
          s.previewSource = 'timeout';
          render();
          syncRail();
          persistState('');
        } else { s.previewMode = 'unavailable'; s.previewSource = 'network'; render(); persistState(''); }
        publishFunnelEvent('free_result_resolved', { result_mode: error && error.name === 'AbortError' ? 'timeout' : 'network_error', error_code: error && error.name === 'AbortError' ? 'timeout' : 'network' }, { onceKey: 'free_result_resolved' });
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
        if (!currentRequest()) return;
        activeFreeRequest = null;
        s.fetching = false;
        render();
        if (s.checkoutQueued) {
          if (s.previewToken && s.authorityRefreshRequired) reauthorizeReading();
          else if (!checkoutAuthorizationReady() && s.previewMode) resetCheckout('unavailable', true);
          else setTimeout(continueQueuedCheckout, 0);
        }
      });
  }

  function reauthorizeReading() {
    if (s.fetching || !s.previewToken || !s.authorityRefreshRequired) return;
    s.fetching = true;
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    var request = { sequence: ++freeRequestSequence, identity: readingIdentity(), authorityKey: authorityKey(), controller: ac };
    activeFreeRequest = request;
    function currentRequest() { return activeFreeRequest === request && request.sequence === freeRequestSequence && s.phase === 'result' && request.identity === readingIdentity(); }
    var body = authorityContext();
    body.visitorId = visitorId(); body.previewToken = s.previewToken; body.readingId = s.readingId; body.page = PAGE;
    var timer = ac ? setTimeout(function () { ac.abort(); }, 16000) : 0;
    fetch('https://reading.deckaura.com/api/readings/reauthorize', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: ac ? ac.signal : undefined
    }).then(function (r) { return r.json().catch(function () { return {}; }).then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (result) {
        if (!currentRequest()) return;
        if (!result.ok || result.data.ok !== true || result.data.readingId !== s.readingId || !/^[a-f0-9]{32}$/i.test(String(result.data.token || ''))) throw new Error('authority_unavailable');
        s.previewToken = result.data.token;
        s.authorityKey = request.authorityKey;
        s.authorityRefreshRequired = false;
        s.transportFallback = false; s.transportFailure = '';
        persistState('packages');
      }).catch(function () {
        if (currentRequest()) resetCheckout('unavailable', true);
      }).finally(function () {
        if (timer) clearTimeout(timer);
        if (!currentRequest()) return;
        activeFreeRequest = null; s.fetching = false;
        render();
        if (s.checkoutQueued) continueQueuedCheckout();
      });
  }

  /* ------------ shared rail (career-three-card-compact) ------------ */
  function watchRuntimeScript(script) {
    var load = { script: script, sequence: ++runtimeLoadSequence, timer: 0, settled: false };
    runtimeLoad = load;
    function finish(failed) {
      if (runtimeLoad !== load || (load.settled && failed)) return;
      load.settled = true;
      if (load.timer) clearTimeout(load.timer);
      var host = document.getElementById('cr2-checkout-rail-host');
      if (!host) return;
      var ready = window.DeckauraDeepReading && typeof window.DeckauraDeepReading.enable === 'function';
      if (!ready) {
        host.setAttribute('data-load-failed', 'true');
        handlePricingSnapshot({ status: 'error', error_code: 'checkout_runtime_load_failed', tiers: [] });
      } else {
        host.removeAttribute('data-load-failed');
        if (s.phase === 'result') setTimeout(function () { syncRail(); continueQueuedCheckout(); }, 0);
      }
    }
    script.addEventListener('load', function () { finish(false); }, { once: true });
    script.addEventListener('error', function () { finish(true); }, { once: true });
    load.timer = setTimeout(function () { finish(true); }, 12000);
  }
  function retryRuntimeScript() {
    var host = document.getElementById('cr2-checkout-rail-host');
    var previous = host && host.querySelector('script[src*="deep-reading-cta-runtime.js"]');
    if (!previous || host.getAttribute('data-load-failed') !== 'true') return false;
    var script = document.createElement('script');
    Array.prototype.slice.call(previous.attributes).forEach(function (attr) { script.setAttribute(attr.name, attr.value); });
    var url = new URL(previous.src, window.location.href);
    url.searchParams.set('_deckaura_retry', Date.now().toString(36) + '-' + (runtimeLoadSequence + 1));
    script.src = url.toString();
    host.removeAttribute('data-load-failed');
    watchRuntimeScript(script);
    previous.parentNode.replaceChild(script, previous);
    return true;
  }
  function mountCheckoutRail() {
    if (window.DeckauraDeepReading && typeof window.DeckauraDeepReading.enable === 'function') return true;
    var template = document.getElementById('cr2-checkout-rail-template');
    var host = document.getElementById('cr2-checkout-rail-host');
    if (!template || !template.content || !host || host.getAttribute('data-mounted') === 'true') return false;
    host.setAttribute('data-mounted', 'true');
    var fragment = template.content.cloneNode(true);
    Array.prototype.slice.call(fragment.querySelectorAll('script')).forEach(function (oldScript) {
      var nextScript = document.createElement('script');
      Array.prototype.slice.call(oldScript.attributes).forEach(function (attr) { nextScript.setAttribute(attr.name, attr.value); });
      nextScript.text = oldScript.textContent || '';
      if (nextScript.src && /deep-reading-cta-runtime\.js(?:\?|$)/.test(nextScript.src)) watchRuntimeScript(nextScript);
      oldScript.parentNode.replaceChild(nextScript, oldScript);
    });
    host.appendChild(fragment);
    return !!(window.DeckauraDeepReading && typeof window.DeckauraDeepReading.enable === 'function');
  }
  function removeLegacyRailPresentation() {
    var host = document.getElementById('cr2-checkout-rail-host');
    if (!host || host.getAttribute('data-presentation-pruned') === 'true') return;
    host.querySelectorAll('#ddrGate,#deepReadingCta').forEach(function (node) { node.remove(); });
    host.setAttribute('data-presentation-pruned', 'true');
  }
  function checkoutProperties() {
    var pack = packOf();
    return {
      '_Page Design': 'career-tarot-v2',
      '_Reading Title': pack ? packName(pack) : '',
      '_Package Title': pack ? packName(pack) : '',
      '_Reading Type': TYPE,
      '_Spread Positions': POSITIONS.join(' · '),
      '_Situation': (sitOf() || SITS[7]).title,
      '_Timeframe': s.tf,
      '_attempt_id': ensureAttempt(),
      '_reading_id': s.readingId,
      '_funnel_version': FUNNEL_VERSION,
      '_analytics_schema': ANALYTICS_SCHEMA
    };
  }
  function checkoutNavigationConfirmed(detail) {
    if (checkoutTimer) clearTimeout(checkoutTimer);
    checkoutTimer = 0;
    var pack = packOf();
    publishFunnelEvent('checkout_started', {
      package_id: pack ? pack.id : '',
      package_tier: detail && detail.package_tier || (pack ? pack.tier : ''),
      variant_id: String(detail && detail.variant_id || '')
    }, { onceKey: 'checkout_started' });
  }
  function checkoutFallbackReady() {
    return !s.previewToken && s.transportFallback === true && /^(?:timeout|http_408|http_429|http_5xx)$/.test(String(s.transportFailure || ''));
  }
  function checkoutAuthorizationReady() {
    return (!s.authorityRefreshRequired && s.authorityKey === authorityKey() && /^[a-f0-9]{32}$/i.test(String(s.previewToken || ''))) || checkoutFallbackReady();
  }
  function syncRail() {
    if (!s.cards || s.phase !== 'result') return;
    mountCheckoutRail();
    var api = window.DeckauraDeepReading;
    if (!api || typeof api.enable !== 'function') return;
    var sig = signalString();
    var pack = packOf();
    var fallback = checkoutFallbackReady();
    var checkoutPending = !checkoutAuthorizationReady();
    var identity = readingIdentity();
    var nextSyncSignature = JSON.stringify({
      readingId: s.readingId,
      question: s.q,
      context: contextLine(),
      signals: sig,
      packageId: pack ? pack.id : '',
      previewToken: s.authorityRefreshRequired ? '' : s.previewToken,
      transportFallback: s.transportFallback === true,
      transportFailure: s.transportFailure
    });
    try {
      if (railReadingId !== s.readingId) {
        railReadingId = s.readingId;
        railSyncSignature = nextSyncSignature;
        try {
          api.enable({
            type: TYPE, teaser: false, resultReady: true,
            presentationVariant: VARIANT,
            canonicalPage: PAGE, visitorId: visitorId(),
            question: s.q.trim().slice(0, 400), readingId: s.readingId,
            curiosityQuestion: '', focus: '',
            context: contextLine(),
            signals: sig, cards: sig,
            spread: SPREAD,
            scope: SCOPE, confidence: CONFIDENCE,
            tool: PAGE, defaultTier: pack ? pack.tier : 'deeper',
            previewToken: s.authorityRefreshRequired ? '' : (s.previewToken || ''),
            transportFallback: fallback,
            transportFailure: fallback ? s.transportFailure : '',
            checkoutPending: checkoutPending,
            checkoutRecoveryHost: document.querySelector('[data-cr2-checkout-recovery-host]'),
            onCartReviewReady: function (host) { if (s.phase === 'result' && readingIdentity() === identity) cartReviewReady(host); },
            onPricingStateChange: handlePricingSnapshot,
            onPriceReady: function () { if (s.checkoutQueued) setTimeout(continueQueuedCheckout, 0); },
            onCheckoutNavigationConfirmed: checkoutNavigationConfirmed,
            checkoutGuard: function (detail) {
              var selected = pricingTier(packOf());
              return !!(s.phase === 'result' && readingIdentity() === identity && checkoutAuthorizationReady() && packOf() && detail && detail.tier === packOf().tier && s.pricing && s.pricing.status === 'ready' && selected && selected.available && selected.variant_id === String(detail.variantId || ''));
            },
            checkoutProperties: checkoutProperties
          });
        } catch (enableError) {
          railReadingId = '';
          railSyncSignature = '';
          throw enableError;
        }
      } else if (railSyncSignature !== nextSyncSignature && typeof api.update === 'function') {
        api.update({
          resultReady: true,
          question: s.q.trim().slice(0, 400), readingId: s.readingId,
          context: contextLine(), signals: sig, cards: sig,
          spread: SPREAD, scope: SCOPE, confidence: CONFIDENCE,
          presentationVariant: VARIANT,
          tool: PAGE, checkoutProperties: checkoutProperties,
          previewToken: s.authorityRefreshRequired ? '' : (s.previewToken || ''),
          transportFallback: fallback,
          transportFailure: fallback ? s.transportFailure : ''
        });
        railSyncSignature = nextSyncSignature;
      }
      if (typeof api.getPricingSnapshot === 'function') handlePricingSnapshot(api.getPricingSnapshot());
      /* The shared checkout products are tier-generic. Keep the public line-item
         property aligned with the career-specific name the visitor selected. */
      document.querySelectorAll('.ddr-pack__opt[data-tier]').forEach(function (option) {
        var tier = option.getAttribute('data-tier');
        for (var i = 0; i < PACKS.length; i++) {
          if (PACKS[i].tier === tier) option.setAttribute('data-package-name', packName(PACKS[i]));
        }
      });
      if (typeof api.setPreviewAuthorization === 'function') {
        api.setPreviewAuthorization({
          previewToken: s.authorityRefreshRequired ? '' : (s.previewToken || ''),
          transportFallback: fallback,
          transportFailure: fallback ? s.transportFailure : ''
        });
      }
      if (pack && typeof api.setTier === 'function') api.setTier(pack.tier);
      if (typeof api.setCheckoutPending === 'function') api.setCheckoutPending(checkoutPending);
      removeLegacyRailPresentation();
    } catch (_) {}
  }
  var checkoutTimer = 0;
  var checkoutCartReview = null;
  function clearCartReview() {
    checkoutCartReview = null;
    var host = document.querySelector('[data-cr2-checkout-recovery-host]');
    if (!host) return;
    host.__deckauraRecoveryGeneration = (host.__deckauraRecoveryGeneration || 0) + 1;
    host.textContent = '';
    host.hidden = true;
  }
  function checkoutCartReviewReason(reason) {
    return /^(reading_cart_conflict|cart_changed|cart_operation_busy|cart_result_uncertain|cart_read_failed|cart_add_failed|checkout_guard_unavailable|checkout_navigation_busy)$/.test(String(reason || ''));
  }
  function checkoutCartReviewPending() {
    return !!(checkoutCartReview && s.phase === 'result' && checkoutCartReview.identity === readingIdentity() && checkoutCartReview.packageId === s.pkg);
  }
  function cartReviewReady(host) {
    if (!host || host !== document.querySelector('[data-cr2-checkout-recovery-host]') || !host.isConnected || host.hidden || !host.textContent.trim() || !checkoutCartReviewPending()) return;
    var generation = host.__deckauraRecoveryGeneration;
    if (host.__careerReviewFocusedGeneration === generation) return;
    host.__careerReviewFocusedGeneration = generation;
    var identity = readingIdentity();
    requestAnimationFrame(function () {
      if (readingIdentity() !== identity || !checkoutCartReviewPending() || host.hidden || !host.isConnected || host.__deckauraRecoveryGeneration !== generation) return;
      host.tabIndex = -1;
      host.focus({ preventScroll: true });
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      host.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
    });
  }
  function selectPackage(id) {
    if (s.phase !== 'result' || s.buying || !PACKS.some(function (pack) { return pack.id === id; })) return false;
    if (s.pkg !== id) clearCartReview();
    s.pkg = id;
    s.packageChosen = true;
    return true;
  }
  function resetCheckout(message, isError) {
    if (checkoutTimer) clearTimeout(checkoutTimer);
    checkoutTimer = 0;
    s.buying = false;
    s.checkoutQueued = false;
    s.checkoutVia = '';
    s.checkoutMessage = message ? recoveryText(message) : '';
    s.checkoutMessageError = isError === true;
    render();
  }
  function railCheckout() {
    var api = window.DeckauraDeepReading;
    var pack = packOf();
    if (!api || typeof api.checkoutNow !== 'function' || !pack || !priceReady(pack) || !checkoutAuthorizationReady()) return false;
    syncRail();
    try { api.setTier(pack.tier); } catch (_) {}
    try { return api.checkoutNow() === true; } catch (_) { return false; }
  }
  function continueQueuedCheckout() {
    if (!s.checkoutQueued || !s.buying || !packOf()) return;
    if (!checkoutAuthorizationReady() || !priceReady(packOf())) return;
    var api = window.DeckauraDeepReading;
    if (!api || typeof api.checkoutNow !== 'function') { mountCheckoutRail(); return; }
    s.checkoutQueued = false;
    if (!railCheckout()) {
      if (checkoutCartReviewPending()) return;
      resetCheckout('failed', true);
      return;
    }
    if (!s.buying || checkoutCartReviewPending()) return;
    var status = document.querySelector('[data-cr2-status]');
    if (status) { status.textContent = recoveryText('preparing'); status.style.display = 'block'; status.style.color = '#DEC28D'; }
  }
  function startCheckout(via) {
    var pack = packOf();
    if (s.phase !== 'result' || !validCards(s.cards) || !pack || s.buying) return;
    clearCartReview();
    s.checkoutMessage = ''; s.checkoutMessageError = false;
    if (s.previewToken && s.authorityKey !== authorityKey()) s.authorityRefreshRequired = true;
    firstInteraction('checkout');
    publishFunnelEvent('checkout_cta_clicked', { package_id: pack.id, package_tier: pack.tier, placement: via || 'drawer' });
    persistState('packages');
    s.buying = true;
    s.checkoutQueued = true;
    s.checkoutVia = via || 'package_card';
    render();
    var checkoutIdentity = readingIdentity();
    checkoutTimer = setTimeout(function () {
      if (s.phase !== 'result' || readingIdentity() !== checkoutIdentity || !s.buying) return;
      resetCheckout('failed', true);
    }, 30000);
    var api = window.DeckauraDeepReading;
    if (s.pricing && s.pricing.status === 'error') {
      s.pricingRetry = true;
      handlePricingSnapshot({ status: 'loading', currency: s.pricing.currency || '', tiers: s.pricing.tiers || [] });
      try { if (api && typeof api.retryPricing === 'function') api.retryPricing(); } catch (_) {}
    }
    if (s.previewToken && s.authorityRefreshRequired) reauthorizeReading();
    else if (!s.previewToken && !s.transportFallback && !s.fetching) fetchReading();
    syncRail();
    continueQueuedCheckout();
  }

  /* ------------ flow ------------ */
  function scrollToId(id) {
    var el = document.getElementById(id);
    if (!el) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { window.scrollTo({ top: el.getBoundingClientRect().top + (window.pageYOffset || 0) - 76, behavior: reduce ? 'auto' : 'smooth' }); } catch (_) {}
  }
  function scrollToOffer(focusChoice) {
    var offer = document.querySelector('[data-cr2-offer]');
    if (!offer) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { offer.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); } catch (_) { scrollToId(offer.id || 'packages'); }
    if (focusChoice) setTimeout(function () {
      var first = offer.querySelector('[data-cr2-ladder]:not([disabled])');
      if (first) { try { first.focus({ preventScroll: true }); } catch (_) { try { first.focus(); } catch (_) {} } }
    }, reduce ? 0 : 350);
  }
  function focusResultHeading() {
    var heading = document.querySelector('#cards h2,[data-cr2-result] h2');
    if (!heading) return;
    heading.setAttribute('tabindex', '-1');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { heading.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); } catch (_) { scrollToId('cards'); }
    try { heading.focus({ preventScroll: true }); } catch (_) { try { heading.focus(); } catch (_) {} }
  }
  function stickyIsDismissed() {
    try { return sessionStorage.getItem(KEY + '.sticky.dismissed') === '1'; } catch (_) { return false; }
  }
  function dismissSticky() {
    try { sessionStorage.setItem(KEY + '.sticky.dismissed', '1'); } catch (_) {}
  }
  function visibleRatio(el) {
    if (!el) return 0;
    var rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return 0;
    var width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    var height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    return Math.min(1, (width * height) / (rect.width * rect.height));
  }
  function qualifiedObserver(el, callback) {
    var timer = 0;
    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (entry && entry.isIntersecting && entry.intersectionRatio >= 0.5 && document.visibilityState === 'visible') {
        if (!timer) timer = setTimeout(function () {
          timer = 0;
          if (document.visibilityState === 'visible' && visibleRatio(el) >= 0.5) callback();
        }, 1000);
      } else if (timer) { clearTimeout(timer); timer = 0; }
    }, { threshold: [0, 0.5, 1] });
    observer.observe(el);
    return observer;
  }
  function startResultObservers() {
    if (!('IntersectionObserver' in window) || s.phase !== 'result') return;
    if (resultViewObserver) resultViewObserver.disconnect();
    if (offerViewObserver) offerViewObserver.disconnect();
    if (stickyObserver) stickyObserver.disconnect();
    if (packageObserver) packageObserver.disconnect();
    if (footerObserver) footerObserver.disconnect();
    var resultRegion = document.querySelector('[data-cr2-result]');
    var resultViewTarget = document.querySelector('#cards h2') || resultRegion;
    var offer = document.querySelector('[data-cr2-offer]');
    var pattern = document.querySelector('[data-cr2-pattern]');
    var offerTargets = offer ? [offer].concat(Array.prototype.slice.call(offer.querySelectorAll('[data-cr2-ladder]'))) : [];
    var footer = document.querySelector('footer');
    if (resultViewTarget) resultViewObserver = qualifiedObserver(resultViewTarget, function () {
      publishFunnelEvent('free_result_viewed', { card_count: 3 }, { once: true });
    });
    if (offer) offerViewObserver = qualifiedObserver(offer, function () {
      publishFunnelEvent('offer_viewed', { placement: 'result_bridge', package_set: 'two_depths' }, { onceKey: 'offer_viewed_result_bridge' });
    });
    if (pattern) stickyObserver = qualifiedObserver(pattern, function () {
      if (!s.stickyEligible) { s.stickyEligible = true; render(); }
    });
    if (offerTargets.length) {
      packageObserver = new IntersectionObserver(function (entries) {
        var visible = offerTargets.some(function (target) { return visibleRatio(target) >= 0.1; });
        if (visible !== s.packageVisible) { s.packageVisible = visible; render(); }
      }, { threshold: [0, 0.1, 0.5] });
      offerTargets.forEach(function (target) { packageObserver.observe(target); });
    }
    if (footer) {
      footerObserver = new IntersectionObserver(function (entries) {
        var visible = !!(entries[0] && entries[0].isIntersecting);
        if (visible !== s.footerVisible) { s.footerVisible = visible; render(); }
      }, { threshold: [0] });
      footerObserver.observe(footer);
    }
    if (engagementTimer) clearInterval(engagementTimer);
    engagedVisibleMs = 0;
    engagedLastTick = Date.now();
    engagementTimer = setInterval(function () {
      var now = Date.now();
      var elapsed = Math.min(1500, Math.max(0, now - engagedLastTick));
      engagedLastTick = now;
      if (document.visibilityState === 'visible' && resultRegion && visibleRatio(resultRegion) > 0) engagedVisibleMs += elapsed;
      if (engagedVisibleMs >= 10000) {
        publishFunnelEvent('free_result_engaged', { active_visible_seconds: 10 }, { once: true });
        if (!s.stickyEligible) { s.stickyEligible = true; render(); }
        clearInterval(engagementTimer); engagementTimer = 0;
      }
    }, 500);
  }
  function updateKeyboardState() {
    var vv = window.visualViewport;
    var next = !!(vv && vv.height < window.innerHeight * 0.76);
    if (next !== s.keyboardOpen) { s.keyboardOpen = next; render(); }
  }
  function beginDifferentDecision() {
    clearCartReview();
    invalidateReadingRequest();
    if (s.phase === 'result' && validCards(s.cards)) {
      try { localStorage.setItem(PREVIOUS_KEY, JSON.stringify(statePayload())); } catch (_) {}
    }
    try { sessionStorage.removeItem(KEY); localStorage.removeItem(KEY); sessionStorage.removeItem(KEY + '.sticky.dismissed'); } catch (_) {}
    if (checkoutTimer) clearTimeout(checkoutTimer);
    checkoutTimer = 0;
    s.sit = 'other'; s.step = 3; s.detail = ''; s.q = ''; s.tf = 'No fixed date'; s.err = '';
    s.phase = 'form'; s.anim = false; s.cards = null; s.pkg = null; s.packageChosen = false; s.drawer = false;
    s.readingId = ''; s.previewToken = ''; s.transportFallback = false; s.transportFailure = '';
    s.createdAt = 0; s.authorityKey = ''; s.authorityRefreshRequired = false;
    s.checkoutMessage = ''; s.checkoutMessageError = false;
    s.insight = ''; s.insightLocale = ''; s.previewMode = ''; s.previewSource = ''; s.fetching = false; s.buying = false;
    s.attemptId = ''; s.sentEvents = {}; s.pricing = null; s.pricingSignature = ''; s.pricingRetry = false;
    s.checkoutQueued = false; s.checkoutVia = '';
    s.stickyEligible = false; s.packageVisible = false; s.footerVisible = false; s.keyboardOpen = false;
    railReadingId = ''; railSyncSignature = ''; returnAnchor = '';
    var live = document.querySelector('[data-cr2-live]');
    if (live) live.textContent = '';
    closePackageComparison();
    try { if (window.DeckauraDeepReading && typeof window.DeckauraDeepReading.disable === 'function') window.DeckauraDeepReading.disable(); } catch (_) {}
    publishFunnelEvent('different_decision_started', { previous_saved: previousState() ? 1 : 0 }, { once: true });
    render(); persistState(''); scrollToId('decide');
    setTimeout(function () { var q = document.querySelector('[data-cr2-q]'); if (q) q.focus(); }, 80);
  }
  function returnToPreviousReading() {
    var saved = previousState();
    if (!saved) return;
    saved.returnAnchor = 'cards';
    try {
      sessionStorage.setItem(KEY, JSON.stringify(saved));
      localStorage.setItem(KEY, JSON.stringify(saved));
      window.location.reload();
    } catch (_) {}
  }
  function next() {
    if (s.phase !== 'form' || s.buying) return;
    clearCartReview();
    firstInteraction('form_continue');
    if (!meaningfulInput(s.q, 8, 1).valid) { s.err = FLOW_LANGUAGE[LANG] ? FLOW_LANGUAGE[LANG].errors[2] : 'Write a meaningful question with at least 8 characters.'; render(); return; }
    s.err = '';
    s.sit = inferSituation();
    s.step = 3;
    publishFunnelEvent('question_validated', { length_bucket: s.q.trim().length <= 20 ? '8-20' : (s.q.trim().length <= 60 ? '21-60' : (s.q.trim().length <= 120 ? '61-120' : '121+')) }, { once: true });
    publishFunnelEvent('draw_submitted', { category_id: s.sit, timeframe_id: s.tf });
    invalidateReadingRequest();
    s.previewToken = ''; s.authorityKey = ''; s.authorityRefreshRequired = false;
    s.transportFallback = false; s.transportFailure = '';
    s.insight = ''; s.insightLocale = ''; s.previewMode = ''; s.previewSource = '';
    s.checkoutMessage = ''; s.checkoutMessageError = false;
    s.cards = pickCards();
    preloadCardImages(s.cards);
    s.pkg = null;
    s.packageChosen = false;
    s.pricing = null;
    s.pricingSignature = '';
    s.phase = 'result';
    closePackageComparison();
    s.anim = true;
    publishFunnelEvent('draw_succeeded', { card_count: 3 }, { once: true });
    publishFunnelEvent('cards_completed', { card_count: 3 }, { once: true });
    publishFunnelEvent('free_result_requested', { category_id: s.sit, timeframe_id: s.tf }, { once: true });
    render();
    persistState('');
    clearTimeout(animTimer);
    animTimer = setTimeout(function () { s.anim = false; }, 1500);
    setTimeout(focusResultHeading, 60);
    startResultObservers();
    fetchReading();
    var live = document.querySelector('[data-cr2-live]');
    if (live) live.textContent = uiText('Your three cards are revealed below.');
  }

  /* ------------ events ------------ */
  document.addEventListener('deckaura:funnel-event', function (event) {
    var detail = event.detail || {}, data = detail.data || {};
    var eventPage = data.page || data.tool;
    if (eventPage !== PAGE || s.phase !== 'result' || railReadingId !== s.readingId) return;
    if (detail.readingType && detail.readingType !== TYPE) return;
    if (data.presentation_variant && data.presentation_variant !== VARIANT) return;
    if (data.reading_id && data.reading_id !== s.readingId) return;
    if (detail.name === 'checkout_prepare_start') {
      if (checkoutTimer) clearTimeout(checkoutTimer);
      checkoutTimer = 0;
      checkoutCartReview = null;
      s.buying = true;
      s.checkoutQueued = false;
      s.checkoutMessage = ''; s.checkoutMessageError = false;
      render();
      return;
    }
    if (detail.name !== 'checkout_prepare_error') return;
    var reason = data.reason || data.status || detail.reason || '';
    if (checkoutCartReviewReason(reason) && document.querySelector('[data-cr2-checkout-recovery-host]')) {
      checkoutCartReview = { identity: readingIdentity(), packageId: s.pkg, reason: reason };
      resetCheckout('', false);
      return;
    }
    if (!s.buying) return;
    var invalidAuthority = String(data.code || detail.code || '').toUpperCase() === 'DIRECT_TAROT_PREVIEW_EXPIRED_OR_INVALID';
    if (invalidAuthority && s.previewToken) s.authorityRefreshRequired = true;
    resetCheckout(invalidAuthority && s.previewToken ? 'expired' : 'failed', true);
    persistState('packages');
  });
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    var scopeEl = t.closest('[data-cr2]');
    if (!scopeEl) return;
    var fq = t.closest('[data-cr2-faq]');
    if (fq) {
      var open = fq.getAttribute('aria-expanded') === 'true';
      var box = fq.nextElementSibling;
      var sign = fq.querySelector('span[aria-hidden]');
      fq.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (box) box.style.display = open ? 'none' : 'block';
      if (sign) sign.style.transform = open ? 'none' : 'rotate(45deg)';
      return;
    }
    var sitEl = t.closest('[data-cr2-sit]');
    if (sitEl) {
      if (s.phase === 'result') { scrollToId('cards'); return; }
      firstInteraction('category');
      s.sit = sitEl.getAttribute('data-cr2-sit'); s.step = 3; s.err = ''; render(); persistState('');
      publishFunnelEvent('category_selected', { category_id: s.sit });
      setTimeout(function () {
        scrollToId('form');
        var question = document.querySelector('[data-cr2-q]');
        if (question) { try { question.focus({ preventScroll: true }); } catch (_) { question.focus(); } }
      }, 50);
      return;
    }
    var chip = t.closest('[data-cr2-chip]');
    if (chip) {
      if (s.phase !== 'form' || s.buying) return;
      firstInteraction('question_prompt');
      if (s.q.trim()) { var currentQuestion = document.querySelector('[data-cr2-q]'); if (currentQuestion) currentQuestion.focus(); return; }
      s.q = chip.textContent;
      var qa = document.querySelector('[data-cr2-q]');
      if (qa) qa.value = s.q;
      s.err = '';
      publishFunnelEvent('question_started', { source: 'prompt' }, { once: true });
      render();
      persistState('');
      return;
    }
    var tf = t.closest('[data-cr2-tf]');
    if (tf) { if (s.phase !== 'form' || s.buying) return; firstInteraction('timeframe'); s.tf = tf.getAttribute('data-cr2-tf'); render(); persistState(''); publishFunnelEvent('timeframe_selected', { timeframe_id: s.tf }); return; }
    var lad = t.closest('[data-cr2-ladder]');
    if (lad) { if (!selectPackage(lad.getAttribute('data-cr2-ladder'))) return; firstInteraction('package'); render(); persistState('packages'); publishFunnelEvent('package_selected', { package_id: s.pkg, placement: 'ladder' }); startCheckout('package_card'); return; }
    var drawerPkg = t.closest('[data-cr2-drawer-pkg]');
    if (drawerPkg) { if (!selectPackage(drawerPkg.getAttribute('data-cr2-drawer-pkg'))) return; firstInteraction('package'); render(); persistState('packages'); publishFunnelEvent('package_selected', { package_id: s.pkg, placement: 'order_summary' }); startCheckout('order_summary'); return; }
    var pkgCta = t.closest('[data-cr2-pcta]');
    if (pkgCta) {
      var host = pkgCta.closest('[data-cr2-pkg]');
      if (host) { if (!selectPackage(host.getAttribute('data-cr2-pkg'))) return; firstInteraction('package'); render(); persistState('packages'); publishFunnelEvent('package_selected', { package_id: s.pkg, placement: 'package_card' }); startCheckout('package_card'); }
      return;
    }
    var act = t.closest('[data-cr2-act]');
    if (!act) return;
    var a = act.getAttribute('data-cr2-act');
    if (a === 'next') { ev.preventDefault(); next(); }
    else if (a === 'back') { ev.preventDefault(); s.err = ''; s.step = 3; render(); persistState(''); var backQuestion = document.querySelector('[data-cr2-q]'); if (backQuestion) backQuestion.focus(); }
    else if (a === 'changesit') { ev.preventDefault(); scrollToId('decide'); }
    else if (a === 'restart') { ev.preventDefault(); scrollToOffer(true); }
    else if (a === 'newdecision') { ev.preventDefault(); beginDifferentDecision(); }
    else if (a === 'returnprevious') { ev.preventDefault(); returnToPreviousReading(); }
    else if (a === 'closedrawer') { ev.preventDefault(); closeDrawer(act.hasAttribute('data-cr2-overlay') ? 'overlay' : 'close_button'); }
    else if (a === 'checkout') { ev.preventDefault(); startCheckout('drawer'); }
    else if (a === 'retryprice') {
      ev.preventDefault();
      var rail = window.DeckauraDeepReading;
      s.pricingRetry = true;
      s.checkoutMessage = ''; s.checkoutMessageError = false;
      handlePricingSnapshot({ status: 'loading', currency: s.pricing && s.pricing.currency || '', tiers: s.pricing && s.pricing.tiers || [] });
      if (rail && typeof rail.retryPricing === 'function') rail.retryPricing();
      else if (!retryRuntimeScript()) syncRail();
    }
    else if (a === 'closesticky') { ev.preventDefault(); dismissSticky(); render(); publishFunnelEvent('continuation_dismissed', { placement: 'sticky' }, { once: true }); }
    else if (a === 'sticky') { ev.preventDefault(); if (packOf()) startCheckout('sticky'); else scrollToOffer(true); }
    else if (a === 'start') {
      ev.preventDefault(); firstInteraction('hero'); publishFunnelEvent('start_clicked', { placement: 'hero' }, { once: true }); scrollToId(s.phase === 'result' ? 'cards' : 'decide');
      if (s.phase !== 'result') setTimeout(function () { var startQuestion = document.querySelector('[data-cr2-q]'); if (startQuestion) startQuestion.focus(); }, 350);
    }
  });
  document.addEventListener('keydown', function (ev) {
    if (s.drawer) {
      if (ev.key === 'Escape') { ev.preventDefault(); closeDrawer('escape'); return; }
      if (ev.key === 'Tab') {
        var focusables = focusableInDrawer();
        if (!focusables.length) { ev.preventDefault(); return; }
        var first = focusables[0], last = focusables[focusables.length - 1];
        if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); return; }
        if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); return; }
      }
    }
    var el = ev.target && ev.target.closest && (ev.target.closest('[data-cr2-sit]') || ev.target.closest('[data-cr2-tf]') || ev.target.closest('[data-cr2-drawer-pkg]'));
    if (!el || !/^(?:ArrowLeft|ArrowRight|ArrowUp|ArrowDown|Home|End)$/.test(ev.key)) return;
    var group = el.closest('[role="radiogroup"]');
    if (!group) return;
    var radios = Array.prototype.slice.call(group.querySelectorAll('[role="radio"]'));
    var index = radios.indexOf(el);
    if (index < 0) return;
    ev.preventDefault();
    var nextIndex = ev.key === 'Home' ? 0 : (ev.key === 'End' ? radios.length - 1 : index + (/^(?:ArrowRight|ArrowDown)$/.test(ev.key) ? 1 : -1));
    nextIndex = (nextIndex + radios.length) % radios.length;
    var nextRadio = radios[nextIndex];
    if (nextRadio.hasAttribute('data-cr2-ladder') || nextRadio.hasAttribute('data-cr2-drawer-pkg')) {
      if (!selectPackage(nextRadio.getAttribute('data-cr2-ladder') || nextRadio.getAttribute('data-cr2-drawer-pkg'))) return;
      render(); persistState('');
      publishFunnelEvent('package_selected', { package_id: s.pkg, placement: nextRadio.hasAttribute('data-cr2-drawer-pkg') ? 'order_summary_keyboard' : 'ladder_keyboard' });
    } else nextRadio.click();
    try { nextRadio.focus(); } catch (_) {}
  });
  document.addEventListener('input', function (ev) {
    var t = ev.target;
    if (!t || !t.matches) return;
    if (t.matches('[data-cr2-detail]')) {
      if (s.phase !== 'form' || s.buying) { t.value = s.detail; return; }
      firstInteraction('context');
      s.detail = t.value.slice(0, 320);
      s.err = '';
      t.setAttribute('aria-invalid', 'false');
      bindAll('detailCount', s.detail.length + ' / 320');
      bindAll('detailRequirement', optionalContextRequirementText(s.detail));
      document.querySelectorAll('[data-cr2-bind="detailRequirement"]').forEach(function (el) { el.setAttribute('data-ready', !s.detail.trim() || meaningfulInput(s.detail, 10).valid ? 'true' : 'false'); });
      if (s.detail.trim()) publishFunnelEvent('context_started', { length_bucket: s.detail.trim().length <= 20 ? '1-20' : (s.detail.trim().length <= 60 ? '21-60' : '61+') }, { once: true });
      updateNextState(); persistState('');
    } else if (t.matches('[data-cr2-q]')) {
      if (s.phase !== 'form' || s.buying) { t.value = s.q; return; }
      firstInteraction('question');
      s.q = t.value.slice(0, 200);
      s.err = '';
      t.setAttribute('aria-invalid', 'false');
      bindAll('qCount', s.q.length + ' / 200');
      bindAll('qRequirement', requirementText(s.q, 8, 1));
      document.querySelectorAll('[data-cr2-bind="qRequirement"]').forEach(function (el) { el.setAttribute('data-ready', meaningfulInput(s.q, 8, 1).valid ? 'true' : 'false'); });
      if (s.q.trim()) publishFunnelEvent('question_started', { source: 'typed' }, { once: true });
      updateNextState(); persistState('');
    }
  });

  localizeCr2Static();
  var restoredResult = restoreState();
  if (restoredResult) closePackageComparison();
  render();
  dedupeReviewAccessibility();
  setTimeout(localizeCr2Static, 80);
  setTimeout(function () { localizeCr2Static(); dedupeReviewAccessibility(); }, 650);
  publishFunnelEvent('tarot_funnel_viewed', { restored_result: restoredResult ? 1 : 0 }, { ensureAttempt: false, onceKey: 'page_view_' + sessionId });
  if (restoredResult) {
    if (!s.previewToken) fetchReading();
    startResultObservers();
    setTimeout(function () {
      var anchor = returnAnchor || 'cards'; returnAnchor = ''; s.drawer = false; render(); persistState(''); scrollToId(anchor);
    }, 120);
  }
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted || s.phase !== 'result') return;
    clearCartReview();
    invalidateReadingRequest();
    if (checkoutTimer) clearTimeout(checkoutTimer);
    checkoutTimer = 0;
    s.drawer = false; s.buying = false; s.checkoutQueued = false; s.checkoutVia = ''; s.checkoutMessage = ''; s.checkoutMessageError = false; returnAnchor = ''; closePackageComparison(); render(); persistState('');
    startResultObservers();
    setTimeout(function () { scrollToOffer(false); }, 80);
  });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', updateKeyboardState);
    window.visualViewport.addEventListener('scroll', updateKeyboardState);
  }
})();
